import { format } from 'date-fns';
import { Injectable } from '@angular/core';
import { REPORT_TYPE_BASE } from '@navstation-portal/reporting/data-model/vessel-reports';
import {
  DateObject,
  DropboxItem,
  SelectOption,
} from '@navstation-portal/shared/data-model/common';
import { DATE_FORMAT } from '@navstation-portal/shared/data-model/time';
import {
  resolveUtcOffset,
  utcOffsetToTvaTimezone,
} from '@navstation-portal/shared/util/time';
import {
  TankStock,
  TankTransferData,
  VesselSpec,
} from '@navstation-portal/vessels/data-model/vessel';
import {
  BunkerRowEntry,
  BunkerState,
  CARGO_GRADE_OPTIONS,
  CargoPumpMeterState,
  FuelOilBunkerTableState,
  CargoDetailsState,
  CargoState,
  ConsumptionData,
  ConsumptionsState,
  EngineMode,
  FuelSpecState,
  OperationalState,
  PositionAndWeatherState,
  GeneratorEngineState,
  MachineryRhState,
  PowerState,
  IReport,
  StockState,
  StockTank,
  StockTanks,
  ReportType,
  ReportStatus,
  HoseOperationState,
  getOtherSpeedSteamingOption,
} from '@navstation-portal/reporting/data-model/vessel-report';

export const OIL_CODES = {
  '1': 'HS HFO',
  '2': 'LS HFO',
  '3': 'HS MDO',
  '4': 'LS MDO',
  '5': 'HS MGO',
  '6': 'LS MGO',
  '7': 'Sludge',
  '8': 'System Oil',
  '9': 'Cylinder Oil',
  '10': 'AE System Oil',
  '11': 'Turbocharger Oil',
  '12': 'Other',
  '13': 'ULS FO',
};
export type OilCode = (typeof OIL_CODES)[keyof typeof OIL_CODES];

export const DISCHARGE_TO_CODES = {
  '1': 'Shore',
  '2': 'Fuel Tank',
  '3': 'Stern Tube Top Up',
  '4': 'Renewal Camshaft Oil',
  '5': 'Stern Tube Oil',
  '6': 'Stern Tube Seal Consumption',
  '7': 'Top Up Camshaft Oil',
  '8': 'Oily Bilge Tank',
};
export type DischargeToCode =
  (typeof DISCHARGE_TO_CODES)[keyof typeof DISCHARGE_TO_CODES];

export const CLO_LUBRICATION_CODES: Record<number, string> = {
  1: 'Normal Operation',
  2: 'Running In',
  3: 'Damaged Liner',
  4: 'Units Cut Off',
} as const;
export type CloLubricationCode =
  (typeof CLO_LUBRICATION_CODES)[keyof typeof CLO_LUBRICATION_CODES];

export const CYL_OIL_CODES = {
  '1': 'Normal Operation',
  '2': 'Running In',
  '3': 'Damaged Liner',
  '4': 'Units Cut Off',
};
export type CylOilCodes = (typeof CYL_OIL_CODES)[keyof typeof CYL_OIL_CODES];

export interface HoseOperation {
  id: string;
  timestamp: DateObject;
  ifo: number;
  lsf: number;
  lsg: number;
  mgo: number;
}

export interface HeatingOperation {
  id: string;
  comment: string;
  ifo: number;
  mgo: number;
}

type TvaApiBunker = Record<
  string,
  number | boolean | string[] | DateObject | undefined
> & {
  took_fuel_oil: boolean;
  took_lube_oil: boolean;
  debunkered: boolean;
  fuel_started_bunkering_date: DateObject;
  fuel_bunkering_date: DateObject;
  barge_alongside: DateObject;
  barge_away: DateObject;
  bunkered_fuel_grades: string[];
  bunkered_iso_fuel_grades: string[];
  quantity_ordered_hfo: number;
  quantity_ordered_fresh_water: number;
  quantity_received_fresh_water: number;
  bdn_figures_fresh_water: string;
  quantity_ordered_distilled_water: number;
  quantity_received_distilled_water: number;
  bdn_figures_distilled_water: string;
  fuel_lop: boolean;
};

interface TvaApiCargoDetails {
  operation_type: string;
  location: string;
  terminals: string;
  n_cargoes: number;
  cargo_grade: string[];
  berth: string[];
  bl_quantity: number[];
  bl_code: string[];
  bl_discharge: DateObject[];
  surveyors_cargo_quantity: number[];
  flash_point: number[];
  density: number[];
  pour_point: number[];
  load_rate: number[];
  load_temp: number[];
  bl_issue: DateObject[];
  charterer: string[];
  shipper: string[];
  destination_port: string[];
  consignee: string[];
  s2s_ship_name: string[];
  ship_cranes: string[];
  shore_cranes: string[];
  n_ship_cranes: string[];
  n_shore_cranes: string[];
  n_hoses: number;
  hose_diameter: number;
  protests_received: number;
  protests_issued: number;
  dirty_wash: number;
  oily_water: number;
  stored_tank: string;
  fresh_water: number;
  sea_temperature: number;
  air_temperature: number;
  manifold_pressure_1600: number;
  manifold_pressure_2000: number;
  manifold_pressure_000: number;
  manifold_pressure_400: number;
  manifold_pressure_800: number;
  manifold_pressure_1200: number;
  booster_pump_suction_pressure_1600: number;
  booster_pump_suction_pressure_2000: number;
  booster_pump_suction_pressure_000: number;
  booster_pump_suction_pressure_400: number;
  booster_pump_suction_pressure_800: number;
  booster_pump_suction_pressure_1200: number;
  heater_sea_water_outlet_temperature_1600: number;
  heater_sea_water_outlet_temperature_2000: number;
  heater_sea_water_outlet_temperature_000: number;
  heater_sea_water_outlet_temperature_400: number;
  heater_sea_water_outlet_temperature_800: number;
  heater_sea_water_outlet_temperature_1200: number;
  sea_water_inlet_temperature: number;
  hoses: {
    operation_type: 'Load' | 'Discharge';
    terminal: string;
    description: 'Buoy' | 'STS' | 'Terminal' | 'Other';
    hose_on_operations: HoseOperation[];
    hose_off_operations: HoseOperation[];
    heating_operations: HeatingOperation[];
  };
  comments: string;
  protests_received_comments: string;
  protests_issued_comments: string;
  terminal: string;
}

export interface TvaApiPositionAndWeather {
  // observation
  observation: DateObject;
  observation_timezone: string;
  //  wind
  relative_wind_speed: number;
  relative_wind_direction: number;
  true_wind_speed: number;
  true_wind_direction: number;
  true_wind_force: number;
  true_wind_effect: number;
  true_wind_reporting: boolean;

  //  speed and distance
  trip_counter_gps: number;
  trip_counter_log: number;
  speed_log_malfunction: boolean;
  log_distance: number;
  log_speed: number;
  cog: number;
  observed_speed: number;
  observed_distance: number;
  observed_distance_in_eca: number;
  observed_distance_for_total_sea_passage: number;
  remaining_distance: number;
  bad_weather_hours: number;
  bad_weather_observed_distance: number;
  // ship position
  ship_lat_hours: number;
  ship_lat_minutes: number;
  ship_lat_seconds: number;
  ship_lat_direction: string;
  ship_lon_hours: number;
  ship_lon_minutes: number;
  ship_lon_seconds: number;
  ship_lon_direction: string;
  propeller_pitch_percent: number;
  propeller_rpm: number;
  propeller_pitches: number[];
  fin_stabilizer_operational: boolean;

  // currents and sea water
  swell: number;
  swell_direction: number;
  true_current_effect: number;
  wave_height: number;
  water_depth: number;
  current_direction: number;
  current_speed: number;
  relative_current_speed: number;
  wave_direction: number;
  air_temperature: number;
  sea_temperature: number;
  sea_water_density: number;
  atmospheric_pressure: number;
  salinity: number;
}

export interface TvaApiOperational {
  number_of_passengers: number;
  number_of_crew: number;
  voyage_number: string;
  cp_speed?: number;
  cp_fuel?: number;
  instructed_speed?: number;
  instructed_foc?: number;
  other_speed_steaming?: string;
  all_fast_draught_forward: number;
  all_fast_draught_aft: number;
  all_fast_trim: number;
  recommended_trim?: number;
  operation_type: string;
  location: string;
  draught_aft: number;
  draught_forward: number;
  trim?: number;
  tugs_all_fast: number;
  tugs_last_line: number;
  ballast_water: number;
  ballast_water_intake: number;
  ballast_water_discharged: number;
  ballast_water_sample_date: DateObject;
  ballast_water_count_of_cells: number;
  weight: number;
  captain_name: string;
  chief_engineer: string;
  operating_code: string;
  ballast_code: string;
  routing_advice_followed: boolean;
  report_from: DateObject;
  report_to: DateObject;
  report_creation: DateObject;
  report_period: number;
  eosp: DateObject;
  timezone: string;
  previous_timezone: string;
  port_name: string;
  port_latitude_hours: number;
  port_latitude_minutes: number;
  port_latitude_seconds: number;
  port_latitude_direction: string;
  port_longitude_hours: number;
  port_longitude_minutes: number;
  port_longitude_seconds: number;
  port_longitude_direction: string;
  displacement: number;
  gm: number;
  shear_force: number;
  bending_force: number;
  torsion: number;
  gm_reserve: number;
  dead_weight_reserve: number;
  constant_weight: number;
  etd: DateObject;
  nor_tendered: DateObject;
  all_fast: DateObject;
  hoses_connected: DateObject;
  commence: DateObject;
  finish: DateObject;
  hoses_disconnected: DateObject;
  standby_engines: DateObject;
  cargo_documents_onboard: DateObject;
  start_discharging: DateObject;
  end_discharging: DateObject;
  completed_tank_inspection: DateObject;
  fresh_water_domestic?: number;
  fresh_water_tank_cleaning?: number;
  slops_residual_tanks?: number;
  slops_slop_tanks?: number;
  total_interface_oil?: number;
  total_interface_water?: number;
  first_line?: DateObject;
  gangway_down?: DateObject;
  cargo_docs_onboard?: DateObject;
  awaiting_orders: boolean;
  next_port: string;
  remaining_distance: number;
  remaining_time: number;
  destination_timezone: string;
  eta_pilot_station: DateObject;
  maneuvering_type?: string;
  time_sailed: number;
  report_submitted: DateObject;
  arriving: boolean;
  eta: DateObject;
  eta_timezone: string;
  previous_eta: DateObject;
  cosp: DateObject;
  previously_in_eca: boolean;
  enter_eca: DateObject;
  exit_eca: DateObject;
  time_in_eca: number;
  in_eca: boolean;
  purposes_of_call: string[];
}

export interface TvaApiCargoPumpMeter {
  cur: number | null;
  prev: number | null;
  diff: number | null;
}

export interface TvaApiPower {
  [key: string]:
    | number
    | boolean
    | string
    | DateObject
    | DropboxItem[]
    | Record<string, EngineMode>
    | EngineMode[]
    | TvaApiCargoPumpMeter[];
  main_engine_1_power: number;
  main_engine_1_rpm: number;
  main_engine_1_current_rpm: number;
  main_engine_1_previous_rpm: number;
  main_engine_1_shaft_power_meter_counter: number;
  main_engine_1_shaft_power_meter_counter_previous: number;
  main_engine_1_shaft_power_meter_calculated: number;
  main_engine_1_shaft_power_meter_difference: number;
  main_engine_1_load: number;
  main_engine_1_difference_rpm: number;
  main_engine_1_calculated_rpm: number;
  main_engine_1_and_2_power: number;
  main_engine_12_load: number;
  main_engine_1_and_2_rh: number;
  avg_tcrpm_1: number;
  avg_tcrpm_2: number;
  tcrpm_power: number;
  me_operating_modes: Record<string, EngineMode> | EngineMode[];

  main_engine_1_cur_rh: number;
  main_engine_1_prev_rh: number;
  main_engine_1_diff_rh: number;
  main_engine_2_cur_rh: number;
  main_engine_2_prev_rh: number;
  main_engine_2_diff_rh: number;
  aux_engine_1_cur_rh: number;
  aux_engine_1_prev_rh: number;
  aux_engine_1_diff_rh: number;
  aux_engine_2_cur_rh: number;
  aux_engine_2_prev_rh: number;
  aux_engine_2_diff_rh: number;
  aux_engine_3_cur_rh: number;
  aux_engine_3_prev_rh: number;
  aux_engine_3_diff_rh: number;
  aux_engine_4_cur_rh: number;
  aux_engine_4_prev_rh: number;
  aux_engine_4_diff_rh: number;
  aux_boiler_cur_rh: number;
  aux_boiler_prev_rh: number;
  aux_boiler_diff_rh: number;
  aux_boiler_2_prev_rh: number;
  aux_boiler_2_cur_rh: number;
  aux_boiler_2_diff_rh: number;
  composite_boiler_cur_rh: number;
  composite_boiler_prev_rh: number;
  composite_boiler_diff_rh: number;
  main_engine_comp_1_cur_rh: number;
  main_engine_comp_1_prev_rh: number;
  main_engine_comp_1_diff_rh: number;
  main_engine_comp_2_cur_rh: number;
  main_engine_comp_2_prev_rh: number;
  main_engine_comp_2_diff_rh: number;
  main_engine_comp_3_prev_rh: number;
  main_engine_comp_3_diff_rh: number;
  main_engine_comp_4_prev_rh: number;
  main_engine_comp_4_diff_rh: number;

  booster_unit_hfo_cur_rh: number;
  booster_unit_hfo_prev_rh: number;
  booster_unit_hfo_diff_rh: number;
  booster_unit_gas_oil_cur_rh: number;
  booster_unit_gas_oil_prev_rh: number;
  booster_unit_gas_oil_diff_rh: number;
  main_engine_flow_inlet_cur_rh: number;
  main_engine_flow_inlet_prev_rh: number;
  main_engine_flow_inlet_diff_rh: number;
  aux_engine_flow_inlet_cur_rh: number;
  aux_engine_flow_inlet_prev_rh: number;
  aux_engine_flow_inlet_diff_rh: number;
  aux_engine_flow_outlet_cur_rh: number;
  aux_engine_flow_outlet_prev_rh: number;
  aux_engine_flow_outlet_diff_rh: number;
  aux_boiler_flow_cur_rh: number;
  aux_boiler_flow_prev_rh: number;
  aux_boiler_flow_diff_rh: number;
  cylinder_lube_oil_cur: number;
  cylinder_lube_oil_prev: number;
  cylinder_lube_oil_diff: number;

  me_lo_pump_1_cur_rh: number;
  me_lo_pump_1_prev_rh: number;
  me_lo_pump_1_diff_rh: number;
  me_lo_pump_2_cur_rh: number;
  me_lo_pump_2_prev_rh: number;
  me_lo_pump_2_diff_rh: number;
  sea_water_pump_1_cur_rh: number;
  sea_water_pump_1_prev_rh: number;
  sea_water_pump_1_diff_rh: number;
  sea_water_pump_2_cur_rh: number;
  sea_water_pump_2_prev_rh: number;
  sea_water_pump_2_diff_rh: number;
  sg_pump_1_cur_rh: number;
  sg_pump_1_prev_rh: number;
  sg_pump_1_diff_rh: number;
  sg_pump_2_cur_rh: number;
  sg_pump_2_prev_rh: number;
  sg_pump_2_diff_rh: number;
  fo_purifier_1_cur_rh: number;
  fo_purifier_1_prev_rh: number;
  fo_purifier_1_diff_rh: number;
  fo_purifier_2_cur_rh: number;
  fo_purifier_2_prev_rh: number;
  fo_purifier_2_diff_rh: number;
  er_fan_1_cur_rh: number;
  er_fan_1_prev_rh: number;
  er_fan_1_diff_rh: number;
  er_fan_2_cur_rh: number;
  er_fan_2_prev_rh: number;
  er_fan_2_diff_rh: number;
  er_fan_3_cur_rh: number;
  er_fan_3_prev_rh: number;
  er_fan_3_diff_rh: number;
  er_fan_4_cur_rh: number;
  er_fan_4_prev_rh: number;
  er_fan_4_diff_rh: number;

  reporting_pumps: boolean;
  scrubber_in_use: boolean;

  cargo_pumps_kwh: TvaApiCargoPumpMeter[];

  shaft_generator_pto_power: number;
  shaft_generator_pto_max_rating: number;
  shaft_generator_pto_rh: number;

  incinerator_malfunctional: boolean;
  odme_malfunctional: boolean;
  bwts_malfunctional: boolean;
  scrubber_malfunctional: boolean;
  ows_malfunctional: boolean;

  shore_power_is_used: boolean;
  shore_power_reception_port: string;
  shore_power_kwh: number;
  shore_power_hours: number;
  shore_power_start_time: DateObject;
  shore_power_end_time: DateObject;
  total_shore_power_kwh: number;
  shore_power_wtt: number;
  edn_number: string;
  edn_files: DropboxItem[];
  sustainable_shore_power_is_used: boolean;
  proof_of_sustainability_edn_files: DropboxItem[];
  running_total_shore_power_kwh: number;
  edn_id: string;
}

export interface TvaApiConsumption {
  [key: string]: string | number | boolean | Date | DateObject | DropboxItem;

  // Fuel Specifications
  fuel_type_1: string;
  fuel_type_2: string;
  fuel_type_3: string;

  // LCV = Low Calorific Value
  lcv_hshfo: number;
  lcv_lshfo: number;
  lcv_ulsfo: number;
  lcv_hsmdo: number;
  lcv_lsmdo: number;
  lcv_hsmgo: number;
  lcv_lsmgo: number;
  // MRV
  lcv_propane: number;
  lcv_butane: number;
  lcv_lng: number;
  lcv_methanol: number;
  lcv_ethanol: number;
  lcv_other: number;

  lcv_hfo: number;
  lcv_lfo: number;
  lcv_mgo: number;
  lcv_mdo: number;
  lcv_b10lfo: number;
  lcv_b10mgo: number;
  lcv_biolfo: number;
  lcv_biomgo: number;
  lcv_ulsfo2020: number;
  lcv_ulslfo2020: number;
  lcv_ulsmdo2020: number;
  lcv_ulsmgo2020: number;
  lcv_vlsfo2020: number;
  lcv_vlslfo2020: number;
  lcv_lpgp: number;
  lcv_lpgb: number;
  lcv_hvo: number;
  lcv_fame: number;
  lcv_biodiesel: number;
  lcv_bio_lng: number;

  // Sulphur
  sulphur_hshfo: number;
  sulphur_lshfo: number;
  sulphur_ulsfo: number;
  sulphur_hsmdo: number;
  sulphur_lsmdo: number;
  sulphur_hsmgo: number;
  sulphur_lsmgo: number;
  // MRV
  sulphur_propane: number;
  sulphur_butane: number;
  sulphur_lng: number;
  sulphur_methanol: number;
  sulphur_ethanol: number;
  sulphur_other: number;

  sulphur_hfo: number;
  sulphur_lfo: number;
  sulphur_mgo: number;
  sulphur_mdo: number;
  sulphur_b10lfo: number;
  sulphur_b10mgo: number;
  sulphur_biolfo: number;
  sulphur_biomgo: number;
  sulphur_ulsfo2020: number;
  sulphur_ulslfo2020: number;
  sulphur_ulsmdo2020: number;
  sulphur_ulsmgo2020: number;
  sulphur_vlsfo2020: number;
  sulphur_vlslfo2020: number;
  sulphur_lpgp: number;
  sulphur_lpgb: number;
  sulphur_hvo: number;
  sulphur_fame: number;
  sulphur_biodiesel: number;
  sulphur_bio_lng: number;

  // Density at 15
  density_hshfo: number;
  density_lshfo: number;
  density_ulsfo: number;
  density_hsmdo: number;
  density_lsmdo: number;
  density_hsmgo: number;
  density_lsmgo: number;
  // MRV
  density_propane: number;
  density_butane: number;
  density_lng: number;
  density_methanol: number;
  density_ethanol: number;
  density_other: number;

  density_hfo: number;
  density_lfo: number;
  density_mgo: number;
  density_mdo: number;
  density_b10lfo: number;
  density_b10mgo: number;
  density_biolfo: number;
  density_biomgo: number;
  density_ulsfo2020: number;
  density_ulslfo2020: number;
  density_ulsmdo2020: number;
  density_ulsmgo2020: number;
  density_vlsfo2020: number;
  density_vlslfo2020: number;
  density_lpgp: number;
  density_lpgb: number;
  density_hvo: number;
  density_fame: number;
  density_biodiesel: number;
  density_bio_lng: number;

  viscosity_hshfo: number;
  viscosity_lshfo: number;
  viscosity_ulsfo: number;
  viscosity_hsmdo: number;
  viscosity_lsmdo: number;
  viscosity_hsmgo: number;
  viscosity_lsmgo: number;

  viscosity_propane: number;
  viscosity_butane: number;
  viscosity_lng: number;
  viscosity_methanol: number;
  viscosity_ethanol: number;
  viscosity_other: number;

  viscosity_hfo: number;
  viscosity_lfo: number;
  viscosity_mgo: number;
  viscosity_mdo: number;
  viscosity_b10lfo: number;
  viscosity_b10mgo: number;
  viscosity_biolfo: number;
  viscosity_biomgo: number;
  viscosity_ulsfo2020: number;
  viscosity_ulslfo2020: number;
  viscosity_ulsmdo2020: number;
  viscosity_ulsmgo2020: number;
  viscosity_vlsfo2020: number;
  viscosity_vlslfo2020: number;
  viscosity_lpgp: number;
  viscosity_lpgb: number;

  // previous specs
  previous_lcv_hshfo: number;
  previous_lcv_lshfo: number;
  previous_lcv_ulsfo: number;
  previous_lcv_hsmdo: number;
  previous_lcv_lsmdo: number;
  previous_lcv_hsmgo: number;
  previous_lcv_lsmgo: number;

  previous_lcv_hfo: number;
  previous_lcv_lfo: number;
  previous_lcv_mgo: number;
  previous_lcv_b10lfo: number;
  previous_lcv_mdo: number;
  previous_lcv_b10mgo: number;
  previous_lcv_biolfo: number;
  previous_lcv_biomgo: number;
  previous_lcv_ulsfo2020: number;
  previous_lcv_ulslfo2020: number;
  previous_lcv_ulsmgo2020: number;
  previous_lcv_ulsmdo2020: number;
  previous_lcv_vlsfo2020: number;
  previous_lcv_vlslfo2020: number;
  previous_lcv_lpgp: number;
  previous_lcv_lpgb: number;
  previous_lcv_bio_lng: number;
  previous_lcv_lng: number;
  previous_lcv_methanol: number;
  previous_lcv_ethanol: number;
  previous_lcv_other: number;
  previous_lcv_hvo: number;
  previous_lcv_fame: number;
  previous_lcv_biodiesel: number;

  previous_sulphur_hshfo: number;
  previous_sulphur_lshfo: number;
  previous_sulphur_ulsfo: number;
  previous_sulphur_hsmdo: number;
  previous_sulphur_lsmdo: number;
  previous_sulphur_hsmgo: number;
  previous_sulphur_lsmgo: number;

  previous_sulphur_hfo: number;
  previous_sulphur_lfo: number;
  previous_sulphur_mgo: number;
  previous_sulphur_mdo: number;
  previous_sulphur_b10lfo: number;
  previous_sulphur_b10mgo: number;
  previous_sulphur_biolfo: number;
  previous_sulphur_biomgo: number;
  previous_sulphur_ulsfo2020: number;
  previous_sulphur_ulslfo2020: number;
  previous_sulphur_ulsmdo2020: number;
  previous_sulphur_ulsmgo2020: number;
  previous_sulphur_vlsfo2020: number;
  previous_sulphur_vlslfo2020: number;
  previous_sulphur_lpgp: number;
  previous_sulphur_lpgb: number;
  previous_sulphur_bio_lng: number;
  previous_sulphur_lng: number;
  previous_sulphur_methanol: number;
  previous_sulphur_ethanol: number;
  previous_sulphur_other: number;
  previous_sulphur_hvo: number;
  previous_sulphur_fame: number;
  previous_sulphur_biodiesel: number;

  previous_density_hshfo: number;
  previous_density_lshfo: number;
  previous_density_ulsfo: number;
  previous_density_hsmdo: number;
  previous_density_lsmdo: number;
  previous_density_hsmgo: number;
  previous_density_lsmgo: number;

  previous_density_hfo: number;
  previous_density_lfo: number;
  previous_density_mgo: number;
  previous_density_mdo: number;
  previous_density_b10lfo: number;
  previous_density_b10mgo: number;
  previous_density_biolfo: number;
  previous_density_biomgo: number;
  previous_density_ulsfo2020: number;
  previous_density_ulslfo2020: number;
  previous_density_ulsmdo2020: number;
  previous_density_ulsmgo2020: number;
  previous_density_vlsfo2020: number;
  previous_density_vlslfo2020: number;
  previous_density_lpgp: number;
  previous_density_lpgb: number;
  previous_density_bio_lng: number;
  previous_density_lng: number;
  previous_density_methanol: number;
  previous_density_ethanol: number;
  previous_density_other: number;
  previous_density_hvo: number;
  previous_density_fame: number;
  previous_density_biodiesel: number;

  // Service Tank Temperature
  serv_hshfo: number;
  serv_lshfo: number;
  serv_ulsfo: number;
  serv_hsmdo: number;
  serv_lsmdo: number;
  serv_hsmgo: number;
  serv_lsmgo: number;
  // MRV
  serv_propane: number;
  serv_butane: number;
  serv_lng: number;
  serv_methanol: number;
  serv_ethanol: number;
  serv_other: number;

  serv_hfo: number;
  serv_lfo: number;
  serv_mgo: number;
  serv_b10lfo: number;
  serv_mdo: number;
  serv_b10mgo: number;
  serv_biolfo: number;
  serv_biomgo: number;
  serv_ulsfo2020: number;
  serv_ulslfo2020: number;
  serv_ulsmgo2020: number;
  serv_ulsmdo2020: number;
  serv_vlsfo2020: number;
  serv_vlslfo2020: number;
  serv_lpgp: number;
  serv_lpgb: number;
  serv_hvo: number;
  serv_fame: number;
  serv_biodiesel: number;
  serv_bio_lng: number;

  // Fuel Consumption

  // Main Engine Fuel Consumption
  main_engine_hshfo: number;
  main_engine_lshfo: number;
  main_engine_ulsfo: number;
  main_engine_hsmdo: number;
  main_engine_lsmdo: number;
  main_engine_hsmgo: number;
  main_engine_lsmgo: number;

  main_engine_hfo: number;
  main_engine_lfo: number;
  main_engine_mgo: number;
  main_engine_b10lfo: number;
  main_engine_mdo: number;
  main_engine_b10mgo: number;
  main_engine_biolfo: number;
  main_engine_biomgo: number;
  main_engine_ulsfo2020: number;
  main_engine_ulslfo2020: number;
  main_engine_ulsmgo2020: number;
  main_engine_ulsmdo2020: number;
  main_engine_vlsfo2020: number;
  main_engine_vlslfo2020: number;
  main_engine_lpgp: number;
  main_engine_lpgb: number;
  main_engine_hvo: number;
  main_engine_fame: number;
  main_engine_biodiesel: number;
  main_engine_bio_lng: number;

  // MDG Port Fuel Consumption
  mdg_port_hshfo: number;
  mdg_port_lshfo: number;
  mdg_port_ulsfo: number;
  mdg_port_lsmdo: number;
  mdg_port_hsmgo: number;
  mdg_port_lsmgo: number;

  mdg_port_hfo: number;
  mdg_port_lfo: number;
  mdg_port_mgo: number;
  mdg_port_mdo: number;
  mdg_port_b10lfo: number;
  mdg_port_b10mgo: number;
  mdg_port_biolfo: number;
  mdg_port_biomgo: number;
  mdg_port_ulsfo2020: number;
  mdg_port_ulslfo2020: number;
  mdg_port_ulsmdo2020: number;
  mdg_port_ulsmgo2020: number;
  mdg_port_vlsfo2020: number;
  mdg_port_vlslfo2020: number;
  mdg_port_lpgp: number;
  mdg_port_lpgb: number;
  mdg_port_bio_lng: number;
  mdg_port_lng: number;
  mdg_port_methanol: number;
  mdg_port_ethanol: number;
  mdg_port_other: number;
  mdg_port_hvo: number;
  mdg_port_fame: number;
  mdg_port_biodiesel: number;

  // MDG Starboard Fuel Consumption
  mdg_starboard_hshfo: number;
  mdg_starboard_lshfo: number;
  mdg_starboard_ulsfo: number;
  mdg_starboard_lsmdo: number;
  mdg_starboard_hsmgo: number;
  mdg_starboard_lsmgo: number;

  mdg_starboard_hfo: number;
  mdg_starboard_lfo: number;
  mdg_starboard_mgo: number;
  mdg_starboard_mdo: number;
  mdg_starboard_b10lfo: number;
  mdg_starboard_b10mgo: number;
  mdg_starboard_biolfo: number;
  mdg_starboard_biomgo: number;
  mdg_starboard_ulsfo2020: number;
  mdg_starboard_ulslfo2020: number;
  mdg_starboard_ulsmdo2020: number;
  mdg_starboard_ulsmgo2020: number;
  mdg_starboard_vlsfo2020: number;
  mdg_starboard_vlslfo2020: number;
  mdg_starboard_lpgp: number;
  mdg_starboard_lpgb: number;
  mdg_starboard_bio_lng: number;
  mdg_starboard_lng: number;
  mdg_starboard_methanol: number;
  mdg_starboard_ethanol: number;
  mdg_starboard_other: number;
  mdg_starboard_hvo: number;
  mdg_starboard_fame: number;
  mdg_starboard_biodiesel: number;

  // HDG Fuel Consumption
  hdg_hshfo: number;
  hdg_lshfo: number;
  hdg_ulsfo: number;
  hdg_lsmdo: number;
  hdg_hsmgo: number;
  hdg_lsmgo: number;

  hdg_hfo: number;
  hdg_lfo: number;
  hdg_mgo: number;
  hdg_mdo: number;
  hdg_b10lfo: number;
  hdg_b10mgo: number;
  hdg_biolfo: number;
  hdg_biomgo: number;
  hdg_ulsfo2020: number;
  hdg_ulslfo2020: number;
  hdg_ulsmdo2020: number;
  hdg_ulsmgo2020: number;
  hdg_vlsfo2020: number;
  hdg_vlslfo2020: number;
  hdg_lpgp: number;
  hdg_lpgb: number;
  hdg_bio_lng: number;
  hdg_lng: number;
  hdg_methanol: number;
  hdg_ethanol: number;
  hdg_other: number;
  hdg_hvo: number;
  hdg_fame: number;
  hdg_biodiesel: number;

  // DG Fuel Consumption
  // MDG Port +  MDG Starboard + HDG Combined
  dg_hshfo: number;
  dg_lshfo: number;
  dg_ulsfo: number;
  dg_lsmdo: number;
  dg_hsmgo: number;
  dg_lsmgo: number;

  dg_hfo: number;
  dg_lfo: number;
  dg_mgo: number;
  dg_mdo: number;
  dg_b10lfo: number;
  dg_b10mgo: number;
  dg_biolfo: number;
  dg_biomgo: number;
  dg_ulsfo2020: number;
  dg_ulslfo2020: number;
  dg_ulsmdo2020: number;
  dg_ulsmgo2020: number;
  dg_vlsfo2020: number;
  dg_vlslfo2020: number;
  dg_lpgp: number;
  dg_lpgb: number;
  dg_bio_lng: number;
  dg_lng: number;
  dg_methanol: number;
  dg_ethanol: number;
  dg_other: number;
  dg_hvo: number;
  dg_fame: number;
  dg_biodiesel: number;

  // MRV
  main_engine_propane: number;
  main_engine_butane: number;
  main_engine_lng: number;
  main_engine_methanol: number;
  main_engine_ethanol: number;
  main_engine_other: number;

  // Aux Engine Fuel Consumption
  aux_engine_hshfo: number;
  aux_engine_lshfo: number;
  aux_engine_ulsfo: number;
  aux_engine_hsmdo: number;
  aux_engine_lsmdo: number;
  aux_engine_hsmgo: number;
  aux_engine_lsmgo: number;

  aux_engine_hfo: number;
  aux_engine_lfo: number;
  aux_engine_mgo: number;
  aux_engine_b10lfo: number;
  aux_engine_mdo: number;
  aux_engine_b10mgo: number;
  aux_engine_biolfo: number;
  aux_engine_biomgo: number;
  aux_engine_ulsfo2020: number;
  aux_engine_ulslfo2020: number;
  aux_engine_ulsmgo2020: number;
  aux_engine_ulsmdo2020: number;
  aux_engine_vlsfo2020: number;
  aux_engine_vlslfo2020: number;
  aux_engine_lpgp: number;
  aux_engine_lpgb: number;
  aux_engine_bio_lng: number;
  // MRV
  aux_engine_propane: number;
  aux_engine_butane: number;
  aux_engine_lng: number;
  aux_engine_methanol: number;
  aux_engine_ethanol: number;
  aux_engine_other: number;
  aux_engine_hvo: number;
  aux_engine_fame: number;
  aux_engine_biodiesel: number;

  // Aux Boiler Fuel Consumption
  aux_boiler_hshfo: number;
  aux_boiler_lshfo: number;
  aux_boiler_ulsfo: number;
  aux_boiler_hsmdo: number;
  aux_boiler_lsmdo: number;
  aux_boiler_hsmgo: number;
  aux_boiler_lsmgo: number;

  aux_boiler_hfo: number;
  aux_boiler_lfo: number;
  aux_boiler_mgo: number;
  aux_boiler_mdo: number;
  aux_boiler_b10lfo: number;
  aux_boiler_b10mgo: number;
  aux_boiler_biolfo: number;
  aux_boiler_biomgo: number;
  aux_boiler_ulsfo2020: number;
  aux_boiler_ulslfo2020: number;
  aux_boiler_ulsmdo2020: number;
  aux_boiler_ulsmgo2020: number;
  aux_boiler_vlsfo2020: number;
  aux_boiler_vlslfo2020: number;
  aux_boiler_lpgp: number;
  aux_boiler_lpgb: number;
  aux_boiler_bio_lng: number;
  // MRV
  aux_boiler_propane: number;
  aux_boiler_butane: number;
  aux_boiler_lng: number;
  aux_boiler_methanol: number;
  aux_boiler_ethanol: number;
  aux_boiler_other: number;
  aux_boiler_hvo: number;
  aux_boiler_fame: number;
  aux_boiler_biodiesel: number;

  // Incinerator Consumption
  incinerator_hshfo: number;
  incinerator_lshfo: number;
  incinerator_ulsfo: number;
  incinerator_hsmdo: number;
  incinerator_lsmdo: number;
  incinerator_hsmgo: number;
  incinerator_lsmgo: number;
  // MRV
  incinerator_propane: number;
  incinerator_butane: number;
  incinerator_lng: number;
  incinerator_methanol: number;
  incinerator_ethanol: number;
  incinerator_other: number;

  incinerator_hfo: number;
  incinerator_lfo: number;
  incinerator_mgo: number;
  incinerator_mdo: number;
  incinerator_b10lfo: number;
  incinerator_b10mgo: number;
  incinerator_biolfo: number;
  incinerator_biomgo: number;
  incinerator_ulsfo2020: number;
  incinerator_ulslfo2020: number;
  incinerator_ulsmdo2020: number;
  incinerator_ulsmgo2020: number;
  incinerator_vlsfo2020: number;
  incinerator_vlslfo2020: number;
  incinerator_lpgp: number;
  incinerator_lpgb: number;
  incinerator_hvo: number;
  incinerator_fame: number;
  incinerator_biodiesel: number;
  incinerator_bio_lng: number;

  // GCU Consumption
  gas_combustion_units_hshfo: number;
  gas_combustion_units_lshfo: number;
  gas_combustion_units_ulsfo: number;
  gas_combustion_units_hsmdo: number;
  gas_combustion_units_lsmdo: number;
  gas_combustion_units_hsmgo: number;
  gas_combustion_units_lsmgo: number;
  // MRV
  gas_combustion_units_propane: number;
  gas_combustion_units_butane: number;
  gas_combustion_units_lng: number;
  gas_combustion_units_methanol: number;
  gas_combustion_units_ethanol: number;
  gas_combustion_units_other: number;

  gas_combustion_units_hfo: number;
  gas_combustion_units_lfo: number;
  gas_combustion_units_mgo: number;
  gas_combustion_units_mdo: number;
  gas_combustion_units_b10lfo: number;
  gas_combustion_units_b10mgo: number;
  gas_combustion_units_biolfo: number;
  gas_combustion_units_biomgo: number;
  gas_combustion_units_ulsfo2020: number;
  gas_combustion_units_ulslfo2020: number;
  gas_combustion_units_ulsmdo2020: number;
  gas_combustion_units_ulsmgo2020: number;
  gas_combustion_units_vlsfo2020: number;
  gas_combustion_units_vlslfo2020: number;
  gas_combustion_units_lpgp: number;
  gas_combustion_units_lpgb: number;
  gas_combustion_units_hvo: number;
  gas_combustion_units_fame: number;
  gas_combustion_units_biodiesel: number;
  gas_combustion_units_bio_lng: number;

  // DPP Consumption
  dpp_hshfo: number;
  dpp_lshfo: number;
  dpp_ulsfo: number;
  dpp_hsmdo: number;
  dpp_lsmdo: number;
  dpp_hsmgo: number;
  dpp_lsmgo: number;
  // MRV
  dpp_propane: number;
  dpp_butane: number;
  dpp_lng: number;
  dpp_methanol: number;
  dpp_ethanol: number;
  dpp_other: number;

  dpp_hfo: number;
  dpp_lfo: number;
  dpp_mgo: number;
  dpp_mdo: number;
  dpp_b10lfo: number;
  dpp_b10mgo: number;
  dpp_biolfo: number;
  dpp_biomgo: number;
  dpp_ulsfo2020: number;
  dpp_ulslfo2020: number;
  dpp_ulsmdo2020: number;
  dpp_ulsmgo2020: number;
  dpp_vlsfo2020: number;
  dpp_vlslfo2020: number;
  dpp_lpgp: number;
  dpp_lpgb: number;
  dpp_hvo: number;
  dpp_fame: number;
  dpp_biodiesel: number;
  dpp_bio_lng: number;

  // Other Fuel Consumption
  other_hshfo: number;
  other_lshfo: number;
  other_ulsfo: number;
  other_hsmdo: number;
  other_lsmdo: number;
  other_hsmgo: number;
  other_lsmgo: number;
  // MRV
  other_propane: number;
  other_butane: number;
  other_lng: number;
  other_methanol: number;
  other_ethanol: number;
  other_other: number;
  // CII
  other_hfo: number;
  other_lfo: number;
  other_mgo: number;
  other_mdo: number;
  other_b10lfo: number;
  other_b10mgo: number;
  other_biolfo: number;
  other_biomgo: number;
  other_ulsfo2020: number;
  other_ulslfo2020: number;
  other_ulsmdo2020: number;
  other_ulsmgo2020: number;
  other_vlsfo2020: number;
  other_vlslfo2020: number;
  other_lpgp: number;
  other_lpgb: number;
  other_hvo: number;
  other_fame: number;
  other_biodiesel: number;
  other_bio_lng: number;

  // Cargo Related Fuel Consumption
  cargo_related_hshfo: number;
  cargo_related_lshfo: number;
  cargo_related_ulsfo: number;
  cargo_related_hsmdo: number;
  cargo_related_lsmdo: number;
  cargo_related_hsmgo: number;
  cargo_related_lsmgo: number;
  // MRV
  cargo_related_propane: number;
  cargo_related_butane: number;
  cargo_related_lng: number;
  cargo_related_methanol: number;
  cargo_related_ethanol: number;
  cargo_related_other: number;

  cargo_related_hfo: number;
  cargo_related_lfo: number;
  cargo_related_mgo: number;
  cargo_related_mdo: number;
  cargo_related_b10lfo: number;
  cargo_related_b10mgo: number;
  cargo_related_biolfo: number;
  cargo_related_biomgo: number;
  cargo_related_ulsfo2020: number;
  cargo_related_ulslfo2020: number;
  cargo_related_ulsmdo2020: number;
  cargo_related_ulsmgo2020: number;
  cargo_related_vlsfo2020: number;
  cargo_related_vlslfo2020: number;
  cargo_related_lpgp: number;
  cargo_related_lpgb: number;
  cargo_related_hvo: number;
  cargo_related_fame: number;
  cargo_related_biodiesel: number;
  cargo_related_bio_lng: number;

  total_consumption_hshfo: number;
  total_consumption_lshfo: number;
  total_consumption_ulsfo: number;
  total_consumption_hsmdo: number;
  total_consumption_lsmdo: number;
  total_consumption_hsmgo: number;
  total_consumption_lsmgo: number;
  // MRV
  total_consumption_propane: number;
  total_consumption_butane: number;
  total_consumption_lng: number;
  total_consumption_methanol: number;
  total_consumption_ethanol: number;
  total_consumption_other: number;
  // CII
  total_consumption_hfo: number;
  total_consumption_lfo: number;
  total_consumption_mgo: number;
  total_consumption_mdo: number;
  total_consumption_b10lfo: number;
  total_consumption_b10mgo: number;
  total_consumption_biolfo: number;
  total_consumption_biomgo: number;
  total_consumption_ulsfo2020: number;
  total_consumption_ulslfo2020: number;
  total_consumption_ulsmdo2020: number;
  total_consumption_ulsmgo2020: number;
  total_consumption_vlsfo2020: number;
  total_consumption_vlslfo2020: number;
  total_consumption_lpgp: number;
  total_consumption_lpgb: number;
  total_consumption_hvo: number;
  total_consumption_fame: number;
  total_consumption_biodiesel: number;
  total_consumption_bio_lng: number;

  //// detailed cargo related fuel consumptions

  // ig generator
  ig_generator_hshfo: number;
  ig_generator_lshfo: number;
  ig_generator_ulsfo: number;
  ig_generator_hsmdo: number;
  ig_generator_lsmdo: number;
  ig_generator_hsmgo: number;
  ig_generator_lsmgo: number;
  // MRV
  ig_generator_propane: number;
  ig_generator_butane: number;
  ig_generator_lng: number;
  ig_generator_methanol: number;
  ig_generator_ethanol: number;
  ig_generator_other: number;

  ig_generator_hfo: number;
  ig_generator_lfo: number;
  ig_generator_mgo: number;
  ig_generator_mdo: number;
  ig_generator_b10lfo: number;
  ig_generator_b10mgo: number;
  ig_generator_biolfo: number;
  ig_generator_biomgo: number;
  ig_generator_ulsfo2020: number;
  ig_generator_ulslfo2020: number;
  ig_generator_ulsmdo2020: number;
  ig_generator_ulsmgo2020: number;
  ig_generator_vlsfo2020: number;
  ig_generator_vlslfo2020: number;
  ig_generator_lpgp: number;
  ig_generator_lpgb: number;
  ig_generator_hvo: number;
  ig_generator_fame: number;
  ig_generator_biodiesel: number;
  ig_generator_bio_lng: number;

  // gas turbines
  gas_turbines_hshfo: number;
  gas_turbines_lshfo: number;
  gas_turbines_ulsfo: number;
  gas_turbines_hsmdo: number;
  gas_turbines_lsmdo: number;
  gas_turbines_hsmgo: number;
  gas_turbines_lsmgo: number;
  // MRV
  gas_turbines_propane: number;
  gas_turbines_butane: number;
  gas_turbines_lng: number;
  gas_turbines_methanol: number;
  gas_turbines_ethanol: number;
  gas_turbines_other: number;

  // IG generator boiler
  ig_boiler_flue_gas_hshfo: number;
  ig_boiler_flue_gas_lshfo: number;
  ig_boiler_flue_gas_ulsfo: number;
  ig_boiler_flue_gas_hsmdo: number;
  ig_boiler_flue_gas_lsmdo: number;
  ig_boiler_flue_gas_hsmgo: number;
  ig_boiler_flue_gas_lsmgo: number;
  // MRV
  ig_boiler_propane: number;
  ig_boiler_butane: number;
  ig_boiler_lng: number;
  ig_boiler_methanol: number;
  ig_boiler_ethanol: number;
  ig_boiler_other: number;

  ig_boiler_flue_gas_hfo: number;
  ig_boiler_flue_gas_lfo: number;
  ig_boiler_flue_gas_mgo: number;
  ig_boiler_flue_gas_mdo: number;
  ig_boiler_flue_gas_b10lfo: number;
  ig_boiler_flue_gas_b10mgo: number;
  ig_boiler_flue_gas_biolfo: number;
  ig_boiler_flue_gas_biomgo: number;
  ig_boiler_flue_gas_ulsfo2020: number;
  ig_boiler_flue_gas_ulslfo2020: number;
  ig_boiler_flue_gas_ulsmdo2020: number;
  ig_boiler_flue_gas_ulsmgo2020: number;
  ig_boiler_flue_gas_vlsfo2020: number;
  ig_boiler_flue_gas_vlslfo2020: number;
  ig_boiler_flue_gas_lpgp: number;
  ig_boiler_flue_gas_lpgb: number;
  ig_boiler_flue_gas_hvo: number;
  ig_boiler_flue_gas_fame: number;
  ig_boiler_flue_gas_biodiesel: number;
  ig_boiler_flue_gas_bio_lng: number;

  // Other auxiliary engines
  other_aux_engines_hshfo: number;
  other_aux_engines_lshfo: number;
  other_aux_engines_ulsfo: number;
  other_aux_engines_hsmdo: number;
  other_aux_engines_lsmdo: number;
  other_aux_engines_hsmgo: number;
  other_aux_engines_lsmgo: number;
  // MRV
  other_aux_engines_propane: number;
  other_aux_engines_butane: number;
  other_aux_engines_lng: number;
  other_aux_engines_methanol: number;
  other_aux_engines_ethanol: number;
  other_aux_engines_other: number;

  other_aux_engines_hfo: number;
  other_aux_engines_lfo: number;
  other_aux_engines_mgo: number;
  other_aux_engines_mdo: number;
  other_aux_engines_b10lfo: number;
  other_aux_engines_b10mgo: number;
  other_aux_engines_biolfo: number;
  other_aux_engines_biomgo: number;
  other_aux_engines_ulsfo2020: number;
  other_aux_engines_ulslfo2020: number;
  other_aux_engines_ulsmdo2020: number;
  other_aux_engines_ulsmgo2020: number;
  other_aux_engines_vlsfo2020: number;
  other_aux_engines_vlslfo2020: number;
  other_aux_engines_lpgp: number;
  other_aux_engines_lpgb: number;
  other_aux_engines_hvo: number;
  other_aux_engines_fame: number;
  other_aux_engines_biodiesel: number;
  other_aux_engines_bio_lng: number;

  // tank cleaning
  tank_cleaning_hshfo: number;
  tank_cleaning_lshfo: number;
  tank_cleaning_ulsfo: number;
  tank_cleaning_hsmdo: number;
  tank_cleaning_lsmdo: number;
  tank_cleaning_hsmgo: number;
  tank_cleaning_lsmgo: number;
  // MRV
  tank_cleaning_propane: number;
  tank_cleaning_butane: number;
  tank_cleaning_lng: number;
  tank_cleaning_methanol: number;
  tank_cleaning_ethanol: number;
  tank_cleaning_other: number;

  // Cargo Heating
  cargo_heating_hshfo: number;
  cargo_heating_lshfo: number;
  cargo_heating_ulsfo: number;
  cargo_heating_hsmdo: number;
  cargo_heating_lsmdo: number;
  cargo_heating_hsmgo: number;
  cargo_heating_lsmgo: number;
  // MRV
  cargo_heating_propane: number;
  cargo_heating_butane: number;
  cargo_heating_lng: number;
  cargo_heating_methanol: number;
  cargo_heating_ethanol: number;
  cargo_heating_other: number;

  // Ballast Operations
  ballast_operations_hshfo: number;
  ballast_operations_lshfo: number;
  ballast_operations_ulsfo: number;
  ballast_operations_hsmdo: number;
  ballast_operations_lsmdo: number;
  ballast_operations_hsmgo: number;
  ballast_operations_lsmgo: number;
  // MRV
  ballast_operations_propane: number;
  ballast_operations_butane: number;
  ballast_operations_lng: number;
  ballast_operations_methanol: number;
  ballast_operations_ethanol: number;
  ballast_operations_other: number;

  // Cargo Shifting
  cargo_shifting_hshfo: number;
  cargo_shifting_lshfo: number;
  cargo_shifting_ulsfo: number;
  cargo_shifting_hsmdo: number;
  cargo_shifting_lsmdo: number;
  cargo_shifting_hsmgo: number;
  cargo_shifting_lsmgo: number;
  // MRV
  cargo_shifting_propane: number;
  cargo_shifting_butane: number;
  cargo_shifting_lng: number;
  cargo_shifting_methanol: number;
  cargo_shifting_ethanol: number;
  cargo_shifting_other: number;

  // auxiliary engine consumptions breakdown
  ae_basic_load_hshfo: number;
  ae_basic_load_lshfo: number;
  ae_basic_load_ulsfo: number;
  ae_basic_load_hsmdo: number;
  ae_basic_load_lsmdo: number;
  ae_basic_load_hsmgo: number;
  ae_basic_load_lsmgo: number;

  ae_basic_load_hfo: number;
  ae_basic_load_lfo: number;
  ae_basic_load_mgo: number;
  ae_basic_load_mdo: number;
  ae_basic_load_b10lfo: number;
  ae_basic_load_b10mgo: number;
  ae_basic_load_biolfo: number;
  ae_basic_load_biomgo: number;
  ae_basic_load_ulsfo2020: number;
  ae_basic_load_ulslfo2020: number;
  ae_basic_load_ulsmdo2020: number;
  ae_basic_load_ulsmgo2020: number;
  ae_basic_load_vlsfo2020: number;
  ae_basic_load_vlslfo2020: number;
  ae_basic_load_lpgp: number;
  ae_basic_load_lpgb: number;
  ae_basic_load_bio_lng: number;
  ae_basic_load_lng: number;
  ae_basic_load_methanol: number;
  ae_basic_load_ethanol: number;
  ae_basic_load_other: number;
  ae_basic_load_hvo: number;
  ae_basic_load_fame: number;
  ae_basic_load_biodiesel: number;

  ae_ballast_operations_hshfo: number;
  ae_ballast_operations_lshfo: number;
  ae_ballast_operations_ulsfo: number;
  ae_ballast_operations_hsmdo: number;
  ae_ballast_operations_lsmdo: number;
  ae_ballast_operations_hsmgo: number;
  ae_ballast_operations_lsmgo: number;

  ae_ballast_operations_hfo: number;
  ae_ballast_operations_lfo: number;
  ae_ballast_operations_mgo: number;
  ae_ballast_operations_mdo: number;
  ae_ballast_operations_b10lfo: number;
  ae_ballast_operations_b10mgo: number;
  ae_ballast_operations_biolfo: number;
  ae_ballast_operations_biomgo: number;
  ae_ballast_operations_ulsfo2020: number;
  ae_ballast_operations_ulslfo2020: number;
  ae_ballast_operations_ulsmdo2020: number;
  ae_ballast_operations_ulsmgo2020: number;
  ae_ballast_operations_vlsfo2020: number;
  ae_ballast_operations_vlslfo2020: number;
  ae_ballast_operations_lpgp: number;
  ae_ballast_operations_lpgb: number;
  ae_ballast_operations_bio_lng: number;
  ae_ballast_operations_lng: number;
  ae_ballast_operations_methanol: number;
  ae_ballast_operations_ethanol: number;
  ae_ballast_operations_other: number;
  ae_ballast_operations_hvo: number;
  ae_ballast_operations_fame: number;
  ae_ballast_operations_biodiesel: number;

  ae_crane_operations_hshfo: number;
  ae_crane_operations_lshfo: number;
  ae_crane_operations_ulsfo: number;
  ae_crane_operations_hsmdo: number;
  ae_crane_operations_lsmdo: number;
  ae_crane_operations_hsmgo: number;
  ae_crane_operations_lsmgo: number;

  ae_crane_operations_hfo: number;
  ae_crane_operations_lfo: number;
  ae_crane_operations_mgo: number;
  ae_crane_operations_mdo: number;
  ae_crane_operations_b10lfo: number;
  ae_crane_operations_b10mgo: number;
  ae_crane_operations_biolfo: number;
  ae_crane_operations_biomgo: number;
  ae_crane_operations_ulsfo2020: number;
  ae_crane_operations_ulslfo2020: number;
  ae_crane_operations_ulsmdo2020: number;
  ae_crane_operations_ulsmgo2020: number;
  ae_crane_operations_vlsfo2020: number;
  ae_crane_operations_vlslfo2020: number;
  ae_crane_operations_lpgp: number;
  ae_crane_operations_lpgb: number;
  ae_crane_operations_bio_lng: number;
  ae_crane_operations_lng: number;
  ae_crane_operations_methanol: number;
  ae_crane_operations_ethanol: number;
  ae_crane_operations_other: number;
  ae_crane_operations_hvo: number;
  ae_crane_operations_fame: number;
  ae_crane_operations_biodiesel: number;

  ae_cdl_operations_hshfo: number;
  ae_cdl_operations_lshfo: number;
  ae_cdl_operations_ulsfo: number;
  ae_cdl_operations_hsmdo: number;
  ae_cdl_operations_lsmdo: number;
  ae_cdl_operations_hsmgo: number;
  ae_cdl_operations_lsmgo: number;

  ae_cdl_operations_hfo: number;
  ae_cdl_operations_lfo: number;
  ae_cdl_operations_mgo: number;
  ae_cdl_operations_mdo: number;
  ae_cdl_operations_b10lfo: number;
  ae_cdl_operations_b10mgo: number;
  ae_cdl_operations_biolfo: number;
  ae_cdl_operations_biomgo: number;
  ae_cdl_operations_ulsfo2020: number;
  ae_cdl_operations_ulslfo2020: number;
  ae_cdl_operations_ulsmdo2020: number;
  ae_cdl_operations_ulsmgo2020: number;
  ae_cdl_operations_vlsfo2020: number;
  ae_cdl_operations_vlslfo2020: number;
  ae_cdl_operations_lpgp: number;
  ae_cdl_operations_lpgb: number;
  ae_cdl_operations_bio_lng: number;
  ae_cdl_operations_lng: number;
  ae_cdl_operations_methanol: number;
  ae_cdl_operations_ethanol: number;
  ae_cdl_operations_other: number;
  ae_cdl_operations_hvo: number;
  ae_cdl_operations_fame: number;
  ae_cdl_operations_biodiesel: number;

  ae_cargo_heating_hshfo: number;
  ae_cargo_heating_lshfo: number;
  ae_cargo_heating_ulsfo: number;
  ae_cargo_heating_hsmdo: number;
  ae_cargo_heating_lsmdo: number;
  ae_cargo_heating_hsmgo: number;
  ae_cargo_heating_lsmgo: number;
  // CII
  ae_cargo_heating_hfo: number;
  ae_cargo_heating_lfo: number;
  ae_cargo_heating_mgo: number;
  ae_cargo_heating_mdo: number;
  ae_cargo_heating_b10lfo: number;
  ae_cargo_heating_b10mgo: number;
  ae_cargo_heating_biolfo: number;
  ae_cargo_heating_biomgo: number;
  ae_cargo_heating_ulsfo2020: number;
  ae_cargo_heating_ulslfo2020: number;
  ae_cargo_heating_ulsmdo2020: number;
  ae_cargo_heating_ulsmgo2020: number;
  ae_cargo_heating_vlsfo2020: number;
  ae_cargo_heating_vlslfo2020: number;
  ae_cargo_heating_lpgp: number;
  ae_cargo_heating_lpgb: number;
  ae_cargo_heating_bio_lng: number;
  ae_cargo_heating_lng: number;
  ae_cargo_heating_methanol: number;
  ae_cargo_heating_ethanol: number;
  ae_cargo_heating_other: number;
  ae_cargo_heating_hvo: number;
  ae_cargo_heating_fame: number;
  ae_cargo_heating_biodiesel: number;

  ae_cargo_cooling_hshfo: number;
  ae_cargo_cooling_lshfo: number;
  ae_cargo_cooling_ulsfo: number;
  ae_cargo_cooling_hsmdo: number;
  ae_cargo_cooling_lsmdo: number;
  ae_cargo_cooling_hsmgo: number;
  ae_cargo_cooling_lsmgo: number;
  // CII
  ae_cargo_cooling_hfo: number;
  ae_cargo_cooling_lfo: number;
  ae_cargo_cooling_mgo: number;
  ae_cargo_cooling_mdo: number;
  ae_cargo_cooling_b10lfo: number;
  ae_cargo_cooling_b10mgo: number;
  ae_cargo_cooling_biolfo: number;
  ae_cargo_cooling_biomgo: number;
  ae_cargo_cooling_ulsfo2020: number;
  ae_cargo_cooling_ulslfo2020: number;
  ae_cargo_cooling_ulsmdo2020: number;
  ae_cargo_cooling_ulsmgo2020: number;
  ae_cargo_cooling_vlsfo2020: number;
  ae_cargo_cooling_vlslfo2020: number;
  ae_cargo_cooling_lpgp: number;
  ae_cargo_cooling_lpgb: number;
  ae_cargo_cooling_bio_lng: number;
  ae_cargo_cooling_lng: number;
  ae_cargo_cooling_methanol: number;
  ae_cargo_cooling_ethanol: number;
  ae_cargo_cooling_other: number;
  ae_cargo_cooling_hvo: number;
  ae_cargo_cooling_fame: number;
  ae_cargo_cooling_biodiesel: number;

  ae_tank_cleaning_hshfo: number;
  ae_tank_cleaning_lshfo: number;
  ae_tank_cleaning_ulsfo: number;
  ae_tank_cleaning_hsmdo: number;
  ae_tank_cleaning_lsmdo: number;
  ae_tank_cleaning_hsmgo: number;
  ae_tank_cleaning_lsmgo: number;
  // CII
  ae_tank_cleaning_hfo: number;
  ae_tank_cleaning_lfo: number;
  ae_tank_cleaning_mgo: number;
  ae_tank_cleaning_mdo: number;
  ae_tank_cleaning_b10lfo: number;
  ae_tank_cleaning_b10mgo: number;
  ae_tank_cleaning_biolfo: number;
  ae_tank_cleaning_biomgo: number;
  ae_tank_cleaning_ulsfo2020: number;
  ae_tank_cleaning_ulslfo2020: number;
  ae_tank_cleaning_ulsmdo2020: number;
  ae_tank_cleaning_ulsmgo2020: number;
  ae_tank_cleaning_vlsfo2020: number;
  ae_tank_cleaning_vlslfo2020: number;
  ae_tank_cleaning_lpgp: number;
  ae_tank_cleaning_lpgb: number;
  ae_tank_cleaning_bio_lng: number;
  ae_tank_cleaning_lng: number;
  ae_tank_cleaning_methanol: number;
  ae_tank_cleaning_ethanol: number;
  ae_tank_cleaning_other: number;
  ae_tank_cleaning_hvo: number;
  ae_tank_cleaning_fame: number;
  ae_tank_cleaning_biodiesel: number;

  ae_cargo_shifting_hshfo: number;
  ae_cargo_shifting_lshfo: number;
  ae_cargo_shifting_ulsfo: number;
  ae_cargo_shifting_hsmdo: number;
  ae_cargo_shifting_lsmdo: number;
  ae_cargo_shifting_hsmgo: number;
  ae_cargo_shifting_lsmgo: number;
  // CII
  ae_cargo_shifting_hfo: number;
  ae_cargo_shifting_lfo: number;
  ae_cargo_shifting_mgo: number;
  ae_cargo_shifting_mdo: number;
  ae_cargo_shifting_b10lfo: number;
  ae_cargo_shifting_b10mgo: number;
  ae_cargo_shifting_biolfo: number;
  ae_cargo_shifting_biomgo: number;
  ae_cargo_shifting_ulsfo2020: number;
  ae_cargo_shifting_ulslfo2020: number;
  ae_cargo_shifting_ulsmdo2020: number;
  ae_cargo_shifting_ulsmgo2020: number;
  ae_cargo_shifting_vlsfo2020: number;
  ae_cargo_shifting_vlslfo2020: number;
  ae_cargo_shifting_lpgp: number;
  ae_cargo_shifting_lpgb: number;
  ae_cargo_shifting_bio_lng: number;
  ae_cargo_shifting_lng: number;
  ae_cargo_shifting_methanol: number;
  ae_cargo_shifting_ethanol: number;
  ae_cargo_shifting_other: number;
  ae_cargo_shifting_hvo: number;
  ae_cargo_shifting_fame: number;
  ae_cargo_shifting_biodiesel: number;

  ae_tank_cooling_hfo: number;
  ae_tank_cooling_lfo: number;
  ae_tank_cooling_mgo: number;
  ae_tank_cooling_mdo: number;
  ae_tank_cooling_b10lfo: number;
  ae_tank_cooling_b10mgo: number;
  ae_tank_cooling_biolfo: number;
  ae_tank_cooling_biomgo: number;
  ae_tank_cooling_ulsfo2020: number;
  ae_tank_cooling_ulslfo2020: number;
  ae_tank_cooling_ulsmdo2020: number;
  ae_tank_cooling_ulsmgo2020: number;
  ae_tank_cooling_vlsfo2020: number;
  ae_tank_cooling_vlslfo2020: number;
  ae_tank_cooling_lpgp: number;
  ae_tank_cooling_lpgb: number;
  ae_tank_cooling_bio_lng: number;
  ae_tank_cooling_lng: number;
  ae_tank_cooling_methanol: number;
  ae_tank_cooling_ethanol: number;
  ae_tank_cooling_other: number;
  ae_tank_cooling_hvo: number;
  ae_tank_cooling_fame: number;
  ae_tank_cooling_biodiesel: number;

  ae_inert_gas_systems_hshfo: number;
  ae_inert_gas_systems_lshfo: number;
  ae_inert_gas_systems_ulsfo: number;
  ae_inert_gas_systems_hsmdo: number;
  ae_inert_gas_systems_lsmdo: number;
  ae_inert_gas_systems_hsmgo: number;
  ae_inert_gas_systems_lsmgo: number;

  ae_inert_gas_systems_hfo: number;
  ae_inert_gas_systems_lfo: number;
  ae_inert_gas_systems_mgo: number;
  ae_inert_gas_systems_mdo: number;
  ae_inert_gas_systems_b10lfo: number;
  ae_inert_gas_systems_b10mgo: number;
  ae_inert_gas_systems_biolfo: number;
  ae_inert_gas_systems_biomgo: number;
  ae_inert_gas_systems_ulsfo2020: number;
  ae_inert_gas_systems_ulslfo2020: number;
  ae_inert_gas_systems_ulsmdo2020: number;
  ae_inert_gas_systems_ulsmgo2020: number;
  ae_inert_gas_systems_vlsfo2020: number;
  ae_inert_gas_systems_vlslfo2020: number;
  ae_inert_gas_systems_lpgp: number;
  ae_inert_gas_systems_lpgb: number;
  ae_inert_gas_systems_bio_lng: number;
  ae_inert_gas_systems_lng: number;
  ae_inert_gas_systems_methanol: number;
  ae_inert_gas_systems_ethanol: number;
  ae_inert_gas_systems_other: number;
  ae_inert_gas_systems_hvo: number;
  ae_inert_gas_systems_fame: number;
  ae_inert_gas_systems_biodiesel: number;

  ae_cargo_hold_washing_hshfo: number;
  ae_cargo_hold_washing_lshfo: number;
  ae_cargo_hold_washing_ulsfo: number;
  ae_cargo_hold_washing_hsmdo: number;
  ae_cargo_hold_washing_lsmdo: number;
  ae_cargo_hold_washing_hsmgo: number;
  ae_cargo_hold_washing_lsmgo: number;

  ae_cargo_hold_washing_hfo: number;
  ae_cargo_hold_washing_lfo: number;
  ae_cargo_hold_washing_mgo: number;
  ae_cargo_hold_washing_mdo: number;
  ae_cargo_hold_washing_b10lfo: number;
  ae_cargo_hold_washing_b10mgo: number;
  ae_cargo_hold_washing_biolfo: number;
  ae_cargo_hold_washing_biomgo: number;
  ae_cargo_hold_washing_ulsfo2020: number;
  ae_cargo_hold_washing_ulslfo2020: number;
  ae_cargo_hold_washing_ulsmdo2020: number;
  ae_cargo_hold_washing_ulsmgo2020: number;
  ae_cargo_hold_washing_vlsfo2020: number;
  ae_cargo_hold_washing_vlslfo2020: number;
  ae_cargo_hold_washing_lpgp: number;
  ae_cargo_hold_washing_lpgb: number;
  ae_cargo_hold_washing_bio_lng: number;
  ae_cargo_hold_washing_lng: number;
  ae_cargo_hold_washing_methanol: number;
  ae_cargo_hold_washing_ethanol: number;
  ae_cargo_hold_washing_other: number;
  ae_cargo_hold_washing_hvo: number;
  ae_cargo_hold_washing_fame: number;
  ae_cargo_hold_washing_biodiesel: number;

  ae_scrubber_hshfo: number;
  ae_scrubber_lshfo: number;
  ae_scrubber_ulsfo: number;
  ae_scrubber_hsmdo: number;
  ae_scrubber_lsmdo: number;
  ae_scrubber_hsmgo: number;
  ae_scrubber_lsmgo: number;

  ae_bunker_heating_hshfo: number;
  ae_bunker_heating_lshfo: number;
  ae_bunker_heating_ulsfo: number;
  ae_bunker_heating_hsmdo: number;
  ae_bunker_heating_lsmdo: number;
  ae_bunker_heating_hsmgo: number;
  ae_bunker_heating_lsmgo: number;

  ae_scrubber_hfo: number;
  ae_scrubber_lfo: number;
  ae_scrubber_mgo: number;
  ae_scrubber_mdo: number;
  ae_scrubber_b10lfo: number;
  ae_scrubber_b10mgo: number;
  ae_scrubber_biolfo: number;
  ae_scrubber_biomgo: number;
  ae_scrubber_ulsfo2020: number;
  ae_scrubber_ulslfo2020: number;
  ae_scrubber_ulsmdo2020: number;
  ae_scrubber_ulsmgo2020: number;
  ae_scrubber_vlsfo2020: number;
  ae_scrubber_vlslfo2020: number;
  ae_scrubber_lpgp: number;
  ae_scrubber_lpgb: number;
  ae_scrubber_bio_lng: number;
  ae_scrubber_lng: number;
  ae_scrubber_methanol: number;
  ae_scrubber_ethanol: number;
  ae_scrubber_other: number;
  ae_scrubber_hvo: number;
  ae_scrubber_fame: number;
  ae_scrubber_biodiesel: number;

  ae_bunker_heating_hfo: number;
  ae_bunker_heating_lfo: number;
  ae_bunker_heating_mgo: number;
  ae_bunker_heating_mdo: number;
  ae_bunker_heating_b10lfo: number;
  ae_bunker_heating_b10mgo: number;
  ae_bunker_heating_biolfo: number;
  ae_bunker_heating_biomgo: number;
  ae_bunker_heating_ulsfo2020: number;
  ae_bunker_heating_ulslfo2020: number;
  ae_bunker_heating_ulsmdo2020: number;
  ae_bunker_heating_ulsmgo2020: number;
  ae_bunker_heating_vlsfo2020: number;
  ae_bunker_heating_vlslfo2020: number;
  ae_bunker_heating_lpgp: number;
  ae_bunker_heating_lpgb: number;
  ae_bunker_heating_bio_lng: number;
  ae_bunker_heating_lng: number;
  ae_bunker_heating_methanol: number;
  ae_bunker_heating_ethanol: number;
  ae_bunker_heating_other: number;
  ae_bunker_heating_hvo: number;
  ae_bunker_heating_fame: number;
  ae_bunker_heating_biodiesel: number;

  ae_tank_pressure_control_hshfo: number;
  ae_tank_pressure_control_lshfo: number;
  ae_tank_pressure_control_ulsfo: number;
  ae_tank_pressure_control_hsmdo: number;
  ae_tank_pressure_control_lsmdo: number;
  ae_tank_pressure_control_hsmgo: number;
  ae_tank_pressure_control_lsmgo: number;
  ae_tank_pressure_control_propane: number;
  ae_tank_pressure_control_butane: number;
  ae_tank_pressure_control_hfo: number;
  ae_tank_pressure_control_lfo: number;
  ae_tank_pressure_control_mgo: number;
  ae_tank_pressure_control_mdo: number;
  ae_tank_pressure_control_b10lfo: number;
  ae_tank_pressure_control_b10mgo: number;
  ae_tank_pressure_control_biolfo: number;
  ae_tank_pressure_control_biomgo: number;
  ae_tank_pressure_control_ulsfo2020: number;
  ae_tank_pressure_control_ulslfo2020: number;
  ae_tank_pressure_control_ulsmdo2020: number;
  ae_tank_pressure_control_ulsmgo2020: number;
  ae_tank_pressure_control_vlsfo2020: number;
  ae_tank_pressure_control_vlslfo2020: number;
  ae_tank_pressure_control_lpgp: number;
  ae_tank_pressure_control_lpgb: number;
  ae_tank_pressure_control_bio_lng: number;
  ae_tank_pressure_control_lng: number;
  ae_tank_pressure_control_methanol: number;
  ae_tank_pressure_control_ethanol: number;
  ae_tank_pressure_control_other: number;
  ae_tank_pressure_control_hvo: number;
  ae_tank_pressure_control_fame: number;
  ae_tank_pressure_control_biodiesel: number;

  dg_basic_load_hshfo: number;
  dg_basic_load_lshfo: number;
  dg_basic_load_ulsfo: number;
  dg_basic_load_lsmdo: number;
  dg_basic_load_hsmgo: number;
  dg_basic_load_lsmgo: number;

  dg_basic_load_hfo: number;
  dg_basic_load_lfo: number;
  dg_basic_load_mgo: number;
  dg_basic_load_mdo: number;
  dg_basic_load_b10lfo: number;
  dg_basic_load_b10mgo: number;
  dg_basic_load_biolfo: number;
  dg_basic_load_biomgo: number;
  dg_basic_load_ulsfo2020: number;
  dg_basic_load_ulslfo2020: number;
  dg_basic_load_ulsmdo2020: number;
  dg_basic_load_ulsmgo2020: number;
  dg_basic_load_vlsfo2020: number;
  dg_basic_load_vlslfo2020: number;
  dg_basic_load_lpgp: number;
  dg_basic_load_lpgb: number;
  dg_basic_load_bio_lng: number;
  dg_basic_load_lng: number;
  dg_basic_load_methanol: number;
  dg_basic_load_ethanol: number;
  dg_basic_load_other: number;
  dg_basic_load_hvo: number;
  dg_basic_load_fame: number;
  dg_basic_load_biodiesel: number;

  prop_cons_hshfo: number;
  prop_cons_lshfo: number;
  prop_cons_ulsfo: number;
  prop_cons_lsmdo: number;
  prop_cons_hsmgo: number;
  prop_cons_lsmgo: number;

  prop_cons_hfo: number;
  prop_cons_lfo: number;
  prop_cons_mgo: number;
  prop_cons_mdo: number;
  prop_cons_b10lfo: number;
  prop_cons_b10mgo: number;
  prop_cons_biolfo: number;
  prop_cons_biomgo: number;
  prop_cons_ulsfo2020: number;
  prop_cons_ulslfo2020: number;
  prop_cons_ulsmdo2020: number;
  prop_cons_ulsmgo2020: number;
  prop_cons_vlsfo2020: number;
  prop_cons_vlslfo2020: number;
  prop_cons_lpgp: number;
  prop_cons_lpgb: number;
  prop_cons_bio_lng: number;
  prop_cons_lng: number;
  prop_cons_methanol: number;
  prop_cons_ethanol: number;
  prop_cons_other: number;
  prop_cons_hvo: number;
  prop_cons_fame: number;
  prop_cons_biodiesel: number;

  dg_ballast_operations_hshfo: number;
  dg_ballast_operations_lshfo: number;
  dg_ballast_operations_ulsfo: number;
  dg_ballast_operations_lsmdo: number;
  dg_ballast_operations_hsmgo: number;
  dg_ballast_operations_lsmgo: number;

  dg_ballast_operations_hfo: number;
  dg_ballast_operations_lfo: number;
  dg_ballast_operations_mgo: number;
  dg_ballast_operations_mdo: number;
  dg_ballast_operations_b10lfo: number;
  dg_ballast_operations_b10mgo: number;
  dg_ballast_operations_biolfo: number;
  dg_ballast_operations_biomgo: number;
  dg_ballast_operations_ulsfo2020: number;
  dg_ballast_operations_ulslfo2020: number;
  dg_ballast_operations_ulsmdo2020: number;
  dg_ballast_operations_ulsmgo2020: number;
  dg_ballast_operations_vlsfo2020: number;
  dg_ballast_operations_vlslfo2020: number;
  dg_ballast_operations_lpgp: number;
  dg_ballast_operations_lpgb: number;
  dg_ballast_operations_bio_lng: number;
  dg_ballast_operations_lng: number;
  dg_ballast_operations_methanol: number;
  dg_ballast_operations_ethanol: number;
  dg_ballast_operations_other: number;
  dg_ballast_operations_hvo: number;
  dg_ballast_operations_fame: number;
  dg_ballast_operations_biodiesel: number;

  dg_cargo_heating_hshfo: number;
  dg_cargo_heating_lshfo: number;
  dg_cargo_heating_ulsfo: number;
  dg_cargo_heating_lsmdo: number;
  dg_cargo_heating_hsmgo: number;
  dg_cargo_heating_lsmgo: number;

  dg_cargo_heating_hfo: number;
  dg_cargo_heating_lfo: number;
  dg_cargo_heating_mgo: number;
  dg_cargo_heating_mdo: number;
  dg_cargo_heating_b10lfo: number;
  dg_cargo_heating_b10mgo: number;
  dg_cargo_heating_biolfo: number;
  dg_cargo_heating_biomgo: number;
  dg_cargo_heating_ulsfo2020: number;
  dg_cargo_heating_ulslfo2020: number;
  dg_cargo_heating_ulsmdo2020: number;
  dg_cargo_heating_ulsmgo2020: number;
  dg_cargo_heating_vlsfo2020: number;
  dg_cargo_heating_vlslfo2020: number;
  dg_cargo_heating_lpgp: number;
  dg_cargo_heating_lpgb: number;
  dg_cargo_heating_bio_lng: number;
  dg_cargo_heating_lng: number;
  dg_cargo_heating_methanol: number;
  dg_cargo_heating_ethanol: number;
  dg_cargo_heating_other: number;
  dg_cargo_heating_hvo: number;
  dg_cargo_heating_fame: number;
  dg_cargo_heating_biodiesel: number;

  dg_tank_cleaning_hshfo: number;
  dg_tank_cleaning_lshfo: number;
  dg_tank_cleaning_ulsfo: number;
  dg_tank_cleaning_lsmdo: number;
  dg_tank_cleaning_hsmgo: number;
  dg_tank_cleaning_lsmgo: number;

  dg_tank_cleaning_hfo: number;
  dg_tank_cleaning_lfo: number;
  dg_tank_cleaning_mgo: number;
  dg_tank_cleaning_mdo: number;
  dg_tank_cleaning_b10lfo: number;
  dg_tank_cleaning_b10mgo: number;
  dg_tank_cleaning_biolfo: number;
  dg_tank_cleaning_biomgo: number;
  dg_tank_cleaning_ulsfo2020: number;
  dg_tank_cleaning_ulslfo2020: number;
  dg_tank_cleaning_ulsmdo2020: number;
  dg_tank_cleaning_ulsmgo2020: number;
  dg_tank_cleaning_vlsfo2020: number;
  dg_tank_cleaning_vlslfo2020: number;
  dg_tank_cleaning_lpgp: number;
  dg_tank_cleaning_lpgb: number;
  dg_tank_cleaning_bio_lng: number;
  dg_tank_cleaning_lng: number;
  dg_tank_cleaning_methanol: number;
  dg_tank_cleaning_ethanol: number;
  dg_tank_cleaning_other: number;
  dg_tank_cleaning_hvo: number;
  dg_tank_cleaning_fame: number;
  dg_tank_cleaning_biodiesel: number;

  dg_cargo_shifting_hshfo: number;
  dg_cargo_shifting_lshfo: number;
  dg_cargo_shifting_ulsfo: number;
  dg_cargo_shifting_lsmdo: number;
  dg_cargo_shifting_hsmgo: number;
  dg_cargo_shifting_lsmgo: number;

  dg_cargo_shifting_hfo: number;
  dg_cargo_shifting_lfo: number;
  dg_cargo_shifting_mgo: number;
  dg_cargo_shifting_mdo: number;
  dg_cargo_shifting_b10lfo: number;
  dg_cargo_shifting_b10mgo: number;
  dg_cargo_shifting_biolfo: number;
  dg_cargo_shifting_biomgo: number;
  dg_cargo_shifting_ulsfo2020: number;
  dg_cargo_shifting_ulslfo2020: number;
  dg_cargo_shifting_ulsmdo2020: number;
  dg_cargo_shifting_ulsmgo2020: number;
  dg_cargo_shifting_vlsfo2020: number;
  dg_cargo_shifting_vlslfo2020: number;
  dg_cargo_shifting_lpgp: number;
  dg_cargo_shifting_lpgb: number;
  dg_cargo_shifting_bio_lng: number;
  dg_cargo_shifting_lng: number;
  dg_cargo_shifting_methanol: number;
  dg_cargo_shifting_ethanol: number;
  dg_cargo_shifting_other: number;
  dg_cargo_shifting_hvo: number;
  dg_cargo_shifting_fame: number;
  dg_cargo_shifting_biodiesel: number;

  dg_inert_gas_systems_hshfo: number;
  dg_inert_gas_systems_lshfo: number;
  dg_inert_gas_systems_ulsfo: number;
  dg_inert_gas_systems_lsmdo: number;
  dg_inert_gas_systems_hsmgo: number;
  dg_inert_gas_systems_lsmgo: number;

  dg_inert_gas_systems_hfo: number;
  dg_inert_gas_systems_lfo: number;
  dg_inert_gas_systems_mgo: number;
  dg_inert_gas_systems_mdo: number;
  dg_inert_gas_systems_b10lfo: number;
  dg_inert_gas_systems_b10mgo: number;
  dg_inert_gas_systems_biolfo: number;
  dg_inert_gas_systems_biomgo: number;
  dg_inert_gas_systems_ulsfo2020: number;
  dg_inert_gas_systems_ulslfo2020: number;
  dg_inert_gas_systems_ulsmdo2020: number;
  dg_inert_gas_systems_ulsmgo2020: number;
  dg_inert_gas_systems_vlsfo2020: number;
  dg_inert_gas_systems_vlslfo2020: number;
  dg_inert_gas_systems_lpgp: number;
  dg_inert_gas_systems_lpgb: number;
  dg_inert_gas_systems_bio_lng: number;
  dg_inert_gas_systems_lng: number;
  dg_inert_gas_systems_methanol: number;
  dg_inert_gas_systems_ethanol: number;
  dg_inert_gas_systems_other: number;
  dg_inert_gas_systems_hvo: number;
  dg_inert_gas_systems_fame: number;
  dg_inert_gas_systems_biodiesel: number;

  // auxiliary boiler consumptions breakdown
  ab_basic_load_hshfo: number;
  ab_basic_load_lshfo: number;
  ab_basic_load_ulsfo: number;
  ab_basic_load_hsmdo: number;
  ab_basic_load_lsmdo: number;
  ab_basic_load_hsmgo: number;
  ab_basic_load_lsmgo: number;

  ab_basic_load_hfo: number;
  ab_basic_load_lfo: number;
  ab_basic_load_mgo: number;
  ab_basic_load_mdo: number;
  ab_basic_load_b10lfo: number;
  ab_basic_load_b10mgo: number;
  ab_basic_load_biolfo: number;
  ab_basic_load_biomgo: number;
  ab_basic_load_ulsfo2020: number;
  ab_basic_load_ulslfo2020: number;
  ab_basic_load_ulsmdo2020: number;
  ab_basic_load_ulsmgo2020: number;
  ab_basic_load_vlsfo2020: number;
  ab_basic_load_vlslfo2020: number;
  ab_basic_load_lpgp: number;
  ab_basic_load_lpgb: number;
  ab_basic_load_bio_lng: number;
  ab_basic_load_lng: number;
  ab_basic_load_methanol: number;
  ab_basic_load_ethanol: number;
  ab_basic_load_other: number;
  ab_basic_load_hvo: number;
  ab_basic_load_fame: number;
  ab_basic_load_biodiesel: number;

  ab_ballast_operations_hshfo: number;
  ab_ballast_operations_lshfo: number;
  ab_ballast_operations_ulsfo: number;
  ab_ballast_operations_hsmdo: number;
  ab_ballast_operations_lsmdo: number;
  ab_ballast_operations_hsmgo: number;
  ab_ballast_operations_lsmgo: number;
  // CII
  ab_ballast_operations_hfo: number;
  ab_ballast_operations_lfo: number;
  ab_ballast_operations_mgo: number;
  ab_ballast_operations_mdo: number;
  ab_ballast_operations_b10lfo: number;
  ab_ballast_operations_b10mgo: number;
  ab_ballast_operations_biolfo: number;
  ab_ballast_operations_biomgo: number;
  ab_ballast_operations_ulsfo2020: number;
  ab_ballast_operations_ulslfo2020: number;
  ab_ballast_operations_ulsmdo2020: number;
  ab_ballast_operations_ulsmgo2020: number;
  ab_ballast_operations_vlsfo2020: number;
  ab_ballast_operations_vlslfo2020: number;
  ab_ballast_operations_lpgp: number;
  ab_ballast_operations_lpgb: number;
  ab_ballast_operations_bio_lng: number;
  ab_ballast_operations_lng: number;
  ab_ballast_operations_methanol: number;
  ab_ballast_operations_ethanol: number;
  ab_ballast_operations_other: number;
  ab_ballast_operations_hvo: number;
  ab_ballast_operations_fame: number;
  ab_ballast_operations_biodiesel: number;

  ab_cargo_heating_hshfo: number;
  ab_cargo_heating_lshfo: number;
  ab_cargo_heating_ulsfo: number;
  ab_cargo_heating_hsmdo: number;
  ab_cargo_heating_lsmdo: number;
  ab_cargo_heating_hsmgo: number;
  ab_cargo_heating_lsmgo: number;
  // CII
  ab_cargo_heating_hfo: number;
  ab_cargo_heating_lfo: number;
  ab_cargo_heating_mgo: number;
  ab_cargo_heating_mdo: number;
  ab_cargo_heating_b10lfo: number;
  ab_cargo_heating_b10mgo: number;
  ab_cargo_heating_biolfo: number;
  ab_cargo_heating_biomgo: number;
  ab_cargo_heating_ulsfo2020: number;
  ab_cargo_heating_ulslfo2020: number;
  ab_cargo_heating_ulsmdo2020: number;
  ab_cargo_heating_ulsmgo2020: number;
  ab_cargo_heating_vlsfo2020: number;
  ab_cargo_heating_vlslfo2020: number;
  ab_cargo_heating_lpgp: number;
  ab_cargo_heating_lpgb: number;
  ab_cargo_heating_bio_lng: number;
  ab_cargo_heating_lng: number;
  ab_cargo_heating_methanol: number;
  ab_cargo_heating_ethanol: number;
  ab_cargo_heating_other: number;
  ab_cargo_heating_hvo: number;
  ab_cargo_heating_fame: number;
  ab_cargo_heating_biodiesel: number;

  ab_bunker_heating_hshfo: number;
  ab_bunker_heating_lshfo: number;
  ab_bunker_heating_ulsfo: number;
  ab_bunker_heating_hsmdo: number;
  ab_bunker_heating_lsmdo: number;
  ab_bunker_heating_hsmgo: number;
  ab_bunker_heating_lsmgo: number;
  ab_bunker_heating_hfo: number;
  ab_bunker_heating_lfo: number;
  ab_bunker_heating_mgo: number;
  ab_bunker_heating_mdo: number;
  ab_bunker_heating_b10lfo: number;
  ab_bunker_heating_b10mgo: number;
  ab_bunker_heating_biolfo: number;
  ab_bunker_heating_biomgo: number;
  ab_bunker_heating_ulsfo2020: number;
  ab_bunker_heating_ulslfo2020: number;
  ab_bunker_heating_ulsmdo2020: number;
  ab_bunker_heating_ulsmgo2020: number;
  ab_bunker_heating_vlsfo2020: number;
  ab_bunker_heating_vlslfo2020: number;
  ab_bunker_heating_lpgp: number;
  ab_bunker_heating_lpgb: number;
  ab_bunker_heating_bio_lng: number;
  ab_bunker_heating_lng: number;
  ab_bunker_heating_methanol: number;
  ab_bunker_heating_ethanol: number;
  ab_bunker_heating_other: number;
  ab_bunker_heating_hvo: number;
  ab_bunker_heating_fame: number;
  ab_bunker_heating_biodiesel: number;

  c_slip_lng: number;

  ab_tank_cleaning_hshfo: number;
  ab_tank_cleaning_lshfo: number;
  ab_tank_cleaning_ulsfo: number;
  ab_tank_cleaning_hsmdo: number;
  ab_tank_cleaning_lsmdo: number;
  ab_tank_cleaning_hsmgo: number;
  ab_tank_cleaning_lsmgo: number;
  // CII
  ab_tank_cleaning_hfo: number;
  ab_tank_cleaning_lfo: number;
  ab_tank_cleaning_mgo: number;
  ab_tank_cleaning_mdo: number;
  ab_tank_cleaning_b10lfo: number;
  ab_tank_cleaning_b10mgo: number;
  ab_tank_cleaning_biolfo: number;
  ab_tank_cleaning_biomgo: number;
  ab_tank_cleaning_ulsfo2020: number;
  ab_tank_cleaning_ulslfo2020: number;
  ab_tank_cleaning_ulsmdo2020: number;
  ab_tank_cleaning_ulsmgo2020: number;
  ab_tank_cleaning_vlsfo2020: number;
  ab_tank_cleaning_vlslfo2020: number;
  ab_tank_cleaning_lpgp: number;
  ab_tank_cleaning_lpgb: number;
  ab_tank_cleaning_bio_lng: number;
  ab_tank_cleaning_lng: number;
  ab_tank_cleaning_methanol: number;
  ab_tank_cleaning_ethanol: number;
  ab_tank_cleaning_other: number;
  ab_tank_cleaning_hvo: number;
  ab_tank_cleaning_fame: number;
  ab_tank_cleaning_biodiesel: number;

  ab_cargo_shifting_hshfo: number;
  ab_cargo_shifting_lshfo: number;
  ab_cargo_shifting_ulsfo: number;
  ab_cargo_shifting_hsmdo: number;
  ab_cargo_shifting_lsmdo: number;
  ab_cargo_shifting_hsmgo: number;
  ab_cargo_shifting_lsmgo: number;
  // CII
  ab_cargo_shifting_hfo: number;
  ab_cargo_shifting_lfo: number;
  ab_cargo_shifting_mgo: number;
  ab_cargo_shifting_mdo: number;
  ab_cargo_shifting_b10lfo: number;
  ab_cargo_shifting_b10mgo: number;
  ab_cargo_shifting_biolfo: number;
  ab_cargo_shifting_biomgo: number;
  ab_cargo_shifting_ulsfo2020: number;
  ab_cargo_shifting_ulslfo2020: number;
  ab_cargo_shifting_ulsmdo2020: number;
  ab_cargo_shifting_ulsmgo2020: number;
  ab_cargo_shifting_vlsfo2020: number;
  ab_cargo_shifting_vlslfo2020: number;
  ab_cargo_shifting_lpgp: number;
  ab_cargo_shifting_lpgb: number;
  ab_cargo_shifting_bio_lng: number;
  ab_cargo_shifting_lng: number;
  ab_cargo_shifting_methanol: number;
  ab_cargo_shifting_ethanol: number;
  ab_cargo_shifting_other: number;
  ab_cargo_shifting_hvo: number;
  ab_cargo_shifting_fame: number;
  ab_cargo_shifting_biodiesel: number;

  ab_inert_gas_systems_hshfo: number;
  ab_inert_gas_systems_lshfo: number;
  ab_inert_gas_systems_ulsfo: number;
  ab_inert_gas_systems_hsmdo: number;
  ab_inert_gas_systems_lsmdo: number;
  ab_inert_gas_systems_hsmgo: number;
  ab_inert_gas_systems_lsmgo: number;

  ab_inert_gas_systems_hfo: number;
  ab_inert_gas_systems_lfo: number;
  ab_inert_gas_systems_mgo: number;
  ab_inert_gas_systems_mdo: number;
  ab_inert_gas_systems_b10lfo: number;
  ab_inert_gas_systems_b10mgo: number;
  ab_inert_gas_systems_biolfo: number;
  ab_inert_gas_systems_biomgo: number;
  ab_inert_gas_systems_ulsfo2020: number;
  ab_inert_gas_systems_ulslfo2020: number;
  ab_inert_gas_systems_ulsmdo2020: number;
  ab_inert_gas_systems_ulsmgo2020: number;
  ab_inert_gas_systems_vlsfo2020: number;
  ab_inert_gas_systems_vlslfo2020: number;
  ab_inert_gas_systems_lpgp: number;
  ab_inert_gas_systems_lpgb: number;
  ab_inert_gas_systems_bio_lng: number;
  ab_inert_gas_systems_lng: number;
  ab_inert_gas_systems_methanol: number;
  ab_inert_gas_systems_ethanol: number;
  ab_inert_gas_systems_other: number;
  ab_inert_gas_systems_hvo: number;
  ab_inert_gas_systems_fame: number;
  ab_inert_gas_systems_biodiesel: number;

  ab_tank_pressure_control_hshfo: number;
  ab_tank_pressure_control_lshfo: number;
  ab_tank_pressure_control_ulsfo: number;
  ab_tank_pressure_control_hsmdo: number;
  ab_tank_pressure_control_lsmdo: number;
  ab_tank_pressure_control_hsmgo: number;
  ab_tank_pressure_control_lsmgo: number;
  ab_tank_pressure_control_propane: number;
  ab_tank_pressure_control_butane: number;
  ab_tank_pressure_control_hfo: number;
  ab_tank_pressure_control_lfo: number;
  ab_tank_pressure_control_mgo: number;
  ab_tank_pressure_control_mdo: number;
  ab_tank_pressure_control_b10lfo: number;
  ab_tank_pressure_control_b10mgo: number;
  ab_tank_pressure_control_biolfo: number;
  ab_tank_pressure_control_biomgo: number;
  ab_tank_pressure_control_ulsfo2020: number;
  ab_tank_pressure_control_ulslfo2020: number;
  ab_tank_pressure_control_ulsmdo2020: number;
  ab_tank_pressure_control_ulsmgo2020: number;
  ab_tank_pressure_control_vlsfo2020: number;
  ab_tank_pressure_control_vlslfo2020: number;
  ab_tank_pressure_control_lpgp: number;
  ab_tank_pressure_control_lpgb: number;
  ab_tank_pressure_control_bio_lng: number;
  ab_tank_pressure_control_lng: number;
  ab_tank_pressure_control_methanol: number;
  ab_tank_pressure_control_ethanol: number;
  ab_tank_pressure_control_other: number;
  ab_tank_pressure_control_hvo: number;
  ab_tank_pressure_control_fame: number;
  ab_tank_pressure_control_biodiesel: number;

  // total consumption for main engine, aux engine, boiler, and all engines
  total_fuel_consumption: number;
  total_basic_load_ae: number;
  total_basic_load_dg: number;
  total_prop_cons: number;
  total_basic_load_ab: number;
  total_me_consumption: number;
  total_ae_consumption: number;
  total_mdg_port_consumption: number;
  total_mdg_starboard_consumption: number;
  total_hdg_consumption: number;
  total_dg_consumption: number;
  total_boiler_consumption: number;
  total_other_cons: number;
  total_ae_cargo_cooling_cons: number;
  total_ae_cargo_heating_cons: number;
  total_ae_cargo_shifting_cons: number;
  total_ab_cargo_heating_cons: number;
  total_ab_cargo_shifting_cons: number;
  // total main engine and aux enigne consumptions normalized over 24 hours
  total_me_consumption_24: number;
  total_ae_consumption_24: number;
  total_dg_consumption_24: number;
  total_prop_consumption_24: number;
  total_cii_consumption_24: number;

  // number of aux engines
  n_aux_engines: number;

  // tender/zodiac
  tender_arctic_diesel: number;
  tender_automobile_diesel: number;
  tender_mgo: number;
  tender_ulg: number;

  // Water
  fwg_non_operational: boolean;
  fresh_water: number;
  fresh_water_consumption: number;
  technical_water: number;
  technical_water_consumption: number;
  distilled_water: number;
  caustic_soda: number;

  // Oil Transfer & Losses
  oil_time_of_transfer: Date;
  oil_quantity: number;
  oil_code: OilCode;
  oil_discharge: DischargeToCode;
  oil_comments: string;

  sludge_tank_quantity_lost: number;
  fuel_oil_drain_tank_quantity_lost: number;
  oily_bilge_tank_quantity_lost: number;
  waste_oil_tank_quantity_lost: number;
  sludge_tank_oil_code: OilCode;
  fuel_oil_drain_tank_oil_code: OilCode;
  oily_bilge_tank_oil_code: OilCode;
  waste_oil_tank_oil_code: OilCode;

  total_waste_oil: number;
  incinerated_waste_oil: number;
  evaporated_waste_oil: number;

  // Cylinder Oil Consumptions
  cyl_oil_code: CloLubricationCode;
  cyl_oil_bn: number;
  cyl_oil_density_at_15: number;
  cyl_oil_tank_temperature: number;
  cyl_oil_consumption: number;
  cyl_oil_kg_consumption: number;
  cyl_oil_expected_kg_consumption: number;
  cyl_oil_sloc: number;

  // Inert Gas Systems
  system_run_1_from: DateObject;
  system_run_1_to: DateObject;
  system_run_2_from: DateObject;
  system_run_2_to: DateObject;
  system_run_3_from: DateObject;
  system_run_3_to: DateObject;
  system_run_4_from: DateObject;
  system_run_4_to: DateObject;

  // Capital
  urea_consumption: number;

  // Cargo Heating Logs
  cargo_heating_logs: DropboxItem;
  port_operation_logs: DropboxItem;

  new_fuel_configuration: boolean;
}

export interface TvaApiStock {
  tank_transfers: TankTransferData[];
  tanks: Record<string, TankStock>;
  tank_cleaning_chemical: number;
  bilge_water: number;
  sludge: number;
  discharged_bilge_water: number;
  discharged_sludge: number;
  discharged_waste_oil: number;
  fresh_water: number;
  distilled_water: number;
  lube_oil_system: number;
  lube_oil_spare: number;
  cyl_oil_low_bn: number;
  cyl_oil: number;
  aux_engine_lube_oil: number;
  turbo_oil: number;
  hydraulic_oil: number;
  other_oil: number;
  total_waste_oil: number;
  incinerated_waste_oil: number;
  evaporated_waste_oil: number;
  tank_sounding: boolean;
  tank_based_reporting?: boolean;
  new_fuel_configuration: boolean;
}

// report as received from api
export interface TvaApiReport {
  _cls: string;
  report_number: number;
  // Root-level lifecycle status; absent on some legacy reports.
  status?: ReportStatus;
  cargo: TvaApiCargoDetails;
  bunker: TvaApiBunker;
  position: TvaApiPositionAndWeather;
  operational: TvaApiOperational;
  power: TvaApiPower;
  consumption: TvaApiConsumption;
  stock: TvaApiStock;
}

// The four classes the backend stores, each with its base type. getReportType
// reads the pairs one way and getReportCls reads them the other way.
const REPORT_CLASSES: readonly (readonly [string, ReportType])[] = [
  ['Report.PortReport', ReportType.Port],
  ['Report.SeaReport', ReportType.Sea],
  ['Report.AnchorReport', ReportType.Anchor],
  ['Report.ManeuveringReport', ReportType.Maneuvering],
];
const REPORT_TYPE_BY_CLASS = new Map(REPORT_CLASSES);
const REPORT_CLASS_BY_TYPE = new Map(
  REPORT_CLASSES.map(([cls, type]) => [type, cls] as const),
);

// The editor reads the class alone. V1 derives Departure, Sea (Arrival) and
// Drifting from one operational field each (maneuvering_type, arriving and
// anchor_drifting), and this read does not. The side menu creates only the four
// class types, so the editor must open a report as the type it created. If it
// did not, each rule that names a type would change its answer when the report
// opens again, and a field the user filled could disappear. The vessel reports
// list gets the derived type from the backend's pretty_report_type.
//
// A report with no class reads as Port.
const getReportType = (cls: string): ReportType =>
  REPORT_TYPE_BY_CLASS.get(cls) ?? ReportType.Port;

const convertToDate = (dateObject: DateObject): Date | undefined => {
  const rawDate = dateObject?.$date;
  if (!rawDate) return;

  const sourceDate = new Date(rawDate);

  return new Date(
    sourceDate.getTime() + sourceDate.getTimezoneOffset() * 60_000,
  );
};

// V1 wrote some maneuvering types as a Python 2 repr, for example u'departure'.
// The code inside the quotes is correct, so the read removes the wrapper. A V2
// save then writes the plain code.
const convertManeuveringType = (value?: string): string | undefined =>
  value?.replace(/^u'(.*)'$/, '$1');

const convertOperationalSection = (report: TvaApiReport): OperationalState => {
  const isPortReport = getReportType(report._cls) === ReportType.Port;
  const cargoWeight = report.operational.weight;
  return {
    vesselDetails: {
      reportNumber: report?.report_number,
      voyageNumber: report.operational.voyage_number,
      cpSpeed: report.operational.cp_speed,
      cpFuel: report.operational.cp_fuel,
      instructedSpeed: report.operational.instructed_speed,
      instructedFoc: report.operational.instructed_foc,
      otherSpeedSteaming: getOtherSpeedSteamingOption(
        report.operational.other_speed_steaming,
      ),
    },
    miscellaneous: {
      captainName: report.operational.captain_name,
      chiefEngineer: report.operational.chief_engineer,
      cargoWeight: isPortReport ? cargoWeight : undefined,
      operatingCode: report.operational.operating_code,
      ballastCode: report.operational.ballast_code,
      routingAdviceFollowed: report.operational.routing_advice_followed,
    },
    cargoDetails: {
      cargoWeight: isPortReport ? undefined : cargoWeight,
    },
    reportTime: {
      reportFrom: convertToDate(report.operational.report_from),
      reportTo: convertToDate(report.operational.report_to),
      // Until now eosp was written on submit and never read back. It holds a
      // value only while the ship arrives at EOSP, so a report that is not
      // arriving leaves the field empty. An older arriving report can hold a
      // blank eosp with the same instant in report_to, so that falls back
      // rather than showing a blank date.
      eosp: report.operational.arriving
        ? (convertToDate(report.operational.eosp) ??
          convertToDate(report.operational.report_to))
        : undefined,
      reportCreation: convertToDate(report.operational.report_creation),
      timezone: report.operational.timezone,
      previousTimezone: report.operational.previous_timezone,
      reportPeriod: report.operational.report_period,
      timeSailed: report.operational.time_sailed,
      reportSubmitted: convertToDate(report.operational.report_submitted),
    },
    portAndPosition: {
      portName: report.operational.port_name,
      portPosition: {
        latDegrees: report.operational.port_latitude_hours,
        latMinutes: report.operational.port_latitude_minutes,
        latSeconds: report.operational.port_latitude_seconds,
        latDirection: {
          code: report.operational.port_latitude_direction,
          name: report.operational.port_latitude_direction,
        },
        longDegrees: report.operational.port_longitude_hours,
        longMinutes: report.operational.port_longitude_minutes,
        longSeconds: report.operational.port_longitude_seconds,
        longDirection: {
          code: report.operational.port_longitude_direction,
          name: report.operational.port_longitude_direction,
        },
      },
    },
    stabilityConditions: {
      displacement: report.operational.displacement,
      gm: report.operational.gm,
      shearForce: report.operational.shear_force,
      bendingForce: report.operational.bending_force,
      torsion: report.operational.torsion,
      gmReserve: report.operational.gm_reserve,
      deadWeightReserve: report.operational.dead_weight_reserve,
      constantWeight: report.operational.constant_weight,
    },
    portOperationsTimestamp: {
      etd: convertToDate(report.operational.etd),
      norTendered: convertToDate(report.operational.nor_tendered),
      allFast: convertToDate(report.operational.all_fast),
      hosesConnected: convertToDate(report.operational.hoses_connected),
      commenceLoad: convertToDate(report.operational.commence),
      finishLoad: convertToDate(report.operational.finish),
      hosesDisconnected: convertToDate(report.operational.hoses_disconnected),
      lastLine: convertToDate(report.operational.standby_engines),
      cargoDocsOnboard: convertToDate(
        report.operational.cargo_documents_onboard,
      ),
      startDischarging: convertToDate(report.operational.start_discharging),
      endDischarging: convertToDate(report.operational.end_discharging),
      completedTankInspection: convertToDate(
        report.operational.completed_tank_inspection,
      ),
    },
    poolSpecificFields: {
      freshWaterDomestic: report.operational.fresh_water_domestic,
      freshWaterTankCleaning: report.operational.fresh_water_tank_cleaning,
      slopsResidualTanks: report.operational.slops_residual_tanks,
      slopsSlopTanks: report.operational.slops_slop_tanks,
      totalInterfaceOil: report.operational.total_interface_oil,
      totalInterfaceWater: report.operational.total_interface_water,
      firstLine: report.operational.first_line
        ? convertToDate(report.operational.first_line)
        : undefined,
      gangwayDown: report.operational.gangway_down
        ? convertToDate(report.operational.gangway_down)
        : undefined,
      cargoDocsOnboard: report.operational.cargo_docs_onboard
        ? convertToDate(report.operational.cargo_docs_onboard)
        : undefined,
    },
    operatingConditions: {
      operationType: {
        code: report.cargo?.operation_type,
        name: report.cargo?.operation_type,
      },
      cargoLocation: {
        code: report.cargo?.location,
        name: report.cargo?.location,
      },
      tugsAllFast: report.operational.tugs_all_fast,
      tugsLastLine: report.operational.tugs_last_line,
      // V1 holds the all-fast draughts and trim on a Port report, and the
      // plain draughts and trim on every other report.
      draughtAft: isPortReport
        ? report.operational.all_fast_draught_aft
        : report.operational.draught_aft,
      draughtForward: isPortReport
        ? report.operational.all_fast_draught_forward
        : report.operational.draught_forward,
      trim: isPortReport
        ? report.operational.all_fast_trim
        : report.operational.trim,
      recommendedTrim: report.operational.recommended_trim,
      ballastWaterOnBoard: report.operational.ballast_water,
      ballastWaterIntake: report.operational.ballast_water_intake,
      ballastWaterDischarged: report.operational.ballast_water_discharged,
      ballastWaterSampleDate: convertToDate(
        report.operational.ballast_water_sample_date,
      ),
      ballastWaterCountOfCells: report.operational.ballast_water_count_of_cells,
    },
    nextPort: {
      awaitingOrders: report.operational.awaiting_orders,
      nextPort: report.operational.next_port,
      remainingDistance: report.operational.remaining_distance,
      remainingTime: report.operational.remaining_time,
      nextTimezone: report.operational.destination_timezone,
      etaPilotStation: convertToDate(report.operational.eta_pilot_station),
    },
    otherDetails: {
      numPassengers: report.operational.number_of_passengers,
      numCrew: report.operational.number_of_crew,
    },
    voyageItinerary: {
      maneuveringType: convertManeuveringType(
        report.operational.maneuvering_type,
      ),
      awaitingOrders: report.operational.awaiting_orders,
      arrivingAtEosp: report.operational.arriving,
      nextPort: report.operational.next_port,
      eta: convertToDate(report.operational.eta),
      etaTimezone: report.operational.eta_timezone,
      previousEta: convertToDate(report.operational.previous_eta),
      cosp: convertToDate(report.operational.cosp),
      previouslyInEca: report.operational.previously_in_eca,
      enterEca: convertToDate(report.operational.enter_eca),
      exitEca: convertToDate(report.operational.exit_eca),
      timeInEca: report.operational.time_in_eca,
      inEca: report.operational.in_eca,
    },
    portCallReasons: {
      purposesOfCall: report.operational.purposes_of_call,
    },
  };
};

const convertPositionAndWeatherSection = (
  report: TvaApiReport,
  isSavedReport = false,
): PositionAndWeatherState => {
  return {
    observation: {
      observationTime: isSavedReport
        ? convertToDate(report?.position?.observation)
        : new Date(),
      // V1 shows the saved observation timezone, or else the previous
      // timezone of the report (position-observation.html:35). A new report
      // takes the timezone of the previous report, as V1 copies it.
      observationTimezone: isSavedReport
        ? (report?.position?.observation_timezone ??
          report.operational.previous_timezone)
        : report.operational.timezone,
    },
    wind: {
      apparentWindSpeed: report?.position?.relative_wind_speed,
      apparentWindDirection: report?.position?.relative_wind_direction,
      trueWindReporting: report?.position?.true_wind_reporting,
      trueWindSpeed: report?.position?.true_wind_speed,
      trueWindDirection: report?.position?.true_wind_direction,
      trueWindForce: report?.position?.true_wind_force,
      trueWindDirectionTrueNorth: report?.position?.true_wind_effect,
    },
    speedAndDistance: {
      tripCounterGps: report?.position?.trip_counter_gps,
      tripCounterLog: report?.position?.trip_counter_log,
      speedLogMalfunction: report?.position?.speed_log_malfunction,

      logSpeed: report?.position?.log_speed,
      logDistance: report?.position?.log_distance,
      courseOverGround: report?.position?.cog,
      observedSpeed: report?.position?.observed_speed,
      observedDistance: report?.position?.observed_distance,
      observedDistanceInEca: report?.position?.observed_distance_in_eca,
      observedDistanceTotalSeaPassage:
        report?.position?.observed_distance_for_total_sea_passage,
      remainingDistance: report?.position?.remaining_distance,
      badWeatherHours: report?.position?.bad_weather_hours,
      badWeatherObservedDistance:
        report?.position?.bad_weather_observed_distance,
      propellerRpm: report?.position?.propeller_rpm,
      propellerPitchPercent: report?.position?.propeller_pitch_percent,
    },
    shipPosition: {
      position: {
        latDegrees: report?.position?.ship_lat_hours,
        latMinutes: report?.position?.ship_lat_minutes,
        latSeconds: report?.position?.ship_lat_seconds,
        latDirection: {
          code: report?.position?.ship_lat_direction,
          name: report?.position?.ship_lat_direction,
        },
        longDegrees: report?.position?.ship_lon_hours,
        longMinutes: report?.position?.ship_lon_minutes,
        longSeconds: report?.position?.ship_lon_seconds,
        longDirection: {
          code: report?.position?.ship_lon_direction,
          name: report?.position?.ship_lon_direction,
        },
      },
    },
    currentsAndSeaWater: {
      seaSwell: report?.position?.swell,
      seaSwellDirection: report?.position?.swell_direction,
      waveHeight: report?.position?.wave_height,
      waterDepth: report?.position?.water_depth,
      currentDirectionTrueNorth: report?.position?.true_current_effect,
      trueCurrentSpeed: report?.position?.current_speed,
      apparentCurrentDirection: report?.position?.current_direction,
      apparentCurrentSpeed: report?.position?.relative_current_speed,
      apparentWaveDirection: report?.position?.wave_direction,
      seaTemperature: report?.position?.sea_temperature,
      seaWaterDensity: report?.position?.sea_water_density,
      atmosphericPressure: report?.position?.atmospheric_pressure,
      salinity: report?.position?.salinity,
    },
    propellers: {
      propellerPitches: report?.position?.propeller_pitches?.map((pitch) => ({
        pitch,
      })),
    },
  };
};

// V1 and the TVA-API power model hold aux engines 1 to 10.
const MAX_AUX_ENGINES = 10;
// V1 holds up to 10 cargo compressors and 10 BOG compressors
// (reports.service.ts:808-827 in ../TVA-Reporting).
const MAX_CARGO_COMPRESSORS = 10;
const MAX_BOG_COMPRESSORS = 10;
const MAX_CARGO_PUMPS = 8;
const MAX_BALLAST_PUMPS = 2;

type RhEntryConfig = {
  key: string;
  label: string;
  base: string;
  currentToken?: 'cur' | 'curr';
  isFirstBaseIndexBlank?: boolean;
  keepUnindexed?: boolean;
};

const buildRhEntry = (
  power: TvaApiPower,
  config: RhEntryConfig,
): MachineryRhState => {
  const currentToken = config.currentToken ?? 'cur';

  return {
    key: config.key,
    label: config.label,
    current: power[`${config.base}_${currentToken}_rh`] as number,
    previous: power[`${config.base}_prev_rh`] as number,
    difference: power[`${config.base}_diff_rh`] as number,
  };
};

const buildRhEntries = (
  power: TvaApiPower,
  count: number,
  {
    key,
    label,
    base,
    currentToken,
    isFirstBaseIndexBlank,
    keepUnindexed,
  }: RhEntryConfig,
): MachineryRhState[] =>
  Array.from({ length: count }, (_, i) => {
    const index = i + 1;
    const keySuffix = keepUnindexed ? '' : `${index}`;
    const labelSuffix = keepUnindexed ? '' : ` ${index}`;
    const baseSuffix =
      keepUnindexed || (isFirstBaseIndexBlank && index === 1)
        ? ''
        : `_${index}`;

    return buildRhEntry(power, {
      key: `${key}${keySuffix}`,
      label: `${label}${labelSuffix}`,
      base: `${base}${baseSuffix}`,
      currentToken,
    });
  });

// TODO: #132614 Add conversion for diesel_gen_N_cur_rh/_prev_rh/_diff_rh ("MDG") running hours
// rows for Diesel-Electric Propulsion vessels, driven by vessel_specifications.prime_mover.diesel_generators.
const convertMachineryRunningHours = (
  power: TvaApiPower,
  vesselSpec: VesselSpec | undefined,
) => {
  const CONFIG_RH_ME: RhEntryConfig = {
    key: 'mainEngine',
    label: 'Main Engine',
    base: 'main_engine',
  };
  const CONFIG_RH_AE: RhEntryConfig = {
    key: 'auxEngine',
    label: 'Auxiliary Engine',
    base: 'aux_engine',
  };
  const CONFIG_RH_AB: RhEntryConfig = {
    key: 'auxBoiler',
    label: 'Auxiliary Boiler',
    base: 'aux_boiler',
    isFirstBaseIndexBlank: true,
  };
  const CONFIG_SHAFT_GEN_PTO: RhEntryConfig = {
    key: 'shaftGeneratorPTO',
    label: 'Shaft Generator (PTO Mode)',
    base: 'shaft_generator_pto',
    currentToken: 'curr',
    keepUnindexed: true,
  };
  const CONFIG_RH_ME_COMP: RhEntryConfig = {
    key: 'mainAirComp',
    label: 'Main Air Comp.',
    base: 'main_engine_comp',
  };
  const MAIN_ENGINE_COMP_COUNT = 4;
  const SHAFT_GEN_PTO_COUNT = 1;

  const auxEngineCount = vesselSpec?.engine.number_of_aux_engines ?? 0;
  const mainEngineCount = vesselSpec?.engine.number_of_main_engines ?? 0;
  const auxBoilerCount = vesselSpec?.engine.number_of_aux_boilers ?? 0;

  const mainEngineRh = buildRhEntries(power, mainEngineCount, CONFIG_RH_ME);

  const auxEngineRh = buildRhEntries(power, auxEngineCount, CONFIG_RH_AE);

  // The 4 rows always exist, so a vessel with no saved reading can type a
  // first one. The form rule template decides whether each one shows.
  const mainEngineCompRh = buildRhEntries(
    power,
    MAIN_ENGINE_COMP_COUNT,
    CONFIG_RH_ME_COMP,
  );

  const auxBoilerRh = buildRhEntries(power, auxBoilerCount, CONFIG_RH_AB);

  // Each row from here always exists. The form rule template decides whether
  // it shows.
  const single = (key: string, label: string, base: string) =>
    buildRhEntries(power, 1, { key, label, base, keepUnindexed: true });

  const shaftGeneratorPtoRh = buildRhEntries(
    power,
    SHAFT_GEN_PTO_COUNT,
    CONFIG_SHAFT_GEN_PTO,
  );

  return [
    ...mainEngineRh,
    ...auxEngineRh,
    ...mainEngineCompRh,
    ...auxBoilerRh,
    ...single('compositeBoiler', 'Composite Boiler', 'composite_boiler'),
    ...buildRhEntries(power, MAX_CARGO_COMPRESSORS, {
      key: 'cargoCompressor',
      label: 'Cargo Compressor',
      base: 'cargo_compressor',
    }),
    ...buildRhEntries(power, MAX_BOG_COMPRESSORS, {
      key: 'bogCompressor',
      label: 'BOG Compressor',
      base: 'bog_compressor',
    }),
    ...buildRhEntries(power, MAX_CARGO_PUMPS, {
      key: 'cargoPump',
      label: 'Cargo Pump',
      base: 'cargo_pump',
    }),
    ...buildRhEntries(power, MAX_BALLAST_PUMPS, {
      key: 'ballastPump',
      label: 'Ballast Pump',
      base: 'ballast_pump',
    }),
    ...shaftGeneratorPtoRh,
    ...single('bowThruster', 'Bow Thruster', 'bow_thruster'),
    ...single('sternThruster', 'Stern Thruster', 'stern_thruster'),
    ...single('scr', 'Selective Catalytic Reduction', 'scr'),
  ];
};
const convertCargoPumpMeters = (power: TvaApiPower): CargoPumpMeterState[] => {
  const entries = power['cargo_pumps_kwh'];
  if (!Array.isArray(entries)) return [];

  return entries.map((entry) => ({
    cur: entry.cur ?? undefined,
    prev: entry.prev ?? undefined,
  }));
};

const convertGeneratorEngines = (power: TvaApiPower) => {
  const generatorEngines: GeneratorEngineState[] = [];
  const hasNonZeroValue = (key: string) => {
    const value = power[key];
    return typeof value === 'number' && value !== 0;
  };

  for (let i = 0; i < MAX_AUX_ENGINES; i++) {
    const baseKey = `aux_engine_${i + 1}_`;
    const enginePowerKey = `${baseKey}power`;
    const maxRatingKey = `${baseKey}max_rating`;
    const runningHoursKey = `${baseKey}cur_rh`;
    const chargeAirPressureKey = `${baseKey}charge_air_pressure`;
    const loAddedKey = `${baseKey}lo`;

    if (
      !hasNonZeroValue(enginePowerKey) &&
      !hasNonZeroValue(maxRatingKey) &&
      !hasNonZeroValue(runningHoursKey) &&
      !hasNonZeroValue(chargeAirPressureKey) &&
      !hasNonZeroValue(loAddedKey)
    ) {
      continue;
    }

    generatorEngines.push({
      key: `auxEngine${i + 1}`,
      label: `Auxiliary Engine ${i + 1}`,
      power: power[enginePowerKey] as number,
      maxRating: power[maxRatingKey] as number,
      runningHours: power[runningHoursKey] as number,
      chargeAirPressure: power[chargeAirPressureKey] as number,
      loAdded: power[loAddedKey] as number,
    });
  }

  const turboKeys = [
    'turbo_power',
    'turbo_maxRating',
    'turbo_rh',
    'turbo_charge_air_pressure',
  ];
  if (turboKeys.some(hasNonZeroValue)) {
    generatorEngines.push({
      key: 'turboGenerator',
      label: 'Turbo Generator',
      power: power['turbo_power'] as number,
      maxRating: power['turbo_maxRating'] as number,
      runningHours: power['turbo_rh'] as number,
      chargeAirPressure: power['turbo_charge_air_pressure'] as number,
    });
  }

  return generatorEngines;
};

const convertEngineModes = (
  modes: Record<string, EngineMode> | EngineMode[] | undefined,
): EngineMode[] | undefined => {
  if (!modes) return;

  const engineModes = Array.isArray(modes) ? modes : Object.values(modes);
  return engineModes.length === 1 &&
    Object.keys(engineModes[0] || {}).length === 0
    ? []
    : engineModes;
};

// Readings for the new "Flow Meter Installation" section live under dynamic,
// vessel-configured flow_path keys on `report.power`. We don't know which keys
// those are here (no vessel spec), so pass through every numeric power value;
// the PowerStore reconciles this down to the vessel's real flow_paths.
const extractFlowMeterInstallationReadings = (
  power: TvaApiPower,
): Record<string, number> => {
  const readings: Record<string, number> = {};
  for (const [key, value] of Object.entries(power)) {
    if (typeof value === 'number') readings[key] = value;
  }
  return readings;
};

const convertPowerSection = (
  report: TvaApiReport,
  vesselSpec: VesselSpec | undefined,
): PowerState => {
  const reportsAveragePowerByHand =
    !!vesselSpec?.information?.manually_report_calculated_average_total_power;
  const averagePower = report.power.main_engine_1_shaft_power_meter_calculated;

  return {
    environmental: {
      incineratorMalfunctional: report.power.incinerator_malfunctional,
      odmeMalfunctional: report.power.odme_malfunctional,
      bwtsMalfunctional: report.power.bwts_malfunctional,
      scrubberMalfunctional: report.power.scrubber_malfunctional,
      owsMalfunctional: report.power.ows_malfunctional,
    },
    shorePower: {
      isUsed: report.power.shore_power_is_used,
      receptionPort: report.power.shore_power_reception_port,
      kwh: report.power.shore_power_kwh,
      hours: report.power.shore_power_hours,
      startTime: convertToDate(report.power.shore_power_start_time),
      endTime: convertToDate(report.power.shore_power_end_time),
      totalKwh: report.power.total_shore_power_kwh,
      wtt: report.power.shore_power_wtt,
      ednNumber: report.power.edn_number,
      ednFiles: report.power.edn_files ?? [],
      sustainableIsUsed: report.power.sustainable_shore_power_is_used,
      proofOfSustainabilityEdnFiles:
        report.power.proof_of_sustainability_edn_files ?? [],
      runningTotalKwh: report.power.running_total_shore_power_kwh,
      ednId: report.power.edn_id,
    },
    mainEngine: {
      power: report.power.main_engine_1_power,
      rpm: report.power.main_engine_1_rpm,
      revCounterCurrent: report.power.main_engine_1_current_rpm,
      revCounterPrevious: report.power.main_engine_1_previous_rpm,
      shaftPowerMeterCounterCurrent:
        report.power.main_engine_1_shaft_power_meter_counter,
      shaftPowerMeterCounterPrevious:
        report.power.main_engine_1_shaft_power_meter_counter_previous,
      // V1 has one key for the average power. Read it into one field only,
      // or a value can wait in a field that no control writes, and the save
      // sends it back after the user clears the other field.
      shaftPowerMeterCounterCalculatedManual: reportsAveragePowerByHand
        ? averagePower
        : undefined,
      tcRpm1: report.power.avg_tcrpm_1,
      tcRpm2: report.power.avg_tcrpm_2,
      tcRpmPower: report.power.tcrpm_power,
      engineModes: convertEngineModes(report.power.me_operating_modes),
      mainEngineLoad: report.power.main_engine_1_load,
      mainEngineRevCounterDiff: report.power.main_engine_1_difference_rpm,
      mainEngineCalculatedRpm: report.power.main_engine_1_calculated_rpm,
      mainEngines1And2Power: report.power.main_engine_1_and_2_power,
      mainEngines1And2Load: report.power.main_engine_12_load,
      mainEngines1And2RunningHours: report.power.main_engine_1_and_2_rh,
      shaftPowerMeterCounterDifference:
        report.power.main_engine_1_shaft_power_meter_difference,
      shaftPowerMeterCounterCalculated: reportsAveragePowerByHand
        ? undefined
        : averagePower,
    },
    machineryRh: convertMachineryRunningHours(report.power, vesselSpec),
    flowMeterInstallationOld: {
      foBoosterCurr: report.power.booster_unit_hfo_cur_rh,
      foBoosterPrev: report.power.booster_unit_hfo_prev_rh,
      foBoosterDiff: report.power.booster_unit_hfo_diff_rh,
      gasOilBoosterCurr: report.power.booster_unit_gas_oil_cur_rh,
      gasOilBoosterPrev: report.power.booster_unit_gas_oil_prev_rh,
      gasOilBoosterDiff: report.power.booster_unit_gas_oil_diff_rh,
      meAeInletCurr: report.power.main_engine_flow_inlet_cur_rh,
      meAeInletPrev: report.power.main_engine_flow_inlet_prev_rh,
      meAeInletDiff: report.power.main_engine_flow_inlet_diff_rh,
      auxEngineInletCurr: report.power.aux_engine_flow_inlet_cur_rh,
      auxEngineInletPrev: report.power.aux_engine_flow_inlet_prev_rh,
      auxEngineInletDiff: report.power.aux_engine_flow_inlet_diff_rh,
      auxEngineOutletCurr: report.power.aux_engine_flow_outlet_cur_rh,
      auxEngineOutletPrev: report.power.aux_engine_flow_outlet_prev_rh,
      auxEngineOutletDiff: report.power.aux_engine_flow_outlet_diff_rh,
      auxBoiler1Curr: report.power.aux_boiler_flow_cur_rh,
      auxBoiler1Prev: report.power.aux_boiler_flow_prev_rh,
      auxBoiler1Diff: report.power.aux_boiler_flow_diff_rh,
      cylinderLubeOilCurr: report.power.cylinder_lube_oil_cur,
      cylinderLubeOilPrev: report.power.cylinder_lube_oil_prev,
      cylinderLubeOilDiff: report.power.cylinder_lube_oil_diff,
    },
    flowMeterInstallation: extractFlowMeterInstallationReadings(report.power),
    pumps: {
      reportPumpSection: report.power.reporting_pumps,
      meLoPump1Curr: report.power.me_lo_pump_1_cur_rh,
      meLoPump1Prev: report.power.me_lo_pump_1_prev_rh,
      meLoPump1Diff: report.power.me_lo_pump_1_diff_rh,
      meLoPump2Curr: report.power.me_lo_pump_2_cur_rh,
      meLoPump2Prev: report.power.me_lo_pump_2_prev_rh,
      meLoPump2Diff: report.power.me_lo_pump_2_diff_rh,
      seaWaterPump1Curr: report.power.sea_water_pump_1_cur_rh,
      seaWaterPump1Prev: report.power.sea_water_pump_1_prev_rh,
      seaWaterPump1Diff: report.power.sea_water_pump_1_diff_rh,
      seaWaterPump2Curr: report.power.sea_water_pump_2_cur_rh,
      seaWaterPump2Prev: report.power.sea_water_pump_2_prev_rh,
      seaWaterPump2Diff: report.power.sea_water_pump_2_diff_rh,
      sgPump1Curr: report.power.sg_pump_1_cur_rh,
      sgPump1Prev: report.power.sg_pump_1_prev_rh,
      sgPump1Diff: report.power.sg_pump_1_diff_rh,
      sgPump2Curr: report.power.sg_pump_2_cur_rh,
      sgPump2Prev: report.power.sg_pump_2_prev_rh,
      sgPump2Diff: report.power.sg_pump_2_diff_rh,
      foPurifier1Curr: report.power.fo_purifier_1_cur_rh,
      foPurifier1Prev: report.power.fo_purifier_1_prev_rh,
      foPurifier1Diff: report.power.fo_purifier_1_diff_rh,
      foPurifier2Curr: report.power.fo_purifier_2_cur_rh,
      foPurifier2Prev: report.power.fo_purifier_2_prev_rh,
      foPurifier2Diff: report.power.fo_purifier_2_diff_rh,
      erFan1Curr: report.power.er_fan_1_cur_rh,
      erFan1Prev: report.power.er_fan_1_prev_rh,
      erFan1Diff: report.power.er_fan_1_diff_rh,
      erFan2Curr: report.power.er_fan_2_cur_rh,
      erFan2Prev: report.power.er_fan_2_prev_rh,
      erFan2Diff: report.power.er_fan_2_diff_rh,
      erFan3Curr: report.power.er_fan_3_cur_rh,
      erFan3Prev: report.power.er_fan_3_prev_rh,
      erFan3Diff: report.power.er_fan_3_diff_rh,
      erFan4Curr: report.power.er_fan_4_cur_rh,
      erFan4Prev: report.power.er_fan_4_prev_rh,
      erFan4Diff: report.power.er_fan_4_diff_rh,
    },
    generatorEngines: convertGeneratorEngines(report.power),
    cargoPumpsKwh: convertCargoPumpMeters(report.power),
    scrubber: {
      inUse: report.power.scrubber_in_use,
      mode: (report.power['scrubber_mode'] as unknown as string) ?? '',
      runningHours: (report.power['scrubber_rh'] as number) ?? 0,
    },
    shaftGeneratorPTO: {
      key: 'shaftGeneratorPTO',
      power: report.power.shaft_generator_pto_power,
      maxRating: report.power.shaft_generator_pto_max_rating,
      runningHours: report.power.shaft_generator_pto_rh,
    },
  };
};

const convertToConsumptionData = (report: TvaApiReport, key: string) => {
  if (!key) return;
  if (key === 'cargo_heating_logs') return;

  const consumption = report.consumption;
  if (!consumption) return;

  const result = Object.keys(consumption).reduce((acc, cur) => {
    if (!cur.startsWith(key)) return acc;

    acc.hshfo = consumption[`${key}_hshfo`] as number;
    acc.lshfo = consumption[`${key}_lshfo`] as number;
    acc.ulsfo = consumption[`${key}_ulsfo`] as number;
    acc.hsmdo = consumption[`${key}_hsmdo`] as number;
    acc.lsmdo = consumption[`${key}_lsmdo`] as number;
    acc.hsmgo = consumption[`${key}_hsmgo`] as number;
    acc.lsmgo = consumption[`${key}_lsmgo`] as number;
    acc.propane = consumption[`${key}_propane`] as number;
    acc.butane = consumption[`${key}_butane`] as number;
    acc.lng = consumption[`${key}_lng`] as number;
    acc.methanol = consumption[`${key}_methanol`] as number;
    acc.ethanol = consumption[`${key}_ethanol`] as number;
    acc.other = consumption[`${key}_other`] as number;
    acc.hfo = consumption[`${key}_hfo`] as number;
    acc.lfo = consumption[`${key}_lfo`] as number;
    acc.mgo = consumption[`${key}_mgo`] as number;
    acc.mdo = consumption[`${key}_mdo`] as number;
    acc.b10lfo = consumption[`${key}_b10lfo`] as number;
    acc.b10mgo = consumption[`${key}_b10mgo`] as number;
    acc.biolfo = consumption[`${key}_biolfo`] as number;
    acc.biomgo = consumption[`${key}_biomgo`] as number;
    acc.ulsfo2020 = consumption[`${key}_ulsfo2020`] as number;
    acc.ulslfo2020 = consumption[`${key}_ulslfo2020`] as number;
    acc.ulsmdo2020 = consumption[`${key}_ulsmdo2020`] as number;
    acc.ulsmgo2020 = consumption[`${key}_ulsmgo2020`] as number;
    acc.vlsfo2020 = consumption[`${key}_vlsfo2020`] as number;
    acc.vlslfo2020 = consumption[`${key}_vlslfo2020`] as number;
    acc.lpgp = consumption[`${key}_lpgp`] as number;
    acc.lpgb = consumption[`${key}_lpgb`] as number;
    acc.hvo = consumption[`${key}_hvo`] as number;
    acc.fame = consumption[`${key}_fame`] as number;
    acc.biodiesel = consumption[`${key}_biodiesel`] as number;
    acc.bio_lng = consumption[`${key}_bio_lng`] as number;

    return acc;
  }, {} as ConsumptionData);

  if (Object.keys(result).length === 0) return;
  if (Object.values(result).some((val) => val != null)) return result;
  return;
};

const generateFuelSpec = (
  state: ConsumptionsState,
  i: number,
): FuelSpecState => {
  const fuelType = state[`fuelType${i + 1}`] as string;
  if (fuelType) {
    return {
      fuelType: {
        code: fuelType,
        name: fuelType?.toLocaleUpperCase(),
      },
      lcv: state.lcv ? state.lcv[fuelType] : undefined,
      sulphur: state.sulphur ? state.sulphur[fuelType] : undefined,
      density: state.density ? state.density[fuelType] : undefined,
      fuelTemp: state.serviceTankTemp
        ? state.serviceTankTemp[fuelType]
        : undefined,
      viscosity: state.viscosity ? state.viscosity[fuelType] : undefined,
    };
  }

  return {
    fuelType: undefined,
    lcv: undefined,
    sulphur: undefined,
    density: undefined,
    fuelTemp: undefined,
    viscosity: undefined,
  };
};

const generateFuelSpecs = (state: ConsumptionsState) => {
  for (let i = 0; i < 3; i++) {
    state.fuelSpecs.push(generateFuelSpec(state, i));
  }

  return state;
};

const convertConsumptionSection = (report: TvaApiReport): ConsumptionsState => {
  const consumptionState: ConsumptionsState = {
    fuelType1: report.consumption.fuel_type_1,
    fuelType2: report.consumption.fuel_type_2,
    fuelType3: report.consumption.fuel_type_3,
    lcv: convertToConsumptionData(report, 'lcv'),
    sulphur: convertToConsumptionData(report, 'sulphur'),
    density: convertToConsumptionData(report, 'density'),
    viscosity: convertToConsumptionData(report, 'viscosity'),
    previousLcv: convertToConsumptionData(report, 'previous_lcv'),
    previousSulphur: convertToConsumptionData(report, 'previous_sulphur'),
    previousDensity: convertToConsumptionData(report, 'previous_density'),
    serviceTankTemp: convertToConsumptionData(report, 'serv'),
    mainEngine: convertToConsumptionData(report, 'main_engine'),
    mdgPort: convertToConsumptionData(report, 'mdg_port'),
    mdgStarboard: convertToConsumptionData(report, 'mdg_starboard'),
    hdg: convertToConsumptionData(report, 'hdg'),
    dg: convertToConsumptionData(report, 'dg'),
    auxEngine: convertToConsumptionData(report, 'aux_engine'),
    auxBoiler: convertToConsumptionData(report, 'aux_boiler'),
    incinerator: convertToConsumptionData(report, 'incinerator'),
    gasCombustionUnits: convertToConsumptionData(
      report,
      'gas_combustion_units',
    ),
    dpp: convertToConsumptionData(report, 'dpp'),
    other: convertToConsumptionData(report, 'other'),
    cargoRelated: convertToConsumptionData(report, 'cargo_related'),
    totalConsumption: convertToConsumptionData(report, 'total_consumption'),
    igGenerator: convertToConsumptionData(report, 'ig_generator'),
    gasTurbines: convertToConsumptionData(report, 'gas_turbines'),
    igBoilerFlueGas: convertToConsumptionData(report, 'ig_boiler_flue_gas'),
    otherAuxEngines: convertToConsumptionData(report, 'other_aux_engines'),
    tankCleaning: convertToConsumptionData(report, 'tank_cleaning'),
    cargoHeating: convertToConsumptionData(report, 'cargo_heating'),
    ballastOperations: convertToConsumptionData(report, 'ballast_operations'),
    cargoShifting: convertToConsumptionData(report, 'cargo_shifting'),
    propCons: convertToConsumptionData(report, 'prop_cons'),
    cSlipLng: 0,
    aeBasicLoad: convertToConsumptionData(report, 'ae_basic_load'),
    aeBallastOperations: convertToConsumptionData(
      report,
      'ae_ballast_operations',
    ),
    aeCraneOperations: convertToConsumptionData(report, 'ae_crane_operations'),
    aeCdlOperations: convertToConsumptionData(report, 'ae_cdl_operations'),
    aeCargoHeating: convertToConsumptionData(report, 'ae_cargo_heating'),
    aeCargoCooling: convertToConsumptionData(report, 'ae_cargo_cooling'),
    aeTankCleaning: convertToConsumptionData(report, 'ae_tank_cleaning'),
    aeCargoShifting: convertToConsumptionData(report, 'ae_cargo_shifting'),
    aeTankCooling: convertToConsumptionData(report, 'ae_tank_cooling'),
    aeInertGasSystems: convertToConsumptionData(report, 'ae_inert_gas_systems'),
    aeCargoHoldWashing: convertToConsumptionData(
      report,
      'ae_cargo_hold_washing',
    ),
    aeScrubber: convertToConsumptionData(report, 'ae_scrubber'),
    aeBunkerHeating: convertToConsumptionData(report, 'ae_bunker_heating'),
    aeTankPressureControl: convertToConsumptionData(
      report,
      'ae_tank_pressure_control',
    ),
    dgBasicLoad: convertToConsumptionData(report, 'dg_basic_load'),
    dgBallastOperations: convertToConsumptionData(
      report,
      'dg_ballast_operations',
    ),
    dgCargoHeating: convertToConsumptionData(report, 'dg_cargo_heating'),
    dgTankCleaning: convertToConsumptionData(report, 'dg_tank_cleaning'),
    dgCargoShifting: convertToConsumptionData(report, 'dg_cargo_shifting'),
    dgInertGasSystems: convertToConsumptionData(report, 'dg_inert_gas_systems'),
    abBasicLoad: convertToConsumptionData(report, 'ab_basic_load'),
    abBallastOperations: convertToConsumptionData(
      report,
      'ab_ballast_operations',
    ),
    abCargoHeating: convertToConsumptionData(report, 'ab_cargo_heating'),
    abBunkerHeating: convertToConsumptionData(report, 'ab_bunker_heating'),
    abTankCleaning: convertToConsumptionData(report, 'ab_tank_cleaning'),
    abCargoShifting: convertToConsumptionData(report, 'ab_cargo_shifting'),
    abInertGasSystems: convertToConsumptionData(report, 'ab_inert_gas_systems'),
    abTankPressureControl: convertToConsumptionData(
      report,
      'ab_tank_pressure_control',
    ),
    inertGasSystems: [
      {
        from: report.consumption.system_run_1_from
          ? convertToDate(report.consumption.system_run_1_from)
          : undefined,
        to: report.consumption.system_run_1_to
          ? convertToDate(report.consumption.system_run_1_to)
          : undefined,
      },
      {
        from: report.consumption.system_run_2_from
          ? convertToDate(report.consumption.system_run_2_from)
          : undefined,
        to: report.consumption.system_run_2_to
          ? convertToDate(report.consumption.system_run_2_to)
          : undefined,
      },
      {
        from: report.consumption.system_run_3_from
          ? convertToDate(report.consumption.system_run_3_from)
          : undefined,
        to: report.consumption.system_run_3_to
          ? convertToDate(report.consumption.system_run_3_to)
          : undefined,
      },
      {
        from: report.consumption.system_run_4_from
          ? convertToDate(report.consumption.system_run_4_from)
          : undefined,
        to: report.consumption.system_run_4_to
          ? convertToDate(report.consumption.system_run_4_to)
          : undefined,
      },
    ],
    cylinderOilConsumption: {
      oilCode: {
        code: parseInt(report.consumption.cyl_oil_code),
        name: CLO_LUBRICATION_CODES[parseInt(report.consumption.cyl_oil_code)],
      },
      oilBn: report.consumption.cyl_oil_bn,
      oilDensityAt15: report.consumption.cyl_oil_density_at_15,
      oilTankTemperature: report.consumption.cyl_oil_tank_temperature,
      oilConsumption: report.consumption.cyl_oil_consumption,
      oilKgConsumption: report.consumption.cyl_oil_kg_consumption,
      oilSloc: report.consumption.cyl_oil_sloc,
    },
    totals: {
      fuel: report.consumption.total_fuel_consumption,
      basicLoadAe: report.consumption.total_basic_load_ae,
      basicLoadDg: report.consumption.total_basic_load_dg,
      prop: report.consumption.total_prop_cons,
      basicLoadAb: report.consumption.total_basic_load_ab,
      mainEngine: report.consumption.total_me_consumption,
      auxEngine: report.consumption.total_ae_consumption,
      mdgPort: report.consumption.total_mdg_port_consumption,
      mdgStarboard: report.consumption.total_mdg_starboard_consumption,
      hdg: report.consumption.total_hdg_consumption,
      dg: report.consumption.total_dg_consumption,
      boiler: report.consumption.total_boiler_consumption,
      other: report.consumption.total_other_cons,
      aeCargoCooling: report.consumption.total_ae_cargo_cooling_cons,
      aeCargoHeating: report.consumption.total_ae_cargo_heating_cons,
      aeCargoShifting: report.consumption.total_ae_cargo_shifting_cons,
      abCargoHeating: report.consumption.total_ab_cargo_heating_cons,
      abCargoShifting: report.consumption.total_ab_cargo_shifting_cons,
      me24: report.consumption.total_me_consumption_24,
      ae24: report.consumption.total_ae_consumption_24,
      dg24: report.consumption.total_dg_consumption_24,
      prop24: report.consumption.total_prop_consumption_24,
      cii24: report.consumption.total_cii_consumption_24,
    },
    fuelSpecs: [],
    fwgNonFunctional: report.consumption.fwg_non_operational,
    freshWaterProduced: report.consumption.fresh_water,
    distilledWaterProduced: report.consumption.distilled_water,
  };
  return generateFuelSpecs(consumptionState);
};

// The API stores cargo grade as its code; resolve the full SelectOption (with
// the proper display name) so the cargo type select shows the saved value.
const resolveCargoGrade = (
  value: string | number | undefined,
): SelectOption => {
  const match = CARGO_GRADE_OPTIONS.find(
    (option) => String(option.code) === String(value),
  );
  return (
    match ?? { code: value as string | number, name: value?.toString() ?? '' }
  );
};

const convertCargoes = (cargoDetails: TvaApiCargoDetails) => {
  if (!cargoDetails) return [];

  const cargoes: CargoState[] = [];
  for (let i = 0; i < cargoDetails.n_cargoes; i++) {
    const cargo: CargoState = {
      blCode: cargoDetails.bl_code[i],
      blDischarge: convertToDate(cargoDetails.bl_discharge[i]),
      blIssue: convertToDate(cargoDetails.bl_issue[i]),
      blQuantity: cargoDetails.bl_quantity[i],
      cargoGrade: resolveCargoGrade(cargoDetails.cargo_grade[i]),
      charterer: cargoDetails.charterer[i],
      consignee: cargoDetails.consignee[i],
      density: cargoDetails.density[i],
      destinationPort: cargoDetails.destination_port[i],
      flashPoint: cargoDetails.flash_point[i],
      loadRate: cargoDetails.load_rate[i],
      loadTemp: cargoDetails.load_temp[i],
      pourPoint: cargoDetails.pour_point[i],
      s2sShipName: cargoDetails.s2s_ship_name[i],
      shipper: cargoDetails.shipper[i],
      surveyorsCargoQuantity: cargoDetails.surveyors_cargo_quantity[i],
    };
    cargoes.push(cargo);
  }
  return cargoes;
};

const convertCargoDetailsSection = (
  report: TvaApiReport,
): CargoDetailsState => {
  const defaultNCargoes = 1;
  return {
    terminal: {
      name: report.cargo?.terminal,
    },
    stowageDetails: {
      numberOfCargoes: report.cargo?.n_cargoes ?? defaultNCargoes,
      cargoes: convertCargoes(report.cargo),
    },
    otherDetails: {
      hoseDiameter: report.cargo?.hose_diameter,
      protestsReceived: {
        code: report.cargo?.protests_received,
        name: report.cargo?.protests_received?.toString(),
      },
      protestsIssued: {
        code: report.cargo?.protests_issued,
        name: report.cargo?.protests_issued?.toString(),
      },
      seaTemperature: report.cargo?.sea_temperature,
      airTemperature: report.cargo?.air_temperature,
      protestsReceivedComments: report.cargo?.protests_received_comments,
      protestsIssuedComments: report.cargo?.protests_issued_comments,
    },
    hoses: {
      operationType: {
        code: report.cargo?.hoses?.operation_type,
        name: report.cargo?.hoses?.operation_type?.toString(),
      },
      terminal: report.cargo?.hoses?.terminal,
      description: {
        code: report.cargo?.hoses?.description,
        name: report.cargo?.hoses?.description?.toString(),
      },
      hosesOn: convertHosesOn(report.cargo?.hoses?.hose_on_operations),
      hosesOff: convertHosesOff(report.cargo?.hoses?.hose_off_operations),
      heating: convertHosesHeating(report.cargo?.hoses?.heating_operations),
    },
  };
};

const convertHosesOn = (hosesOn: HoseOperation[]): HoseOperationState[] => {
  if (!hosesOn) return [];
  return hosesOn.map((hose) => ({
    operationTimestamp: convertToDate(hose.timestamp) ?? undefined,
    ifo: hose.ifo,
    lsf: hose.lsf,
    lsg: hose.lsg,
    mgoOn: hose.mgo,
  }));
};

const convertHosesOff = (hosesOff: HoseOperation[]): HoseOperationState[] => {
  if (!hosesOff) return [];
  return hosesOff.map((hose) => ({
    operationTimestamp: convertToDate(hose.timestamp) ?? new Date(),
    ifo: hose.ifo,
    lsf: hose.lsf,
    lsg: hose.lsg,
    mgoOn: hose.mgo,
  }));
};

const convertHosesHeating = (hosesHeating: HeatingOperation[]) => {
  if (!hosesHeating) return [];
  return hosesHeating.map((hose) => ({
    comment: hose.comment,
    ifo: hose.ifo,
    mgo: hose.mgo,
  }));
};

const convertBunkerSectionSavedReport = (report: TvaApiReport): BunkerState => {
  return {
    options: {
      tookFuelOil: report?.bunker?.took_fuel_oil,
      tookLubeOil: report?.bunker?.took_lube_oil,
      debunkered: report?.bunker?.debunkered,
    },
    fuelOilBunkers: {
      startedBunkering: convertToDate(
        report.bunker?.fuel_started_bunkering_date,
      ),
      finishedBunkering: convertToDate(report.bunker?.fuel_bunkering_date),
      bargeAlongside: convertToDate(report.bunker?.barge_alongside),
      bargeAway: convertToDate(report.bunker?.barge_away),
    },
    fuelOilBunkerTable: {
      bunkerRows: createBunkerRows(report.bunker),
      quantityOrderedFreshWater: report.bunker?.quantity_ordered_fresh_water,
      quantityReceivedFreshWater: report.bunker?.quantity_received_fresh_water,
      bdnFiguresFreshWater: report.bunker?.bdn_figures_fresh_water,
      quantityOrderedDistilledWater:
        report.bunker?.quantity_ordered_distilled_water,
      quantityReceivedDistilledWater:
        report.bunker?.quantity_received_distilled_water,
      bdnFiguresDistilledWater: report.bunker?.bdn_figures_distilled_water,
      protestIssued: report.bunker?.fuel_lop,
    },
    tankStockTable: {
      tanks: generateTankStockArray(report.stock?.tanks),
    },
    debunkered: convertDebunkered(report.bunker),
    lubeOilBunkers: convertLubeOilBunkers(report.bunker),
  };
};

const convertBunkerSection = (report: TvaApiReport): BunkerState => {
  return {
    options: {
      tookFuelOil: false,
      tookLubeOil: false,
      debunkered: false,
    },
    fuelOilBunkers: {
      startedBunkering: convertToDate(report.operational.report_to),
      finishedBunkering: convertToDate(report.operational.report_to),
      bargeAlongside: undefined,
      bargeAway: undefined,
    },
    fuelOilBunkerTable: {
      bunkerRows: createBunkerRows(undefined),
    },
    tankStockTable: {
      tanks: generateTankStockArray(report.stock?.tanks),
    },
    debunkered: {},
    lubeOilBunkers: {},
  };
};

const generateTankStockArray = (
  tanksRecord?: Record<string, TankStock>,
): StockTanks => {
  if (!tanksRecord) return {};
  const result: StockTanks = {};

  Object.keys(tanksRecord).forEach((tankId) => {
    const tankStock = tanksRecord[tankId];
    result[tankId] = {
      _id: tankStock._id,
      amount: tankStock.amount,
      amountBeforeBunkering: tankStock.amount_before_bunkering,
      bdnNumber: tankStock.bdn_number,
      bunkerDate: tankStock.bunker_date
        ? convertToDate(tankStock.bunker_date)
        : undefined,
      bunkerPort: tankStock.bunker_port,
      density: tankStock.density,
      densityBeforeBunkering: tankStock.density_before_bunkering,
      fuelClass: tankStock.fuel_class,
      fuelGrade: {
        name: tankStock.fuel_grade?.toLocaleUpperCase(),
        code: tankStock.fuel_grade,
      },
      fuelGradeAddedInTank: {
        name: tankStock.fuel_grade_added_in_tank?.toLocaleUpperCase(),
        code: tankStock.fuel_grade_added_in_tank,
      },
      inUse: tankStock.in_use,
      isBlend: tankStock.is_blend,
      lcv: tankStock.lcv,
      lcvBeforeBunkering: tankStock.lcv_before_bunkering,
      stableUntil: tankStock.stable_until
        ? convertToDate(tankStock.stable_until)
        : undefined,
      sulphurBeforeBunkering: tankStock.sulphur_before_bunkering,
      sulphurAfterBunkering: tankStock.sulphur_after_bunkering,
      quantityAddedInTank: tankStock.quantity_added_in_tank,
      viscosity: tankStock.viscosity,
    };
  });
  return result;
};

const getBunkerField = (
  bunker: TvaApiBunker | undefined,
  prefix: string,
  fuelType: string | undefined,
) => {
  if (!bunker || !fuelType) return;

  return bunker[`${prefix}_${fuelType}`];
};

const getBunkerNumberField = (
  bunker: TvaApiBunker | undefined,
  prefix: string,
  fuelType: string | undefined,
) => {
  const value = getBunkerField(bunker, prefix, fuelType);
  return typeof value === 'number' ? value : undefined;
};

const getBunkerStringField = (
  bunker: TvaApiBunker | undefined,
  prefix: string,
  fuelType: string | undefined,
) => {
  const value = getBunkerField(bunker, prefix, fuelType);
  return value == null ? undefined : String(value);
};

const getBunkerDateField = (
  bunker: TvaApiBunker | undefined,
  prefix: string,
  fuelType: string | undefined,
) => {
  const value = getBunkerField(bunker, prefix, fuelType);
  return value ? convertToDate(value as DateObject) : undefined;
};

const createBunkerRows = (
  bunker?: TvaApiBunker | undefined,
): FuelOilBunkerTableState['bunkerRows'] => {
  const hasBunker = !!bunker;
  const fuelGrades = bunker?.bunkered_fuel_grades || [];
  // Always render at least 3 rows: entered fuels fill the top rows and the
  // remainder are left empty for the user to add more (the table is designed
  // around 3 rows). More than 3 bunkered fuels expands to fit them all.
  const BUNKER_ROW_COUNT = Math.max(fuelGrades.length, 3);
  const bunkerRows: FuelOilBunkerTableState['bunkerRows'] = [];

  for (let i = 0; i < BUNKER_ROW_COUNT; i++) {
    const fuelType = fuelGrades[i];
    const quantityOrdered = hasBunker
      ? getBunkerNumberField(bunker, 'quantity_ordered', fuelType)
      : undefined;
    const quantityReceived = hasBunker
      ? getBunkerNumberField(bunker, 'quantity_received', fuelType)
      : undefined;
    const bdnFigures = hasBunker
      ? getBunkerStringField(bunker, 'bdn_figures', fuelType)
      : undefined;
    const density = hasBunker
      ? getBunkerNumberField(bunker, 'density', fuelType)
      : undefined;
    const lcv = hasBunker
      ? getBunkerNumberField(bunker, 'lcv', fuelType)
      : undefined;
    const sulphur = hasBunker
      ? getBunkerNumberField(bunker, 'sulphur_bdn', fuelType)
      : undefined;
    const viscosity = hasBunker
      ? getBunkerNumberField(bunker, 'viscosity', fuelType)
      : undefined;
    const labSulphur = hasBunker
      ? getBunkerNumberField(bunker, 'lab_sulphur', fuelType)
      : undefined;
    const stableUntil = hasBunker
      ? getBunkerDateField(bunker, 'stable_until', fuelType)
      : undefined;

    bunkerRows.push({
      fuelType: hasBunker ? fuelType : undefined,
      isoFuelGrade: hasBunker ? bunker?.bunkered_iso_fuel_grades[i] : undefined,
      quantityOrdered,
      quantityReceived,
      bdnFigures,
      density,
      lcv,
      sulphur,
      viscosity,
      labSulphur,
      stableUntil,
    });
  }

  return bunkerRows;
};

const convertTankTransfers = (report: TvaApiReport) => {
  const tankTransfers = report.stock?.tank_transfers;
  if (!tankTransfers) return [];

  return tankTransfers.map((transfer: TankTransferData) => {
    return {
      from: transfer.from_tank?.$oid,
      to: transfer.to_tank?.$oid,
      quantity: transfer.quantity,
    };
  });
};

const convertTanks = (report: TvaApiReport) => {
  const tanks = report.stock?.tanks;
  if (!tanks) return;

  const tankIds = Object.keys(tanks);
  if (!tankIds?.length) return;

  return convertStockTanks(tanks);
};

const convertStockTanks = (
  tanks: Record<string, TankStock>,
): Record<string, StockTank> => {
  const result: Record<string, StockTank> = {};
  Object.keys(tanks).forEach((tankId) => {
    const tankStock = tanks[tankId];
    result[tankId] = convertTank(tankStock);
  });

  return result;
};

const convertTank = (tank: TankStock): StockTank => {
  return {
    _id: tank._id,
    amount: tank.amount,
    amountBeforeBunkering: tank.amount_before_bunkering,
    bdnNumber: tank.bdn_number,
    bunkerDate: tank.bunker_date ? convertToDate(tank.bunker_date) : undefined,
    bunkerPort: tank.bunker_port,
    density: tank.density,
    densityBeforeBunkering: tank.density_before_bunkering,
    fuelClass: tank.fuel_class,
    fuelGrade: {
      name: tank.fuel_grade?.toLocaleUpperCase(),
      code: tank.fuel_grade,
    },
    fuelGradeAddedInTank: {
      name: tank.fuel_grade_added_in_tank?.toLocaleUpperCase(),
      code: tank.fuel_grade_added_in_tank,
    },
    inUse: tank.in_use,
    isBlend: tank.is_blend,
    lcv: tank.lcv,
    lcvBeforeBunkering: tank.lcv_before_bunkering,
    stableUntil: tank.stable_until
      ? convertToDate(tank.stable_until)
      : undefined,
    sulphurBeforeBunkering: tank.sulphur_before_bunkering,
    sulphurAfterBunkering: tank.sulphur_after_bunkering,
    quantityAddedInTank: tank.quantity_added_in_tank,
    viscosity: tank.viscosity,
  };
};

const convertStockSection = (report: TvaApiReport): StockState => {
  const result = {
    options: {
      tankSounding: report.stock?.tank_sounding,
      tankBasedReporting: report.stock?.tank_based_reporting,
    },
    tanks: convertTanks(report),
    tankTransfers: convertTankTransfers(report),
    fuelWaterRob: {
      tankCleaningChemical: report.stock?.tank_cleaning_chemical,
      bilgeWater: report.stock?.bilge_water,
      sludge: report.stock?.sludge,
      freshWater: report.stock?.fresh_water,
      distilledWater: report.stock?.distilled_water,
      dischargedSludge: report.stock?.discharged_sludge,
      dischargedBilgeWater: report.stock?.discharged_bilge_water,
    },
    lubeOilRob: {
      lubeOilSystem: report.stock?.lube_oil_system,
      lubeOilSpare: report.stock?.lube_oil_spare,
      lowBnCylinderOil: report.stock?.cyl_oil_low_bn,
      highBnCylinderOil: report.stock?.cyl_oil,
      aeLubeOil: report.stock?.aux_engine_lube_oil,
      turboOil: report.stock?.turbo_oil,
      hydraulicOil: report.stock?.hydraulic_oil,
      otherOil: report.stock?.other_oil,
    },
    oilLosses: {
      sludgeRob: report.stock?.sludge,
      totalWasteOil: report.stock?.total_waste_oil,
      incineratedWasteOil: report.stock?.incinerated_waste_oil,
      evaporatedWaterFromWasteOil: report.stock?.evaporated_waste_oil,
      dischargedWasteOil: report.stock?.discharged_waste_oil,
    },
  };
  return result;
};
// Each type writes the class V1 stores it under: the class of its base type.
// The editor never holds the three derived types, because getReportType does
// not derive them, but each one maps to its class all the same. A new
// ReportType member fails the build at REPORT_TYPE_BASE.
const getReportCls = (type: ReportType): string => {
  const cls = REPORT_CLASS_BY_TYPE.get(REPORT_TYPE_BASE[type]);
  if (!cls) throw new Error(`No report class for the type ${type}`);
  return cls;
};

const convertToDateString = (date?: Date): string | undefined => {
  if (!date) return;
  return format(date, DATE_FORMAT);
};

// Select fields are held in the stores as `SelectOption` objects, but the API
// expects the underlying code/string value.
const optionToString = (value: unknown): string => {
  if (value && typeof value === 'object' && 'code' in value) {
    const code = (value as { code: unknown }).code;
    return code == null ? '' : String(code);
  }
  return value == null ? '' : String(value);
};

// The code of a select option, or null when no option is selected. The save
// merges into the saved report, and a key that is left out keeps the saved
// value, so a cleared field must send null to clear it. An empty string is not
// a choice that the model allows.
const optionToCodeOrNull = (value: unknown): string | null =>
  optionToString(value) || null;

const optionToTvaTimezone = (value: unknown): string | undefined => {
  if (value == null) return;

  const code =
    value && typeof value === 'object' && 'code' in value
      ? (value as { code: unknown }).code
      : value;
  return utcOffsetToTvaTimezone(resolveUtcOffset(code));
};

const directionField = (
  key: string,
  value: unknown,
): Record<string, string> => {
  const direction = optionToString(value);
  return direction ? { [key]: direction } : {};
};

const convertOperationalToApi = (
  operational: OperationalState,
  reportType: ReportType,
): TvaApiOperational => {
  const {
    vesselDetails,
    miscellaneous,
    cargoDetails,
    operatingConditions,
    reportTime,
    portAndPosition,
    stabilityConditions,
    portOperationsTimestamp,
    poolSpecificFields,
    nextPort,
    otherDetails,
    voyageItinerary,
    portCallReasons,
  } = operational ?? ({} as OperationalState);

  const isSeaReport = reportType === ReportType.Sea;
  const isPortReport = reportType === ReportType.Port;
  const portPosition = portAndPosition?.portPosition;
  // The end of the report period, from whichever field the form showed. A
  // hidden field keeps its value, so the arriving flag decides which to read,
  // not which one holds a value.
  const periodEnd = voyageItinerary?.arrivingAtEosp
    ? (reportTime?.eosp ?? reportTime?.reportTo)
    : reportTime?.reportTo;

  return {
    voyage_number: vesselDetails?.voyageNumber,
    cp_speed: vesselDetails?.cpSpeed,
    cp_fuel: vesselDetails?.cpFuel,
    instructed_speed: vesselDetails?.instructedSpeed,
    instructed_foc: vesselDetails?.instructedFoc,
    other_speed_steaming: optionToCodeOrNull(vesselDetails?.otherSpeedSteaming),
    captain_name: miscellaneous?.captainName,
    chief_engineer: miscellaneous?.chiefEngineer,
    weight:
      reportType === ReportType.Port
        ? miscellaneous?.cargoWeight
        : cargoDetails?.cargoWeight,
    operating_code: optionToCodeOrNull(miscellaneous?.operatingCode),
    ballast_code: optionToCodeOrNull(miscellaneous?.ballastCode),
    routing_advice_followed: miscellaneous?.routingAdviceFollowed,

    operation_type: optionToString(operatingConditions?.operationType),
    location: optionToString(operatingConditions?.cargoLocation),
    tugs_all_fast: operatingConditions?.tugsAllFast,
    tugs_last_line: operatingConditions?.tugsLastLine,
    // V1 shows the all-fast set on a Port report and the plain set on every
    // other report, so the form's one set goes to the set the type shows.
    all_fast_draught_forward: isPortReport
      ? operatingConditions?.draughtForward
      : undefined,
    all_fast_draught_aft: isPortReport
      ? operatingConditions?.draughtAft
      : undefined,
    all_fast_trim: isPortReport ? operatingConditions?.trim : undefined,
    draught_forward: isPortReport
      ? undefined
      : operatingConditions?.draughtForward,
    draught_aft: isPortReport ? undefined : operatingConditions?.draughtAft,
    trim: isPortReport ? undefined : operatingConditions?.trim,
    recommended_trim: operatingConditions?.recommendedTrim,
    ballast_water: operatingConditions?.ballastWaterOnBoard,
    ballast_water_intake: operatingConditions?.ballastWaterIntake,
    ballast_water_discharged: operatingConditions?.ballastWaterDischarged,
    ballast_water_sample_date:
      convertToDateString(operatingConditions?.ballastWaterSampleDate) ?? '',
    ballast_water_count_of_cells: operatingConditions?.ballastWaterCountOfCells,

    report_from: convertToDateString(reportTime?.reportFrom) ?? '',
    // report_to is the end of the report period. The form shows the EOSP field
    // in place of Report To while the ship arrives at EOSP, so the period end
    // comes from whichever field the form showed. The pair is written the same
    // way it always was: both hold the instant on an arriving report.
    report_to: convertToDateString(periodEnd) ?? '',
    report_creation: convertToDateString(reportTime?.reportCreation) ?? '',
    timezone: optionToTvaTimezone(reportTime?.timezone),
    previous_timezone: optionToTvaTimezone(reportTime?.previousTimezone),
    report_period: reportTime?.reportPeriod,
    eosp: voyageItinerary?.arrivingAtEosp
      ? (convertToDateString(periodEnd) ?? '')
      : '',
    time_sailed: reportTime?.timeSailed,

    port_name: portAndPosition?.portName,
    port_latitude_hours: portPosition?.latDegrees,
    port_latitude_minutes: portPosition?.latMinutes,
    port_latitude_seconds: portPosition?.latSeconds,
    ...directionField('port_latitude_direction', portPosition?.latDirection),
    port_longitude_hours: portPosition?.longDegrees,
    port_longitude_minutes: portPosition?.longMinutes,
    port_longitude_seconds: portPosition?.longSeconds,
    ...directionField('port_longitude_direction', portPosition?.longDirection),

    displacement: stabilityConditions?.displacement,
    gm: stabilityConditions?.gm,
    shear_force: stabilityConditions?.shearForce,
    bending_force: stabilityConditions?.bendingForce,
    torsion: stabilityConditions?.torsion,
    gm_reserve: stabilityConditions?.gmReserve,
    dead_weight_reserve: stabilityConditions?.deadWeightReserve,
    constant_weight: stabilityConditions?.constantWeight,

    etd: convertToDateString(portOperationsTimestamp?.etd) ?? '',
    nor_tendered:
      convertToDateString(portOperationsTimestamp?.norTendered) ?? '',
    all_fast: convertToDateString(portOperationsTimestamp?.allFast) ?? '',
    hoses_connected:
      convertToDateString(portOperationsTimestamp?.hosesConnected) ?? '',
    commence: convertToDateString(portOperationsTimestamp?.commenceLoad) ?? '',
    finish: convertToDateString(portOperationsTimestamp?.finishLoad) ?? '',
    hoses_disconnected:
      convertToDateString(portOperationsTimestamp?.hosesDisconnected) ?? '',
    standby_engines:
      convertToDateString(portOperationsTimestamp?.lastLine) ?? '',
    cargo_documents_onboard:
      convertToDateString(portOperationsTimestamp?.cargoDocsOnboard) ?? '',
    start_discharging:
      convertToDateString(portOperationsTimestamp?.startDischarging) ?? '',
    end_discharging:
      convertToDateString(portOperationsTimestamp?.endDischarging) ?? '',
    completed_tank_inspection:
      convertToDateString(portOperationsTimestamp?.completedTankInspection) ??
      '',

    fresh_water_domestic: poolSpecificFields?.freshWaterDomestic,
    fresh_water_tank_cleaning: poolSpecificFields?.freshWaterTankCleaning,
    slops_residual_tanks: poolSpecificFields?.slopsResidualTanks,
    slops_slop_tanks: poolSpecificFields?.slopsSlopTanks,
    total_interface_oil: poolSpecificFields?.totalInterfaceOil,
    total_interface_water: poolSpecificFields?.totalInterfaceWater,
    first_line: convertToDateString(poolSpecificFields?.firstLine) ?? '',
    gangway_down: convertToDateString(poolSpecificFields?.gangwayDown) ?? '',
    cargo_docs_onboard:
      convertToDateString(poolSpecificFields?.cargoDocsOnboard) ?? '',

    awaiting_orders: isSeaReport
      ? voyageItinerary?.awaitingOrders
      : nextPort?.awaitingOrders,
    next_port: isSeaReport ? voyageItinerary?.nextPort : nextPort?.nextPort,
    remaining_distance: nextPort?.remainingDistance,
    remaining_time: nextPort?.remainingTime,
    destination_timezone: optionToTvaTimezone(nextPort?.nextTimezone),
    eta_pilot_station: convertToDateString(nextPort?.etaPilotStation) ?? '',
    maneuvering_type: optionToCodeOrNull(voyageItinerary?.maneuveringType),

    arriving: voyageItinerary?.arrivingAtEosp,
    eta: convertToDateString(voyageItinerary?.eta) ?? '',
    eta_timezone: optionToTvaTimezone(voyageItinerary?.etaTimezone),
    previous_eta: convertToDateString(voyageItinerary?.previousEta) ?? '',
    cosp: convertToDateString(voyageItinerary?.cosp) ?? '',
    previously_in_eca: voyageItinerary?.previouslyInEca,
    enter_eca: convertToDateString(voyageItinerary?.enterEca) ?? '',
    exit_eca: convertToDateString(voyageItinerary?.exitEca) ?? '',
    time_in_eca: voyageItinerary?.timeInEca,
    in_eca: voyageItinerary?.inEca,

    number_of_passengers: otherDetails?.numPassengers,
    number_of_crew: otherDetails?.numCrew,
    purposes_of_call: portCallReasons?.purposesOfCall ?? [],
  } as unknown as TvaApiOperational;
};

// Returns the underlying code of a SelectOption, or the value itself.
const optionCode = (value: unknown): unknown => {
  if (value && typeof value === 'object' && 'code' in value) {
    return (value as { code: unknown }).code;
  }
  return value;
};

const toArray = <T>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

// Extracts a plain tank id string from a form value that may be a SelectOption,
// an ObjectId wrapper ({ $oid }), or already a string.
const toTankId = (value: unknown): string | undefined => {
  const code = optionCode(value);
  if (code && typeof code === 'object' && '$oid' in code) {
    const oid = (code as { $oid: unknown }).$oid;
    return oid == null ? undefined : String(oid);
  }
  return code == null ? undefined : String(code);
};

const convertPositionToApi = (
  positionAndWeather: PositionAndWeatherState,
): TvaApiPositionAndWeather => {
  const {
    observation,
    wind,
    speedAndDistance,
    shipPosition,
    currentsAndSeaWater,
    propellers,
  } = positionAndWeather ?? ({} as PositionAndWeatherState);
  const position = shipPosition?.position;

  return {
    // Dates are sent as empty strings when cleared; other fields as null when
    // unset, so the server clears them on merge. Direction enums (N/E/S/W) are
    // omitted when empty (the server rejects '').
    observation: convertToDateString(observation?.observationTime) ?? '',
    observation_timezone: optionToTvaTimezone(observation?.observationTimezone),

    relative_wind_speed: wind?.apparentWindSpeed ?? null,
    relative_wind_direction: wind?.apparentWindDirection ?? null,
    true_wind_speed: wind?.trueWindSpeed ?? null,
    true_wind_direction: wind?.trueWindDirection ?? null,
    true_wind_force: wind?.trueWindForce ?? null,
    true_wind_effect: wind?.trueWindDirectionTrueNorth ?? null,
    true_wind_reporting: wind?.trueWindReporting ?? null,

    trip_counter_gps: speedAndDistance?.tripCounterGps ?? null,
    trip_counter_log: speedAndDistance?.tripCounterLog ?? null,
    speed_log_malfunction: speedAndDistance?.speedLogMalfunction ?? null,
    log_distance: speedAndDistance?.logDistance ?? null,
    log_speed: speedAndDistance?.logSpeed ?? null,
    cog: speedAndDistance?.courseOverGround ?? null,
    observed_speed: speedAndDistance?.observedSpeed ?? null,
    observed_distance: speedAndDistance?.observedDistance ?? null,
    observed_distance_in_eca: speedAndDistance?.observedDistanceInEca ?? null,
    observed_distance_for_total_sea_passage:
      speedAndDistance?.observedDistanceTotalSeaPassage ?? null,
    remaining_distance: speedAndDistance?.remainingDistance ?? null,
    bad_weather_hours: speedAndDistance?.badWeatherHours ?? null,
    bad_weather_observed_distance:
      speedAndDistance?.badWeatherObservedDistance ?? null,
    propeller_rpm: speedAndDistance?.propellerRpm ?? null,
    propeller_pitch_percent: speedAndDistance?.propellerPitchPercent ?? null,

    ship_lat_hours: position?.latDegrees ?? null,
    ship_lat_minutes: position?.latMinutes ?? null,
    ship_lat_seconds: position?.latSeconds ?? null,
    ...directionField('ship_lat_direction', position?.latDirection),
    ship_lon_hours: position?.longDegrees ?? null,
    ship_lon_minutes: position?.longMinutes ?? null,
    ship_lon_seconds: position?.longSeconds ?? null,
    ...directionField('ship_lon_direction', position?.longDirection),

    swell: optionCode(currentsAndSeaWater?.seaSwell) ?? null,
    swell_direction: currentsAndSeaWater?.seaSwellDirection ?? null,
    wave_height: currentsAndSeaWater?.waveHeight ?? null,
    water_depth: currentsAndSeaWater?.waterDepth ?? null,
    true_current_effect: currentsAndSeaWater?.currentDirectionTrueNorth ?? null,
    current_speed: currentsAndSeaWater?.trueCurrentSpeed ?? null,
    current_direction: currentsAndSeaWater?.apparentCurrentDirection ?? null,
    relative_current_speed: currentsAndSeaWater?.apparentCurrentSpeed ?? null,
    wave_direction: currentsAndSeaWater?.apparentWaveDirection ?? null,
    sea_temperature: currentsAndSeaWater?.seaTemperature ?? null,
    sea_water_density: currentsAndSeaWater?.seaWaterDensity ?? null,
    atmospheric_pressure: currentsAndSeaWater?.atmosphericPressure ?? null,
    salinity: currentsAndSeaWater?.salinity ?? null,

    propeller_pitches: propellers?.propellerPitches?.map(
      (entry) => entry.pitch,
    ),
  } as unknown as TvaApiPositionAndWeather;
};

// Reverse of `convertMachineryRunningHours`: maps a machinery entry key back to
// its API field base (e.g. 'auxEngine3' -> 'aux_engine_3', 'auxBoiler1' -> 'aux_boiler').
const machineryKeyToApiBase = (key: string | undefined): string | undefined => {
  if (!key) return;
  const mainEngine = key.match(/^mainEngine(\d+)$/);
  if (mainEngine) return `main_engine_${mainEngine[1]}`;
  const auxEngine = key.match(/^auxEngine(\d+)$/);
  if (auxEngine) return `aux_engine_${auxEngine[1]}`;
  const mainAirComp = key.match(/^mainAirComp(\d+)$/);
  if (mainAirComp) return `main_engine_comp_${mainAirComp[1]}`;
  if (key === 'auxBoiler1') return 'aux_boiler';
  if (key === 'auxBoiler2') return 'aux_boiler_2';
  if (key === 'compositeBoiler') return 'composite_boiler';
  const cargoCompressor = key.match(/^cargoCompressor(\d+)$/);
  if (cargoCompressor) return `cargo_compressor_${cargoCompressor[1]}`;
  const bogCompressor = key.match(/^bogCompressor(\d+)$/);
  if (bogCompressor) return `bog_compressor_${bogCompressor[1]}`;
  const cargoPump = key.match(/^cargoPump(\d+)$/);
  if (cargoPump) return `cargo_pump_${cargoPump[1]}`;
  const ballastPump = key.match(/^ballastPump(\d+)$/);
  if (ballastPump) return `ballast_pump_${ballastPump[1]}`;
  if (key === 'bowThruster') return 'bow_thruster';
  if (key === 'sternThruster') return 'stern_thruster';
  if (key === 'scr') return 'scr';
  return;
};

const PUMP_FIELDS: [string, string][] = [
  ['meLoPump1', 'me_lo_pump_1'],
  ['meLoPump2', 'me_lo_pump_2'],
  ['seaWaterPump1', 'sea_water_pump_1'],
  ['seaWaterPump2', 'sea_water_pump_2'],
  ['sgPump1', 'sg_pump_1'],
  ['sgPump2', 'sg_pump_2'],
  ['foPurifier1', 'fo_purifier_1'],
  ['foPurifier2', 'fo_purifier_2'],
  ['erFan1', 'er_fan_1'],
  ['erFan2', 'er_fan_2'],
  ['erFan3', 'er_fan_3'],
  ['erFan4', 'er_fan_4'],
];

const convertPowerToApi = (power: PowerState): TvaApiPower => {
  const result: Record<string, unknown> = {};
  if (!power) return result as unknown as TvaApiPower;

  // The vessel spec names these paths, so the adapter cannot tell a
  // Difference from a reading. Each empty path goes as null.
  Object.entries(power.flowMeterInstallation ?? {}).forEach(([path, value]) => {
    result[path] = value ?? null;
  });

  const mainEngine = power.mainEngine;
  if (mainEngine) {
    result['main_engine_1_power'] = mainEngine.power;
    result['main_engine_1_rpm'] = mainEngine.rpm;
    result['main_engine_1_current_rpm'] = mainEngine.revCounterCurrent;
    result['main_engine_1_previous_rpm'] = mainEngine.revCounterPrevious;
    result['main_engine_1_shaft_power_meter_counter'] =
      mainEngine.shaftPowerMeterCounterCurrent;
    result['main_engine_1_shaft_power_meter_counter_previous'] =
      mainEngine.shaftPowerMeterCounterPrevious;
    result['avg_tcrpm_1'] = mainEngine.tcRpm1;
    result['avg_tcrpm_2'] = mainEngine.tcRpm2;

    // Calculated Values. TVA-API merges the payload into the saved report,
    // so null clears a value that the payload would otherwise leave as it
    // was. V1 has one key for the average power, and the Hand-reported Value
    // goes in it when the user types one.
    result['tcrpm_power'] = mainEngine.tcRpmPower ?? null;
    result['main_engine_1_load'] = mainEngine.mainEngineLoad ?? null;
    result['main_engine_1_difference_rpm'] =
      mainEngine.mainEngineRevCounterDiff ?? null;
    result['main_engine_1_calculated_rpm'] =
      mainEngine.mainEngineCalculatedRpm ?? null;
    result['main_engine_1_and_2_power'] =
      mainEngine.mainEngines1And2Power ?? null;
    result['main_engine_12_load'] = mainEngine.mainEngines1And2Load ?? null;
    result['main_engine_1_and_2_rh'] =
      mainEngine.mainEngines1And2RunningHours ?? null;
    result['main_engine_1_shaft_power_meter_difference'] =
      mainEngine.shaftPowerMeterCounterDifference ?? null;
    result['main_engine_1_shaft_power_meter_calculated'] =
      mainEngine.shaftPowerMeterCounterCalculatedManual ??
      mainEngine.shaftPowerMeterCounterCalculated ??
      null;
  }

  toArray<MachineryRhState>(power.machineryRh).forEach((entry) => {
    if (entry?.key === 'shaftGeneratorPTO') {
      result['shaft_generator_pto_curr_rh'] = entry.current;
      result['shaft_generator_pto_prev_rh'] = entry.previous;
      result['shaft_generator_pto_diff_rh'] = entry.difference ?? null;
      return;
    }

    const base = machineryKeyToApiBase(entry?.key);
    if (!base) return;
    result[`${base}_cur_rh`] = entry.current;
    result[`${base}_prev_rh`] = entry.previous;
    result[`${base}_diff_rh`] = entry.difference ?? null;
  });

  const flowMeters = power.flowMeterInstallationOld;
  if (flowMeters) {
    result['booster_unit_hfo_cur_rh'] = flowMeters.foBoosterCurr;
    result['booster_unit_hfo_prev_rh'] = flowMeters.foBoosterPrev;
    result['booster_unit_hfo_diff_rh'] = flowMeters.foBoosterDiff ?? null;
    result['booster_unit_gas_oil_cur_rh'] = flowMeters.gasOilBoosterCurr;
    result['booster_unit_gas_oil_prev_rh'] = flowMeters.gasOilBoosterPrev;
    result['booster_unit_gas_oil_diff_rh'] =
      flowMeters.gasOilBoosterDiff ?? null;
    result['main_engine_flow_inlet_cur_rh'] = flowMeters.meAeInletCurr;
    result['main_engine_flow_inlet_prev_rh'] = flowMeters.meAeInletPrev;
    result['main_engine_flow_inlet_diff_rh'] = flowMeters.meAeInletDiff ?? null;
    result['aux_engine_flow_inlet_cur_rh'] = flowMeters.auxEngineInletCurr;
    result['aux_engine_flow_inlet_prev_rh'] = flowMeters.auxEngineInletPrev;
    result['aux_engine_flow_inlet_diff_rh'] =
      flowMeters.auxEngineInletDiff ?? null;
    result['aux_engine_flow_outlet_cur_rh'] = flowMeters.auxEngineOutletCurr;
    result['aux_engine_flow_outlet_prev_rh'] = flowMeters.auxEngineOutletPrev;
    result['aux_engine_flow_outlet_diff_rh'] =
      flowMeters.auxEngineOutletDiff ?? null;
    result['aux_boiler_flow_cur_rh'] = flowMeters.auxBoiler1Curr;
    result['aux_boiler_flow_prev_rh'] = flowMeters.auxBoiler1Prev;
    result['aux_boiler_flow_diff_rh'] = flowMeters.auxBoiler1Diff ?? null;
    result['cylinder_lube_oil_cur'] = flowMeters.cylinderLubeOilCurr;
    result['cylinder_lube_oil_prev'] = flowMeters.cylinderLubeOilPrev;
    result['cylinder_lube_oil_diff'] = flowMeters.cylinderLubeOilDiff ?? null;
  }

  const pumps = power.pumps;
  if (pumps) {
    result['reporting_pumps'] = pumps.reportPumpSection;
    for (const [statePrefix, apiPrefix] of PUMP_FIELDS) {
      result[`${apiPrefix}_cur_rh`] = pumps[`${statePrefix}Curr`];
      result[`${apiPrefix}_prev_rh`] = pumps[`${statePrefix}Prev`];
      result[`${apiPrefix}_diff_rh`] = pumps[`${statePrefix}Diff`] ?? null;
    }
  }

  toArray<GeneratorEngineState>(power.generatorEngines).forEach((generator) => {
    if (generator?.key?.startsWith('turboGenerator')) {
      result['turbo_power'] = generator.power;
      result['turbo_maxRating'] = generator.maxRating;
      result['turbo_rh'] = generator.runningHours;
      result['turbo_charge_air_pressure'] = generator.chargeAirPressure;
      return;
    }

    const auxEngine = generator?.key?.match(/^auxEngine(\d+)$/);
    if (!auxEngine) return;
    const index = auxEngine[1];
    result[`aux_engine_${index}_power`] = generator.power;
    result[`aux_engine_${index}_max_rating`] = generator.maxRating ?? null;
    // aux_engine_N_cur_rh is owned by the machinery running hours section
    // (mapped above from `current`); the generator's running hours is a
    // read-only mirror, so don't overwrite it here.
    result[`aux_engine_${index}_charge_air_pressure`] =
      generator.chargeAirPressure;
    result[`aux_engine_${index}_lo`] = generator.loAdded;
  });

  result['cargo_pumps_kwh'] = toArray<CargoPumpMeterState>(
    power.cargoPumpsKwh,
  ).map((entry) => ({
    cur: entry.cur ?? null,
    prev: entry.prev ?? null,
    diff:
      entry.cur != null &&
      !isNaN(entry.cur) &&
      entry.prev != null &&
      !isNaN(entry.prev)
        ? entry.cur - entry.prev
        : 0,
  }));

  result['scrubber_in_use'] = power.scrubber?.inUse;
  result['scrubber_rh'] = power.scrubber?.runningHours;
  result['scrubber_mode'] = power.scrubber?.mode;

  const shaftGeneratorPTO = power.shaftGeneratorPTO;
  if (shaftGeneratorPTO) {
    result['shaft_generator_pto_power'] = shaftGeneratorPTO.power;
    result['shaft_generator_pto_max_rating'] =
      shaftGeneratorPTO.maxRating ?? null;
    result['shaft_generator_pto_rh'] = shaftGeneratorPTO.runningHours ?? null;
  }

  const environmental = power.environmental;
  if (environmental) {
    result['incinerator_malfunctional'] =
      environmental.incineratorMalfunctional;
    result['odme_malfunctional'] = environmental.odmeMalfunctional;
    result['bwts_malfunctional'] = environmental.bwtsMalfunctional;
    result['scrubber_malfunctional'] = environmental.scrubberMalfunctional;
    result['ows_malfunctional'] = environmental.owsMalfunctional;
  }

  const shorePower = power.shorePower;
  if (shorePower) {
    result['shore_power_is_used'] = shorePower.isUsed ?? false;
    result['shore_power_reception_port'] = shorePower.receptionPort ?? null;
    result['shore_power_kwh'] = shorePower.kwh ?? null;
    result['shore_power_hours'] = shorePower.hours ?? null;
    result['shore_power_start_time'] =
      convertToDateString(shorePower.startTime) ?? '';
    result['shore_power_end_time'] =
      convertToDateString(shorePower.endTime) ?? '';
    result['total_shore_power_kwh'] = shorePower.totalKwh ?? null;
    result['shore_power_wtt'] = shorePower.wtt ?? null;
    result['edn_number'] = shorePower.ednNumber ?? null;
    result['edn_files'] = shorePower.ednFiles ?? [];
    result['sustainable_shore_power_is_used'] =
      shorePower.sustainableIsUsed ?? false;
    result['proof_of_sustainability_edn_files'] =
      shorePower.proofOfSustainabilityEdnFiles ?? [];
    result['running_total_shore_power_kwh'] =
      shorePower.runningTotalKwh ?? null;
    result['edn_id'] = shorePower.ednId ?? null;
  }

  return result as unknown as TvaApiPower;
};

// The consumptions form store organises data by fuel-spec entry (up to 3
// fuels), with total/aux-engine/boiler consumption entries aligned by index to
// the fuel specs. The API stores these as per-fuel-suffixed fields
// (e.g. main_engine_vlsfo2020, ae_basic_load_vlsfo2020), so we fan each entry
// out against its fuel grade. This is the shape the form actually holds, which
// differs from the flat `ConsumptionsState` used on read.
interface ConsumptionFuelSpecEntry {
  fuelType?: SelectOption;
  lcv?: number;
  sulphur?: number;
  density?: number;
  fuelTemp?: number;
  viscosity?: number;
}

interface ConsumptionTotalEntry {
  [key: string]: number | undefined;
}

interface ConsumptionFormShape {
  water?: {
    fwgNonFunctional?: boolean;
    freshWaterProduced?: number;
    distilledWaterProduced?: number;
  };
  inertGasSystems?: { entries?: { from?: Date; to?: Date }[] };
  cylinderOilConsumption?: {
    oilCode?: unknown;
    oilBn?: number;
    oilDensityAt15?: number;
    oilTankTemperature?: number;
    oilConsumption?: number;
    oilKgConsumption?: number;
    oilSloc?: number;
  };
  fuelSpecs?: { entries?: ConsumptionFuelSpecEntry[] };
  totalConsumptions?: { entries?: ConsumptionTotalEntry[] };
  auxEngineConsumptions?: { entries?: ConsumptionTotalEntry[] };
  boilerConsumptions?: { entries?: ConsumptionTotalEntry[] };
}

// Fuel-spec entry field -> API field prefix.
const FUEL_SPEC_FIELDS: [keyof ConsumptionFuelSpecEntry, string][] = [
  ['lcv', 'lcv'],
  ['sulphur', 'sulphur'],
  ['density', 'density'],
  ['fuelTemp', 'serv'],
  ['viscosity', 'viscosity'],
];

// Total-consumption entry field -> API field prefix.
const TOTAL_CONSUMPTION_FIELDS: [string, string][] = [
  ['mainEngine', 'main_engine'],
  ['auxEngines', 'aux_engine'],
  ['boiler', 'aux_boiler'],
  ['igGenerator', 'ig_generator'],
  ['incinerator', 'incinerator'],
  ['dpp', 'dpp'],
  ['otherEngines', 'other'],
  ['cargoRelated', 'cargo_related'],
  ['total', 'total_consumption'],
];

// Aux-engine / boiler breakdown entry field -> API field infix.
const BREAKDOWN_CONSUMPTION_FIELDS: [string, string][] = [
  ['basicLoad', 'basic_load'],
  ['ballastOperations', 'ballast_operations'],
  ['cargoHeating', 'cargo_heating'],
  ['tankCleaning', 'tank_cleaning'],
  ['cargoDischarging', 'cargo_shifting'],
  ['inertGasSystems', 'inert_gas_systems'],
  ['bunkerHeating', 'bunker_heating'],
];

const convertConsumptionToApi = (
  consumption: ConsumptionsState,
): TvaApiConsumption => {
  // V1 always sends this flag (tabs.directive.ts:4359 in ../TVA-Reporting).
  // Without it, TVA-API refuses a report that uses a new fuel grade.
  const result: Record<string, unknown> = { new_fuel_configuration: true };
  if (!consumption) return result as unknown as TvaApiConsumption;

  const form = consumption as unknown as ConsumptionFormShape;

  const fuelSpecs = toArray<ConsumptionFuelSpecEntry>(form.fuelSpecs?.entries);
  const totals = toArray<ConsumptionTotalEntry>(
    form.totalConsumptions?.entries,
  );
  const auxEngine = toArray<ConsumptionTotalEntry>(
    form.auxEngineConsumptions?.entries,
  );
  const boiler = toArray<ConsumptionTotalEntry>(
    form.boilerConsumptions?.entries,
  );

  // Always emit fuel_type_1/2/3. An unset slot is sent as null (rather than
  // omitted) so the server clears it. A slot with no fuel grade can't have
  // per-fuel keyed values, so nothing further is emitted for it.
  const fuelSlotCount = Math.max(3, fuelSpecs.length);
  for (let index = 0; index < fuelSlotCount; index++) {
    const spec = fuelSpecs[index];
    const fuelCode = optionCode(spec?.fuelType);

    if (!fuelCode) {
      result[`fuel_type_${index + 1}`] = null;
      continue;
    }

    const fuel = String(fuelCode);
    result[`fuel_type_${index + 1}`] = fuel;

    // Unset fields for a selected fuel are sent as null (not omitted) so the
    // server clears them on merge rather than retaining stale values.
    for (const [field, prefix] of FUEL_SPEC_FIELDS) {
      result[`${prefix}_${fuel}`] = spec?.[field] ?? null;
    }

    const total = totals[index];
    for (const [field, prefix] of TOTAL_CONSUMPTION_FIELDS) {
      result[`${prefix}_${fuel}`] = total?.[field] ?? null;
    }

    const ae = auxEngine[index];
    for (const [field, infix] of BREAKDOWN_CONSUMPTION_FIELDS) {
      result[`ae_${infix}_${fuel}`] = ae?.[field] ?? null;
    }

    const ab = boiler[index];
    for (const [field, infix] of BREAKDOWN_CONSUMPTION_FIELDS) {
      result[`ab_${infix}_${fuel}`] = ab?.[field] ?? null;
    }
  }

  // Inert gas system dates must be sent as empty strings when cleared (rather
  // than omitted) so the server clears the stored value on merge.
  toArray<{ from?: Date; to?: Date }>(form.inertGasSystems?.entries).forEach(
    (run, index) => {
      result[`system_run_${index + 1}_from`] =
        convertToDateString(run?.from) ?? '';
      result[`system_run_${index + 1}_to`] = convertToDateString(run?.to) ?? '';
    },
  );

  const cylinderOil = form.cylinderOilConsumption;
  if (cylinderOil) {
    const cylOilCode = optionCode(cylinderOil.oilCode);
    result['cyl_oil_code'] =
      cylOilCode == null ? undefined : String(cylOilCode);
    result['cyl_oil_bn'] = cylinderOil.oilBn;
    result['cyl_oil_density_at_15'] = cylinderOil.oilDensityAt15;
    result['cyl_oil_tank_temperature'] = cylinderOil.oilTankTemperature;
    result['cyl_oil_consumption'] = cylinderOil.oilConsumption;
    result['cyl_oil_kg_consumption'] = cylinderOil.oilKgConsumption ?? null;
    result['cyl_oil_sloc'] = cylinderOil.oilSloc ?? null;
  }

  const water = form.water;
  if (water) {
    result['fwg_non_operational'] = water.fwgNonFunctional;
    result['fresh_water'] = water.freshWaterProduced;
    result['distilled_water'] = water.distilledWaterProduced;
  }

  return result as unknown as TvaApiConsumption;
};

const convertTankToApi = (tank: StockTank): TankStock =>
  ({
    _id: tank._id,
    amount: tank.amount ?? null,
    amount_before_bunkering: tank.amountBeforeBunkering ?? null,
    bdn_number: tank.bdnNumber,
    // Dates are sent as empty strings when cleared so the server clears them.
    bunker_date: convertToDateString(tank.bunkerDate) ?? '',
    bunker_port: tank.bunkerPort,
    density: tank.density ?? null,
    density_before_bunkering: tank.densityBeforeBunkering ?? null,
    fuel_class: tank.fuelClass,
    fuel_grade: optionToString(tank.fuelGrade),
    fuel_grade_added_in_tank: optionToString(tank.fuelGradeAddedInTank),
    in_use: tank.inUse,
    is_blend: tank.isBlend,
    lcv: tank.lcv ?? null,
    lcv_before_bunkering: tank.lcvBeforeBunkering ?? null,
    stable_until: convertToDateString(tank.stableUntil) ?? '',
    sulphur_before_bunkering: tank.sulphurBeforeBunkering ?? null,
    sulphur_after_bunkering: tank.sulphurAfterBunkering ?? null,
    quantity_added_in_tank: tank.quantityAddedInTank ?? null,
    viscosity: tank.viscosity ?? null,
  }) as unknown as TankStock;

const convertStockToApi = (stock: StockState): TvaApiStock => {
  // V1 always sends this flag (tabs.directive.ts:10727 in ../TVA-Reporting).
  const result: Record<string, unknown> = { new_fuel_configuration: true };
  if (!stock) return result as unknown as TvaApiStock;

  const tanks: Record<string, unknown> = {};
  Object.entries(stock.tanks ?? {}).forEach(([tankId, tank]) => {
    if (tank) tanks[tankId] = convertTankToApi(tank);
  });
  result['tanks'] = tanks;

  result['tank_transfers'] = toArray<{
    from?: unknown;
    to?: unknown;
    quantity?: number;
  }>(stock.tankTransfers).map((transfer) => {
    // Unset values are sent as null (not omitted) so the server clears them on
    // merge. The tank id is sent as a plain string (the server accepts that or
    // a { $oid } wrapper); a null from_tank/to_tank is treated as no transfer.
    return {
      from_tank: toTankId(transfer.from) ?? null,
      to_tank: toTankId(transfer.to) ?? null,
      quantity: transfer.quantity ?? null,
    };
  });

  result['tank_sounding'] = stock.options?.tankSounding ?? null;
  result['tank_based_reporting'] = stock.options?.tankBasedReporting ?? null;

  // ROB fields are sent as null when unset so the server clears them on merge.
  const fuelWaterRob = stock.fuelWaterRob;
  if (fuelWaterRob) {
    result['tank_cleaning_chemical'] =
      fuelWaterRob.tankCleaningChemical ?? null;
    result['bilge_water'] = fuelWaterRob.bilgeWater ?? null;
    result['fresh_water'] = fuelWaterRob.freshWater ?? null;
    result['distilled_water'] = fuelWaterRob.distilledWater ?? null;
    result['discharged_sludge'] = fuelWaterRob.dischargedSludge ?? null;
    result['discharged_bilge_water'] =
      fuelWaterRob.dischargedBilgeWater ?? null;
  }

  const lubeOilRob = stock.lubeOilRob;
  if (lubeOilRob) {
    result['lube_oil_system'] = lubeOilRob.lubeOilSystem ?? null;
    result['lube_oil_spare'] = lubeOilRob.lubeOilSpare ?? null;
    result['cyl_oil_low_bn'] = lubeOilRob.lowBnCylinderOil ?? null;
    result['cyl_oil'] = lubeOilRob.highBnCylinderOil ?? null;
    result['aux_engine_lube_oil'] = lubeOilRob.aeLubeOil ?? null;
    result['turbo_oil'] = lubeOilRob.turboOil ?? null;
    result['hydraulic_oil'] = lubeOilRob.hydraulicOil ?? null;
    result['other_oil'] = lubeOilRob.otherOil ?? null;
  }

  const oilLosses = stock.oilLosses;
  if (oilLosses) {
    result['total_waste_oil'] = oilLosses.totalWasteOil ?? null;
    result['incinerated_waste_oil'] = oilLosses.incineratedWasteOil ?? null;
    result['evaporated_waste_oil'] =
      oilLosses.evaporatedWaterFromWasteOil ?? null;
    result['sludge'] = oilLosses.sludgeRob ?? null;
    result['discharged_waste_oil'] = oilLosses.dischargedWasteOil ?? null;
  }

  return result as unknown as TvaApiStock;
};

// State bunker-row field -> API field prefix, reversing `createBunkerRows`.
const BUNKER_ROW_FIELDS: [keyof BunkerRowEntry, string][] = [
  ['quantityOrdered', 'quantity_ordered'],
  ['quantityReceived', 'quantity_received'],
  ['bdnFigures', 'bdn_figures'],
  ['density', 'density'],
  ['lcv', 'lcv'],
  ['sulphur', 'sulphur_bdn'],
  ['viscosity', 'viscosity'],
  ['labSulphur', 'lab_sulphur'],
];

// Lube oil bunker state fields -> API field suffix, used to build
// quantity_received_<suffix> and bdn_figures_<suffix>.
const LUBE_OIL_BUNKER_FIELDS: [string, string, string][] = [
  ['meLubeOilSystemQuantity', 'meLubeOilSystemFigures', 'me_lube_oil_system'],
  ['meLubeOilSpareQuantity', 'meLubeOilSpareFigures', 'me_lube_oil_spare'],
  [
    'lowBnCylinderOilQuantity',
    'lowBnCylinderOilFigures',
    'cylinder_oil_low_bn',
  ],
  ['highBnCylinderOilQuantity', 'highBnCylinderOilFigures', 'cylinder_oil'],
  [
    'auxiliaryEngineLubeOilQuantity',
    'auxiliaryEngineLubeOilFigures',
    'ae_lube_oil',
  ],
  ['turboOilQuantity', 'turboOilFigures', 'turbo_oil'],
  ['hydraulicOilQuantity', 'hydraulicOilFigures', 'hydraulic_oil'],
  ['otherOilQuantity', 'otherOilFigures', 'other_oil'],
];

const convertLubeOilBunkers = (
  bunker: TvaApiBunker | undefined,
): BunkerState['lubeOilBunkers'] => {
  if (!bunker) return {};
  const result: Record<string, unknown> = {
    startedBunkering: convertToDate(
      bunker['lube_started_bunkering_date'] as unknown as DateObject,
    ),
    finishedBunkering: convertToDate(
      bunker['lube_bunkering_date'] as unknown as DateObject,
    ),
    protestIssued: bunker['lube_lop'],
  };
  for (const [quantityField, figuresField, suffix] of LUBE_OIL_BUNKER_FIELDS) {
    result[quantityField] = bunker[`quantity_received_${suffix}`];
    result[figuresField] = bunker[`bdn_figures_${suffix}`];
  }
  return result as unknown as BunkerState['lubeOilBunkers'];
};

const convertDebunkered = (
  bunker: TvaApiBunker | undefined,
): BunkerState['debunkered'] => {
  if (!bunker) return {} as BunkerState['debunkered'];
  const grades =
    (bunker['debunkered_fuel_grades'] as string[] | undefined) ?? [];
  const result: Record<string, unknown> = {
    startedDebunkering: convertToDate(
      bunker['fuel_started_debunkering_date'] as unknown as DateObject,
    ),
    finishedDebunkering: convertToDate(
      bunker['fuel_debunkering_date'] as unknown as DateObject,
    ),
  };
  // Two debunkered fuel-grade/quantity slots, keyed by grade on the API side.
  grades.slice(0, 2).forEach((grade, slot) => {
    if (!grade) return;
    result[`debunkeredFuelGrade${slot}`] = {
      code: grade,
      name: grade.toLocaleUpperCase(),
    };
    result[`debunkeredQuantity${slot}`] =
      bunker[`quantity_debunkered_${grade}`];
  });
  return result as unknown as BunkerState['debunkered'];
};

const convertBunkerToApi = (bunker: BunkerState): TvaApiBunker => {
  // V1 always sends this flag (tabs.directive.ts:9330 in ../TVA-Reporting).
  const result: Record<string, unknown> = { new_fuel_configuration: true };
  if (!bunker) return result as unknown as TvaApiBunker;

  const options = bunker.options;
  result['took_fuel_oil'] = options?.tookFuelOil;
  result['took_lube_oil'] = options?.tookLubeOil;
  result['debunkered'] = options?.debunkered;

  // Dates are sent as empty strings when cleared (not omitted) so the server
  // clears them on merge; other fields are sent as null when unset.
  const fuelOilBunkers = bunker.fuelOilBunkers;
  result['fuel_started_bunkering_date'] =
    convertToDateString(fuelOilBunkers?.startedBunkering) ?? '';
  result['fuel_bunkering_date'] =
    convertToDateString(fuelOilBunkers?.finishedBunkering) ?? '';
  result['barge_alongside'] =
    convertToDateString(fuelOilBunkers?.bargeAlongside) ?? '';
  result['barge_away'] = convertToDateString(fuelOilBunkers?.bargeAway) ?? '';

  const table = bunker.fuelOilBunkerTable;
  const fuelGrades: string[] = [];
  const isoFuelGrades: string[] = [];
  toArray<BunkerRowEntry>(table?.bunkerRows).forEach((row) => {
    const fuelType = optionToString(row?.fuelType);
    fuelGrades.push(fuelType);
    isoFuelGrades.push(optionToString(row?.isoFuelGrade));
    if (!fuelType) return;
    for (const [rowKey, apiPrefix] of BUNKER_ROW_FIELDS) {
      result[`${apiPrefix}_${fuelType}`] = row?.[rowKey] ?? null;
    }
    result[`stable_until_${fuelType}`] =
      convertToDateString(row?.stableUntil) ?? '';
  });
  result['bunkered_fuel_grades'] = fuelGrades;
  result['bunkered_iso_fuel_grades'] = isoFuelGrades;

  result['quantity_ordered_fresh_water'] =
    table?.quantityOrderedFreshWater ?? null;
  result['quantity_received_fresh_water'] =
    table?.quantityReceivedFreshWater ?? null;
  result['bdn_figures_fresh_water'] = table?.bdnFiguresFreshWater ?? null;
  result['quantity_ordered_distilled_water'] =
    table?.quantityOrderedDistilledWater ?? null;
  result['quantity_received_distilled_water'] =
    table?.quantityReceivedDistilledWater ?? null;
  result['bdn_figures_distilled_water'] =
    table?.bdnFiguresDistilledWater ?? null;
  result['fuel_lop'] = table?.protestIssued ?? null;

  const lubeOilBunkers = bunker.lubeOilBunkers as
    | Record<string, unknown>
    | undefined;
  if (lubeOilBunkers) {
    result['lube_started_bunkering_date'] =
      convertToDateString(
        lubeOilBunkers['startedBunkering'] as Date | undefined,
      ) ?? '';
    result['lube_bunkering_date'] =
      convertToDateString(
        lubeOilBunkers['finishedBunkering'] as Date | undefined,
      ) ?? '';
    for (const [
      quantityField,
      figuresField,
      suffix,
    ] of LUBE_OIL_BUNKER_FIELDS) {
      result[`quantity_received_${suffix}`] =
        lubeOilBunkers[quantityField] ?? null;
      result[`bdn_figures_${suffix}`] = lubeOilBunkers[figuresField] ?? null;
    }
    result['lube_lop'] = lubeOilBunkers['protestIssued'] ?? null;
  }

  const debunkered = bunker.debunkered as Record<string, unknown> | undefined;
  if (debunkered) {
    result['fuel_started_debunkering_date'] =
      convertToDateString(
        debunkered['startedDebunkering'] as Date | undefined,
      ) ?? '';
    result['fuel_debunkering_date'] =
      convertToDateString(
        debunkered['finishedDebunkering'] as Date | undefined,
      ) ?? '';
    const debunkeredGrades: string[] = [];
    // Two debunkered slots; the quantity is keyed by the selected fuel grade.
    [0, 1].forEach((slot) => {
      const grade = optionToString(debunkered[`debunkeredFuelGrade${slot}`]);
      if (!grade) return;
      debunkeredGrades.push(grade);
      result[`quantity_debunkered_${grade}`] =
        debunkered[`debunkeredQuantity${slot}`] ?? null;
    });
    result['debunkered_fuel_grades'] = debunkeredGrades;
  }

  return result as unknown as TvaApiBunker;
};

const convertHoseOperationsToApi = (hoses?: HoseOperationState[]) =>
  toArray<HoseOperationState>(hoses).map((hose) => ({
    timestamp: convertToDateString(hose?.operationTimestamp),
    ifo: hose?.ifo,
    lsf: hose?.lsf,
    lsg: hose?.lsg,
    mgo: hose?.mgoOn,
  }));

const convertCargoDetailsToApi = (
  cargoDetails: CargoDetailsState,
): TvaApiCargoDetails => {
  const result: Record<string, unknown> = {};
  if (!cargoDetails) return result as unknown as TvaApiCargoDetails;

  result['terminal'] = cargoDetails.terminal?.name;
  const cargoes = toArray<CargoState>(cargoDetails.stowageDetails?.cargoes);
  result['n_cargoes'] =
    optionCode(cargoDetails.stowageDetails?.numberOfCargoes) ?? cargoes.length;
  result['cargo_grade'] = cargoes.map((cargo) =>
    optionToString(cargo?.cargoGrade),
  );
  result['bl_code'] = cargoes.map((cargo) => cargo?.blCode);
  result['bl_quantity'] = cargoes.map((cargo) => cargo?.blQuantity);
  // Date lists must not contain null holes: the server iterates every element
  // as a {$date} once the first one is a date, so a mix of dates and nulls
  // crashes it on read. Only emit the dates that are actually set.
  result['bl_discharge'] = cargoes
    .map((cargo) => convertToDateString(cargo?.blDischarge))
    .filter((date): date is string => date != null);
  result['bl_issue'] = cargoes
    .map((cargo) => convertToDateString(cargo?.blIssue))
    .filter((date): date is string => date != null);
  result['surveyors_cargo_quantity'] = cargoes.map(
    (cargo) => cargo?.surveyorsCargoQuantity,
  );
  result['charterer'] = cargoes.map((cargo) => cargo?.charterer);
  result['consignee'] = cargoes.map((cargo) => cargo?.consignee);
  result['density'] = cargoes.map((cargo) => cargo?.density);
  result['destination_port'] = cargoes.map((cargo) => cargo?.destinationPort);
  result['flash_point'] = cargoes.map((cargo) => cargo?.flashPoint);
  result['load_rate'] = cargoes.map((cargo) => cargo?.loadRate);
  result['load_temp'] = cargoes.map((cargo) => cargo?.loadTemp);
  result['pour_point'] = cargoes.map((cargo) => cargo?.pourPoint);
  result['s2s_ship_name'] = cargoes.map((cargo) => cargo?.s2sShipName);
  result['shipper'] = cargoes.map((cargo) => cargo?.shipper);

  const otherDetails = cargoDetails.otherDetails;
  if (otherDetails) {
    result['hose_diameter'] = otherDetails.hoseDiameter;
    result['protests_received'] = optionCode(otherDetails.protestsReceived);
    result['protests_issued'] = optionCode(otherDetails.protestsIssued);
    result['sea_temperature'] = otherDetails.seaTemperature;
    result['air_temperature'] = otherDetails.airTemperature;
    result['protests_received_comments'] =
      otherDetails.protestsReceivedComments;
    result['protests_issued_comments'] = otherDetails.protestsIssuedComments;
  }

  const hoses = cargoDetails.hoses;
  if (hoses) {
    const hosesEntry: Record<string, unknown> = {
      hose_on_operations: convertHoseOperationsToApi(hoses.hosesOn),
      hose_off_operations: convertHoseOperationsToApi(hoses.hosesOff),
      heating_operations: toArray<{
        comment?: string;
        ifo?: number;
        mgo?: number;
      }>(hoses.heating).map((operation) => ({
        comment: operation?.comment,
        ifo: operation?.ifo,
        mgo: operation?.mgo,
      })),
    };
    const hoseOperationType = optionToString(hoses.operationType);
    const hoseDescription = optionToString(hoses.description);
    if (hoseOperationType) hosesEntry['operation_type'] = hoseOperationType;
    if (hoseDescription) hosesEntry['description'] = hoseDescription;
    if (hoses.terminal) hosesEntry['terminal'] = hoses.terminal;
    result['hoses'] = hosesEntry;
  }

  return result as unknown as TvaApiCargoDetails;
};

@Injectable({ providedIn: 'root' })
export class ReportAdapterService {
  convertFromAPI(
    report: TvaApiReport,
    vesselSpec: VesselSpec | undefined,
  ): IReport {
    return {
      type: getReportType(report?._cls),
      operational: convertOperationalSection(report),
      positionAndWeather: convertPositionAndWeatherSection(report),
      cargoDetails: convertCargoDetailsSection(report),
      power: convertPowerSection(report, vesselSpec),
      consumption: convertConsumptionSection(report),
      stock: convertStockSection(report),
      bunker: convertBunkerSection(report),
    };
  }

  convertFromAPISavedReport(
    report: TvaApiReport,
    vesselSpec: VesselSpec | undefined,
  ): IReport {
    return {
      type: getReportType(report?._cls),
      status: report?.status,
      operational: convertOperationalSection(report),
      positionAndWeather: convertPositionAndWeatherSection(report, true),
      cargoDetails: convertCargoDetailsSection(report),
      power: convertPowerSection(report, vesselSpec),
      consumption: convertConsumptionSection(report),
      stock: convertStockSection(report),
      bunker: convertBunkerSectionSavedReport(report),
    };
  }

  // Composes the API report body from the in-progress form state (IReport).
  convertToAPI(report: IReport): TvaApiReport {
    const operational = report.operational;

    const cargo = convertCargoDetailsToApi(
      report.cargoDetails,
    ) as unknown as Record<string, unknown>;
    const operationType = optionToString(
      operational?.operatingConditions?.operationType,
    );
    const cargoLocation = optionToString(
      operational?.operatingConditions?.cargoLocation,
    );
    if (operationType) cargo['operation_type'] = operationType;
    if (cargoLocation) cargo['location'] = cargoLocation;

    return {
      _cls: getReportCls(report.type),
      report_number: operational?.vesselDetails?.reportNumber ?? 0,
      operational: convertOperationalToApi(operational, report.type),
      cargo: cargo as unknown as TvaApiCargoDetails,
      bunker: convertBunkerToApi(report.bunker),
      position: convertPositionToApi(report.positionAndWeather),
      power: convertPowerToApi(report.power),
      consumption: convertConsumptionToApi(report.consumption),
      stock: convertStockToApi(report.stock),
    };
  }
}
